import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'shared/context/AuthContext';
import { useDarkMode } from 'shared/context/DarkModeContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import API, { getImagenUrl } from 'shared/api';
import ImagenProducto from 'shared/components/ImagenProducto';
import {
  FiPlus, FiEdit2, FiTrash2, FiX, FiShoppingBag, FiTruck,
  FiHash, FiDollarSign, FiCalendar, FiClipboard, FiUser,
  FiBox, FiPackage, FiAlertTriangle, FiFileText, FiEye, FiEyeOff, FiXCircle,
  FiCreditCard, FiCheck, FiDownload, FiUpload,
  FiSearch, FiArrowUp, FiArrowDown, FiArrowLeft, FiZap,
} from 'react-icons/fi';

const money = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const moneySimbolo = (n, simbolo) => {
  const v = Number(n || 0);
  return `${simbolo || '$'}${v.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const simboloMonedaOrden = (orden) => (orden && orden.moneda_codigo ? (orden.moneda_simbolo || orden.moneda_codigo) : '$');

const num = (n) => {
  const v = Number(n || 0);
  return v.toLocaleString('es-VE', { maximumFractionDigits: 3 });
};

const itemKey = (it) => {
  if (!it) return '';
  if (it.tipo_item === 'producto') {
    return 'p:' + String(it.producto_codigo || it.codigo || '').toUpperCase();
  }
  return 'i:' + String(it.insumo_id != null ? it.insumo_id : (it.codigo || ''));
};

const rowKey = (r) => {
  if (!r) return '';
  if (r.tipo_item === 'producto') {
    return 'p:' + String(r.codigo || '').toUpperCase();
  }
  return 'i:' + String(r.id != null ? r.id : (r.codigo || ''));
};

const sumarItem = (prev, it) => {
  const k = itemKey(it);
  const idx = prev.findIndex((p) => itemKey(p) === k);
  if (idx >= 0) {
    return prev.map((p, i) => (i === idx ? { ...p, cantidad: Number(p.cantidad || 0) + Number(it.cantidad || 0) } : p));
  }
  return [...prev, it];
};

// Etiqueta corta por tipo de cambio significativo (para resaltar items cambiados).
const CAMBIO_LABEL = {
  precio: 'Precio',
  cantidad: 'Cantidad',
  nota: 'Nota',
  estado: 'Estado',
  personalizacion: 'Personalización',
  nueva: 'Columna nueva',
  eliminado: 'Eliminado',
  nuevo: 'Nuevo',
};

// Columnas fijas del sistema (se exportan/importan, NO son personalizacion).
// Si aparecen como "nuevas", no se ofrece guardarlas como personalizacion.
const CAMPOS_FIJOS_HEADERS = [
  'ESTADO', 'NOTA PROVEEDOR', 'PRICE', 'ORDER', 'OEM', 'NRO.', 'NRO',
  'NAME IN ENGLISH / SPANISH', 'NAME IN SPANISH', 'VEHICLE MODEL',
  'ENGINE MODEL / SERIAL', 'SERIAL VIN', 'ENGINE AND BODY MODELS', 'UNIT', 'UNIDAD',
];

const cambiosSignificativosDe = (it) => {
  const cambios = it && Array.isArray(it.cambios_respuesta) ? it.cambios_respuesta : [];
  return cambios.map((c) => ({ tipo: c.tipo, etiqueta: c.etiqueta, header: c.header, nuevo: c.nuevo }));
};

const tieneCambiosSignificativos = (it) =>
  !!it && (cambiosSignificativosDe(it).length > 0 || !!it.eliminado_en_respuesta);

const TABS = [
  { id: 'compras', label: 'Compras', permiso: 'compras.seccion.compras' },
  { id: 'ordenes', label: 'Órdenes', permiso: 'compras.seccion.compras' },
  { id: 'proveedores', label: 'Proveedores', permiso: 'compras.seccion.proveedores' },
  { id: 'cuentas', label: 'Cuentas por pagar', permiso: 'compras.seccion.cuentas' },
  { id: 'monedas', label: 'Monedas', permiso: 'compras.ver' },
  { id: 'competidores', label: 'Competidores', permiso: 'compras.seccion.competidores' },
];

const DRAFT_KEY = 'agorax_compra_draft';
const ORDEN_DRAFT_KEY = 'agorax_orden_draft';

const FASES_ORDEN = [
  { id: 'borrador', label: 'Borrador' },
  { id: 'enviada', label: 'Orden enviada' },
  { id: 'cotizacion', label: 'Cotización' },
  { id: 'revision', label: 'Revisión' },
  { id: 'aprobada', label: 'Aprobada y en espera' },
  { id: 'transito', label: 'Tránsito' },
  { id: 'recepcion', label: 'Recepción de mercancía' },
  { id: 'revision_costos', label: 'Revisión de costos y precios' },
  { id: 'completada', label: 'Completada' },
];

const FASE_META = {
  borrador: { label: 'Borrador', bg: 'bg2', color: 'muted' },
  enviada: { label: 'Orden enviada', bg: 'blueBg', color: 'pri2' },
  cotizacion: { label: 'Cotización', bg: 'infoBg', color: 'infoText' },
  revision: { label: 'Revisión', bg: 'amberBg', color: 'amber' },
  aprobada: { label: 'Aprobada y en espera', bg: 'successBg', color: 'successText' },
  transito: { label: 'Tránsito', bg: 'warningBg', color: 'warningText' },
  recepcion: { label: 'Recepción de mercancía', bg: 'greenBg', color: 'green' },
  revision_costos: { label: 'Revisión de costos', bg: 'successBg', color: 'successText' },
  completada: { label: 'Completada', bg: 'greenBg', color: 'green' },
};

function cargarOrdenDraft() {
  try {
    const raw = localStorage.getItem(ORDEN_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d && typeof d === 'object') return d;
  } catch (e) { /* noop */ }
  return null;
}

function limpiarOrdenDraft() {
  try { localStorage.removeItem(ORDEN_DRAFT_KEY); } catch (e) { /* noop */ }
}

function cargarDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (d && typeof d === 'object') return d;
  } catch (e) { /* noop */ }
  return null;
}

function limpiarDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch (e) { /* noop */ }
}

export default function Compras() {
  const { isAdmin, isPlanExterno, isTipoEmpresa, tienePermiso } = useAuth();
  const { COL } = useDarkMode();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const tp = (c) => tienePermiso(c);
  const v = (c) => tienePermiso(c);
  const esImportador = isTipoEmpresa('importador');
  const puedeGestionar = (isAdmin() || tp('compras.gestionar')) && (!isPlanExterno() || esImportador);
  const puedeProveedores = (isAdmin() || tp('compras.proveedores')) && (!isPlanExterno() || esImportador);

  const [tab, setTab] = useState('compras');
  const [sedes, setSedes] = useState([]);
  const [sede, setSede] = useState('');

  const loadSedes = useCallback(async () => {
    try {
      const r = await API.get('/sedes/listar');
      const list = r.data?.data || [];
      setSedes(list);
      const activa = list.find((s) => s.activa) || list[0];
      if (activa) setSede((prev) => prev || activa.codigo);
    } catch (e) {
      console.error('Error cargando sedes', e);
    }
  }, []);

  useEffect(() => {
    loadSedes();
  }, [loadSedes]);

  return (
    <div style={{ padding: isMobile ? 12 : 20, maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 22, fontWeight: 700, color: COL.text }}>Compras</h2>
          <div style={{ fontSize: 13, color: COL.muted, marginTop: 3 }}>
            Mercancía recibida, proveedores y costos
          </div>
        </div>
        <SedeSelector sedes={sedes} sede={sede} setSede={setSede} COL={COL} isMobile={isMobile} />
      </div>

      {isPlanExterno() && !esImportador && (
        <div style={{ ...S.banner, ...S.warnBanner }}>
          <FiAlertTriangle size={16} style={{ color: COL.warningText }} />
          <span>Tu plan es externo: las compras son de solo lectura.</span>
        </div>
      )}

      <div style={{ display: 'flex', gap: 4, margin: '16px 0', borderBottom: `1px solid ${COL.border}`, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {TABS.filter((t) => isAdmin() || tp(t.permiso)).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: isMobile ? '12px 14px' : '10px 16px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: tab === t.id ? 700 : 500,
              color: tab === t.id ? COL.pri2 : COL.muted,
              borderBottom: `2px solid ${tab === t.id ? COL.pri2 : 'transparent'}`,
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'compras' && (
        <ComprasTab
          sede={sede} sedes={sedes} COL={COL} isMobile={isMobile}
          puedeGestionar={puedeGestionar} v={v}
        />
      )}
      {tab === 'ordenes' && (
        <OrdenesTab
          sede={sede} sedes={sedes} COL={COL} isMobile={isMobile}
          puedeGestionar={puedeGestionar} v={v}
        />
      )}
      {tab === 'proveedores' && (
        <ProveedoresTab COL={COL} isMobile={isMobile} puedeGestionar={puedeProveedores} />
      )}
      {tab === 'cuentas' && (
        <CuentasPorPagarTab COL={COL} isMobile={isMobile} puedeGestionar={puedeGestionar} v={v} />
      )}
      {tab === 'monedas' && (
        <MonedasTab COL={COL} isMobile={isMobile} puedeGestionar={puedeGestionar} />
      )}
      {tab === 'competidores' && (
        <CompetidoresTab COL={COL} isMobile={isMobile} puedeGestionar={puedeGestionar} />
      )}
    </div>
  );
}

/* ─────────────────────────── Compras (listado unificado + crear) ─────────────────────────── */

function ComprasTab({ sede, sedes, COL, isMobile, puedeGestionar, v }) {
  const [compras, setCompras] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [proveedores, setProveedores] = useState([]);
  const [proveedorId, setProveedorId] = useState('');
  const [modalCrear, setModalCrear] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [anularCompra, setAnularCompra] = useState(null);
  const [abonoAEliminar, setAbonoAEliminar] = useState(null);

  useEffect(() => {
    API.get('/compras/proveedores', { params: { limite: 200, solo_activos: false } })
      .then((r) => setProveedores(r.data?.data?.items || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async (ultimoId) => {
    setCargando(true);
    try {
      const params = { limite: 50, incluir_anuladas: true };
      if (ultimoId) params.ultimo_id = ultimoId;
      if (fechaDesde) params.fecha_desde = fechaDesde;
      if (fechaHasta) params.fecha_hasta = fechaHasta;
      if (proveedorId) params.proveedor_id = Number(proveedorId);
      const r = await API.get('/compras/listar', { params });
      const d = r.data?.data || {};
      setCompras((prev) => (ultimoId ? [...prev, ...(d.items || [])] : d.items || []));
      setHasMore(!!d.has_more);
    } catch (e) {
      console.error('Error cargando compras', e);
    } finally {
      setCargando(false);
    }
  }, [fechaDesde, fechaHasta, proveedorId]);

  useEffect(() => { load(); }, [load]);

  const ultima = compras[compras.length - 1];
  const cargarMas = () => ultima && load(ultima.id);

  const abrirDetalle = async (id) => {
    try {
      const r = await API.get(`/compras/${id}`);
      if (r.data.status === 'success') setDetalle(r.data.data);
    } catch (e) {
      console.error('Error cargando detalle', e);
    }
  };

  const confirmarAnular = async () => {
    if (!anularCompra || !anularCompra.motivo) return;
    try {
      await API.post(`/compras/${anularCompra.id}/anular`, { motivo: anularCompra.motivo });
      setAnularCompra(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo anular la compra';
      alert(msg);
    }
  };

  const eliminarAbono = async (pagoId) => {
    setAbonoAEliminar(pagoId);
  };

  const confirmarEliminarAbono = async () => {
    if (!abonoAEliminar) return;
    try {
      await API.delete(`/compras/abono/${abonoAEliminar}`);
      setAbonoAEliminar(null);
      if (detalle?.compra?.id) abrirDetalle(detalle.compra.id);
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo eliminar el abono');
      setAbonoAEliminar(null);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} style={{ ...inputStyle(COL), width: isMobile ? '100%' : 150 }} />
        <span style={{ color: COL.muted }}>→</span>
        <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} style={{ ...inputStyle(COL), width: isMobile ? '100%' : 150 }} />
        <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} style={{ ...selectStyle(COL), width: isMobile ? '100%' : 200 }}>
          <option value="">Todos los proveedores</option>
          {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
        </select>
        <div style={{ fontSize: 13, color: COL.muted }}>{compras.length}{hasMore ? '+' : ''} compras</div>
        {puedeGestionar && (
          <div style={{ marginLeft: 'auto' }}>
            <Boton COL={COL} onClick={() => setModalCrear(true)}><FiPlus size={15} /> Nueva compra</Boton>
          </div>
        )}
      </div>

      {compras.length === 0 && !cargando ? (
        <EmptyState
          icon={FiShoppingBag} COL={COL}
          title="Sin compras registradas"
          desc="Registra la mercancía que recibes para actualizar el stock y el costo promedio."
          actionLabel={puedeGestionar ? 'Registrar compra' : null}
          onAction={puedeGestionar ? () => setModalCrear(true) : null}
        />
      ) : (
        <Tabla>
          <thead>
            <tr>
              <th style={TH}>Compra</th>
              <th style={TH}>Fecha</th>
              <th style={TH}>Proveedor</th>
              <th style={TH}>Tipo</th>
              <th style={TH}>Estado</th>
              <th style={{ ...TH, textAlign: 'right' }}>Items</th>
              {v('vis.compras.total') && <th style={{ ...TH, textAlign: 'right' }}>Total</th>}
              {v('vis.compras.total') && <th style={{ ...TH, textAlign: 'right' }}>Saldo</th>}
              <th style={{ ...TH, textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {compras.map((c) => (
              <tr key={c.id} style={{ borderBottom: `1px solid ${COL.border}`, opacity: c.anulada ? 0.55 : 1 }}>
                <td style={TD}>
                  <div style={{ fontWeight: 700, color: COL.text }}>{c.numero_compra}</div>
                  {c.anulada && <div style={{ fontSize: 11, color: COL.red }}>Anulada{c.motivo_anulacion ? ` · ${c.motivo_anulacion}` : ''}</div>}
                </td>
                <td style={TD}>{c.fecha_compra ? String(c.fecha_compra).slice(0, 10) : '—'}</td>
                <td style={TD}>{c.proveedor || '—'}</td>
                <td style={TD}>{c.tipo_pago === 'contado' ? 'Contado' : 'Crédito'}</td>
                <td style={TD}><EstadoPago estado={c.estado_pago} COL={COL} /></td>
                <td style={{ ...TD, textAlign: 'right' }}>{c.n_items}</td>
                {v('vis.compras.total') && <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{money(c.total)}</td>}
                {v('vis.compras.total') && <td style={{ ...TD, textAlign: 'right', color: c.saldo_pendiente > 0 ? COL.red : COL.muted }}>{money(c.saldo_pendiente)}</td>}
                <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <IconBtn COL={COL} onClick={() => abrirDetalle(c.id)}><FiEye size={15} /></IconBtn>
                  {puedeGestionar && !c.anulada && (
                    <IconBtn COL={COL} danger onClick={() => setAnularCompra({ id: c.id, motivo: '' })}><FiXCircle size={15} /></IconBtn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: 12 }}>
          <Boton COL={COL} ghost onClick={cargarMas} disabled={cargando}>{cargando ? 'Cargando…' : 'Cargar más'}</Boton>
        </div>
      )}

      {modalCrear && (
        <ModalNuevaCompra
          COL={COL} isMobile={isMobile} sede={sede} sedes={sedes} v={v}
          onClose={() => setModalCrear(false)}
          onSaved={() => { setModalCrear(false); load(); }}
        />
      )}

      {detalle && (
        <ModalDetalle
          COL={COL} isMobile={isMobile} detalle={detalle} v={v} puedeDevolver={puedeGestionar}
          onClose={() => setDetalle(null)}
          onDevolucion={() => { setDetalle(null); load(); }}
          puedeEliminarAbono={puedeGestionar}
          onEliminarAbono={(pagoId) => eliminarAbono(pagoId)}
        />
      )}

      {anularCompra && (
        <ModalAnular
          COL={COL} isMobile={isMobile}
          onClose={() => setAnularCompra(null)}
          motivo={anularCompra.motivo}
          setMotivo={(m) => setAnularCompra((p) => ({ ...p, motivo: m }))}
          onConfirm={confirmarAnular}
        />
      )}

      {abonoAEliminar && (
        <ModalConfirmar
          COL={COL} isMobile={isMobile}
          titulo="Eliminar abono"
          mensaje="¿Eliminar este abono? Se revertirá el saldo de la factura."
          labelConfirmar="Eliminar"
          onClose={() => setAbonoAEliminar(null)}
          onConfirm={confirmarEliminarAbono}
        />
      )}
    </div>
  );
}

function ModalNuevaCompra({ COL, isMobile, sede, sedes, v, onClose, onSaved }) {
  const [draft] = useState(cargarDraft);
  const [proveedores, setProveedores] = useState([]);
  const [depositos, setDepositos] = useState([]);
  const [proveedorId, setProveedorId] = useState(draft?.proveedorId ?? '');
  const [sedeSel, setSedeSel] = useState(draft?.sedeSel ?? sede ?? '');
  const [depositoId, setDepositoId] = useState(draft?.depositoId ?? '');
  const [fecha, setFecha] = useState(draft?.fecha ?? (() => new Date().toISOString().slice(0, 10)));
  const [tipoPago, setTipoPago] = useState(draft?.tipoPago ?? 'contado');
  const [fechaVencimiento, setFechaVencimiento] = useState(draft?.fechaVencimiento ?? '');
  const [notas, setNotas] = useState(draft?.notas ?? '');
  const [items, setItems] = useState(draft?.items ?? []);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [buscador, setBuscador] = useState(false);

  useEffect(() => {
    API.get('/compras/proveedores', { params: { limite: 200 } })
      .then((r) => setProveedores(r.data?.data?.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!sedeSel) { setDepositos([]); return; }
    API.get('/compras/depositos', { params: { sede: sedeSel } })
      .then((r) => setDepositos(r.data?.data || []))
      .catch(() => setDepositos([]));
  }, [sedeSel]);

  // Guarda el borrador en localStorage para no perderlo al cerrar o cambiar de pestaña.
  useEffect(() => {
    const data = { proveedorId, sedeSel, depositoId, fecha, tipoPago, fechaVencimiento, notas, items };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(data)); } catch (e) { /* noop */ }
  }, [proveedorId, sedeSel, depositoId, fecha, tipoPago, fechaVencimiento, notas, items]);

  const subtotal = items.reduce((s, it) => s + Number(it.cantidad || 0) * Number(it.costo || 0), 0);

  const guardar = async () => {
    setError('');
    if (!items.length) { setError('Agrega al menos un producto o insumo'); return; }
    if (items.some((it) => !Number(it.cantidad) || Number(it.cantidad) <= 0)) { setError('Revisa las cantidades'); return; }
    if (tipoPago === 'credito' && !fechaVencimiento) { setError('Indica la fecha de vencimiento'); return; }
    setGuardando(true);
    try {
      const body = {
        proveedor_id: proveedorId ? Number(proveedorId) : null,
        sede_codigo: sedeSel,
        deposito_id: depositoId ? Number(depositoId) : null,
        recepcion_por: null,
        fecha_compra: fecha || null,
        tipo_pago: tipoPago,
        fecha_vencimiento: tipoPago === 'credito' ? (fechaVencimiento || null) : null,
        notas: notas || null,
        items: items.map((it) => ({
          tipo_item: it.tipo_item,
          producto_codigo: it.tipo_item === 'producto' ? it.producto_codigo : null,
          insumo_id: it.tipo_item === 'insumo' ? (it.insumo_id || null) : null,
          codigo: it.tipo_item === 'insumo' && !it.insumo_id ? it.codigo : null,
          descripcion: it.descripcion,
          cantidad: Number(it.cantidad),
          costo: Number(it.costo),
          precio: it.tipo_item === 'producto' ? (it.precio === '' || it.precio == null ? null : Number(it.precio)) : null,
          nuevo: !!it.nuevo,
          departamento: it.tipo_item === 'producto' ? (it.departamento || null) : null,
          unidad_medida_id: it.tipo_item === 'insumo' ? (it.unidad_medida_id || null) : null,
        })),
      };
      await API.post('/compras/crear', body);
      limpiarDraft();
      onSaved();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'No se pudo crear la compra';
      setError(msg);
    } finally {
      setGuardando(false);
    }
  };

  const quitarItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const setItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const descartarBorrador = () => {
    limpiarDraft();
    setProveedorId('');
    setSedeSel(sede || '');
    setDepositoId('');
    setFecha(new Date().toISOString().slice(0, 10));
    setTipoPago('contado');
    setFechaVencimiento('');
    setNotas('');
    setItems([]);
  };

  const tieneBorrador = !!(draft && (draft.items?.length || draft.notas || draft.fechaVencimiento));

  if (buscador) {
    return (
      <SelectorItem
        COL={COL} isMobile={isMobile}
        onClose={() => setBuscador(false)}
        items={items}
        onPick={(it) => { setItems((prev) => sumarItem(prev, it)); }}
      />
    );
  }

  return (
    <Overlay COL={COL} isMobile={isMobile} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiShoppingBag} title="Nueva compra" subtitle="Mercancía recibida" onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {tieneBorrador && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', borderRadius: 8, background: COL.blueBg, fontSize: 12, color: COL.text }}>
              <span>Se restauró un borrador guardado.</span>
              <button type="button" onClick={descartarBorrador} style={{ border: 'none', background: 'transparent', color: COL.pri2, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                Descartar
              </button>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <Field label="Proveedor" icon={FiTruck} COL={COL}>
              <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} style={selectStyle(COL)}>
                <option value="">Sin proveedor</option>
                {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
              </select>
            </Field>
            <Field label="Sede" icon={FiPackage} required COL={COL}>
              <select value={sedeSel} onChange={(e) => setSedeSel(e.target.value)} style={selectStyle(COL)}>
                {sedes.map((s) => <option key={s.codigo} value={s.codigo}>{s.nombre || s.codigo}</option>)}
              </select>
            </Field>
            <Field label="Depósito" icon={FiBox} COL={COL} hint="Donde llega la mercancía">
              <select value={depositoId} onChange={(e) => setDepositoId(e.target.value)} style={selectStyle(COL)}>
                <option value="">Depósito por defecto</option>
                {depositos.map((d) => (
                  <option key={d.id} value={d.id}>{d.nombre}{d.es_principal ? ' (principal)' : ''}</option>
                ))}
              </select>
            </Field>
            <Field label="Fecha" icon={FiCalendar} COL={COL}>
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={inputStyle(COL)} />
            </Field>
            <Field label="Condición de pago" icon={FiCreditCard} COL={COL}>
              <select value={tipoPago} onChange={(e) => setTipoPago(e.target.value)} style={selectStyle(COL)}>
                <option value="contado">Contado</option>
                <option value="credito">Crédito</option>
              </select>
            </Field>
            {tipoPago === 'credito' && (
              <Field label="Fecha de vencimiento" icon={FiCalendar} required COL={COL} hint="Fecha límite para pagar la factura (por abonos)">
                <input type="date" value={fechaVencimiento} onChange={(e) => setFechaVencimiento(e.target.value)} style={inputStyle(COL)} />
              </Field>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>Items ({items.length})</span>
            <Boton COL={COL} onClick={() => setBuscador(true)}><FiPlus size={14} /> Agregar ítem</Boton>
          </div>

          {items.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map((it, idx) => (
                <div key={idx} style={{ border: `1px solid ${COL.border}`, borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 6, background: it.tipo_item === 'producto' ? COL.blueBg : COL.greenBg, color: it.tipo_item === 'producto' ? COL.pri2 : COL.green, whiteSpace: 'nowrap' }}>
                          {it.tipo_item === 'producto' ? 'Producto' : 'Insumo'}
                        </span>
                        <span style={{ fontSize: 11, color: COL.muted }}>{it.tipo_item === 'producto' ? it.producto_codigo : (it.codigo || `#${it.insumo_id}`)}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: COL.text, wordBreak: 'break-word', marginTop: 2 }}>{it.descripcion}</div>
                    </div>
                    <IconBtn COL={COL} danger onClick={() => quitarItem(idx)}><FiX size={14} /></IconBtn>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 8 }}>
                    <Field label={`Cantidad${it.unidad ? ` (${it.unidad})` : ''}`} icon={FiHash} COL={COL}>
                      <input type="number" inputMode="decimal" min="0" step="0.01" value={it.cantidad} onChange={(e) => setItem(idx, { cantidad: e.target.value })} style={inputStyle(COL)} />
                    </Field>
                    {v('vis.compras.costo') && (
                      <Field label="Costo" icon={FiDollarSign} COL={COL}>
                        <input type="number" inputMode="decimal" min="0" step="0.01" value={it.costo} onChange={(e) => setItem(idx, { costo: e.target.value })} style={inputStyle(COL)} />
                      </Field>
                    )}
                    {it.tipo_item === 'producto' && v('vis.compras.precio') && (
                      <Field label="Precio venta" icon={FiDollarSign} COL={COL}>
                        <input type="number" inputMode="decimal" min="0" step="0.01" value={it.precio} onChange={(e) => setItem(idx, { precio: e.target.value })} style={inputStyle(COL)} />
                      </Field>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Field label="Notas" icon={FiClipboard} COL={COL}>
            <input value={notas} onChange={(e) => setNotas(e.target.value)} style={inputStyle(COL)} placeholder="Opcional" />
          </Field>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderRadius: 10, background: COL.blueBg }}>
            <span style={{ fontSize: 13, color: COL.muted }}>Total</span>
            <span style={{ fontSize: 18, fontWeight: 800, color: COL.text }}>{money(subtotal)}</span>
          </div>

          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label="Registrar compra" guardando={guardando} />
      </div>
    </Overlay>
  );
}

function SelectorItem({ COL, isMobile, onClose, onPick, tipo, items }) {
  const [q, setQ] = useState('');
  const [tab, setTab] = useState(tipo === 'producto' || tipo === 'insumo' ? tipo : 'todo'); // todo | producto | insumo
  const [prod, setProd] = useState({ items: [], hasMore: false, cursor: null });
  const [ins, setIns] = useState({ items: [], hasMore: false, cursor: null });
  const [cargando, setCargando] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [activo, setActivo] = useState(null);
  const [cantidad, setCantidad] = useState('1');
  const [agregados, setAgregados] = useState(0);
  const listRef = React.useRef(null);

  const showProd = tab === 'todo' || tab === 'producto';
  const showIns = tab === 'todo' || tab === 'insumo';

  const agregadoMap = {};
  (items || []).forEach((it) => {
    const k = itemKey(it);
    if (!k) return;
    agregadoMap[k] = (agregadoMap[k] || 0) + Number(it.cantidad || 0);
  });

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const reqs = [];
      if (showProd) reqs.push(API.get('/compras/productos-simples', { params: { q: q || undefined, limite: 100 } }).then((r) => r.data?.data || {}));
      if (showIns) reqs.push(API.get('/compras/insumos-simples', { params: { q: q || undefined, limite: 100 } }).then((r) => r.data?.data || {}));
      const res = await Promise.all(reqs);
      let i = 0;
      if (showProd) {
        const d = res[i++];
        const items = d.items || [];
        setProd({ items, hasMore: !!d.has_more, cursor: items.length ? { codigo: items[items.length - 1].codigo, descripcion: items[items.length - 1].descripcion } : null });
      } else setProd({ items: [], hasMore: false, cursor: null });
      if (showIns) {
        const d = res[i++];
        const items = d.items || [];
        setIns({ items, hasMore: !!d.has_more, cursor: items.length ? { id: items[items.length - 1].id, nombre: items[items.length - 1].nombre } : null });
      } else setIns({ items: [], hasMore: false, cursor: null });
    } catch (e) {
      console.error('Error buscando items', e);
    } finally {
      setCargando(false);
    }
  }, [q, showProd, showIns]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarMas = useCallback(async () => {
    if (cargandoMas) return;
    const reqs = [];
    if (showProd && prod.hasMore && prod.cursor) {
      reqs.push(API.get('/compras/productos-simples', { params: { q: q || undefined, limite: 100, ultimo_codigo: prod.cursor.codigo, ultimo_descripcion: prod.cursor.descripcion } }).then((r) => ({ tipo: 'producto', d: r.data?.data || {} })));
    }
    if (showIns && ins.hasMore && ins.cursor) {
      reqs.push(API.get('/compras/insumos-simples', { params: { q: q || undefined, limite: 100, ultimo_id: ins.cursor.id, ultimo_nombre: ins.cursor.nombre } }).then((r) => ({ tipo: 'insumo', d: r.data?.data || {} })));
    }
    if (!reqs.length) return;
    setCargandoMas(true);
    try {
      const res = await Promise.all(reqs);
      res.forEach(({ tipo, d }) => {
        const items = d.items || [];
        if (tipo === 'producto') {
          setProd((prev) => {
            const merged = [...prev.items, ...items];
            const last = merged[merged.length - 1];
            return { items: merged, hasMore: !!d.has_more, cursor: last ? { codigo: last.codigo, descripcion: last.descripcion } : prev.cursor };
          });
        } else {
          setIns((prev) => {
            const merged = [...prev.items, ...items];
            const last = merged[merged.length - 1];
            return { items: merged, hasMore: !!d.has_more, cursor: last ? { id: last.id, nombre: last.nombre } : prev.cursor };
          });
        }
      });
    } catch (e) {
      console.error('Error cargando más items', e);
    } finally {
      setCargandoMas(false);
    }
  }, [cargandoMas, q, showProd, showIns, prod, ins]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) cargarMas();
  };

  const pick = (r) => {
    const k = rowKey(r);
    if (activo && activo.k === k) { setActivo(null); return; }
    const ya = agregadoMap[k] || 0;
    setActivo({ r, k, ya });
    setCantidad('1');
  };

  const unidadDe = (r) => {
    if (r.tipo_item === 'insumo') return r.unidad_simbolo || r.unidad_nombre || '';
    return r.pesable ? 'kg' : '';
  };

  const confirmarAgregar = () => {
    if (!activo) return;
    const c = Number(cantidad);
    if (!c || c <= 0) return;
    const r = activo.r;
    const unidad = unidadDe(r);
    if (r.tipo_item === 'producto') {
      onPick({ tipo_item: 'producto', producto_codigo: r.codigo, descripcion: r.descripcion, cantidad: c, costo: Number(r.costo || 0), precio: Number(r.precio || 0), nuevo: false, unidad, referencia: r.referencia || '', imagen_url: r.imagen_url || null });
    } else {
      onPick({ tipo_item: 'insumo', insumo_id: r.id, codigo: r.codigo, descripcion: r.nombre, cantidad: c, costo: Number(r.costo_unitario || 0), nuevo: false, unidad_medida_id: r.unidad_medida_id, unidad, referencia: r.referencia || '' });
    }
    setAgregados((n) => n + 1);
    setActivo(null);
  };

  const merged = [
    ...prod.items.map((r) => ({ ...r, tipo_item: 'producto' })),
    ...ins.items.map((r) => ({ ...r, tipo_item: 'insumo' })),
  ];
  const hayMas = (showProd && prod.hasMore) || (showIns && ins.hasMore);

  const tabBtn = (id, label) => (
    <button type="button" onClick={() => setTab(id)} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: tab === id ? 'none' : `1px solid ${COL.border}`, background: tab === id ? COL.pri2 : 'transparent', color: tab === id ? '#fff' : COL.muted, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
      {label}
    </button>
  );

  return (
    <Overlay COL={COL} isMobile={isMobile} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiBox} title="Agregar ítem" subtitle="Selecciona un elemento y confirma la cantidad" onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {tabBtn('todo', 'Todos')}
            {tabBtn('producto', 'Productos')}
            {tabBtn('insumo', 'Insumos')}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar por nombre, código o referencia…" style={inputStyle(COL)} />

          {agregados > 0 && (
            <div style={{ fontSize: 12, color: COL.green, fontWeight: 600 }}>
              ✓ {agregados} agregado{agregados === 1 ? '' : 's'}
            </div>
          )}

          {cargando && merged.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: COL.muted, fontSize: 13 }}>Cargando…</div>
          ) : merged.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: COL.muted, fontSize: 13 }}>Sin resultados</div>
          ) : (
            <>
              <div style={{ fontSize: 11, color: COL.muted }}>{hayMas ? `${merged.length}+ ` : `${merged.length} `}elementos</div>
              <div ref={listRef} onScroll={onScroll} style={{ flex: 1, minHeight: 0, maxHeight: '46vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, WebkitOverflowScrolling: 'touch' }}>
                {merged.map((r) => {
                  const k = rowKey(r);
                  const ya = agregadoMap[k] || 0;
                  const esActivo = !!activo && activo.k === k;
                  return (
                    <div key={`${r.tipo_item}-${r.id || r.codigo}`} onClick={() => pick(r)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, border: esActivo ? `1px solid ${COL.pri2}` : ya > 0 ? `1px solid ${COL.pri2}` : `1px solid ${COL.border}`, background: esActivo ? COL.blueBg : ya > 0 ? COL.blueBg : COL.inputBg, cursor: 'pointer', textAlign: 'left', color: COL.text }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
                        {r.tipo_item === 'producto' && (
                          <ImagenProducto imagen_url={r.imagen_url} size={38} />
                        )}
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{r.descripcion || r.nombre}</span>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, background: r.tipo_item === 'producto' ? COL.blueBg : COL.greenBg, color: r.tipo_item === 'producto' ? COL.pri2 : COL.green, whiteSpace: 'nowrap' }}>
                              {r.tipo_item === 'producto' ? 'Producto' : 'Insumo'}
                            </span>
                            {ya > 0 && !esActivo && (
                              <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 6, background: COL.greenBg, color: COL.green, whiteSpace: 'nowrap' }}>
                                <FiCheck size={10} style={{ verticalAlign: '-1px', marginRight: 2 }} />{num(ya)}
                              </span>
                            )}
                          </span>
                          <span style={{ fontSize: 11, color: COL.muted }}>
                            {r.codigo}{r.referencia ? ` · Ref: ${r.referencia}` : ''}{unidadDe(r) ? ` · ${unidadDe(r)}` : ''}
                          </span>
                        </span>
                      </div>
                      {esActivo ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <input
                            type="number" inputMode="decimal" min="0.01" step="0.01" autoFocus
                            value={cantidad}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setCantidad(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') confirmarAgregar(); }}
                            style={{ ...inputStyle(COL), width: 92, padding: '6px 8px' }}
                            placeholder={unidadDe(r) ? `Cant. (${unidadDe(r)})` : 'Cantidad'}
                          />
                          {unidadDe(r) && <span style={{ fontSize: 11, color: COL.muted, whiteSpace: 'nowrap' }}>{unidadDe(r)}</span>}
                          <Boton COL={COL} onClick={(e) => { e.stopPropagation(); confirmarAgregar(); }}><FiCheck size={14} /> {activo.ya > 0 ? 'Sumar' : 'Agregar'}</Boton>
                        </div>
                      ) : (
                        <span style={{ fontSize: 12, color: COL.pri2, fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>{money(r.tipo_item === 'producto' ? r.costo : r.costo_unitario)}</span>
                      )}
                    </div>
                  );
                })}
                {cargandoMas && (
                  <div style={{ textAlign: 'center', padding: 10, color: COL.muted, fontSize: 12 }}>Cargando más…</div>
                )}
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 16, flexShrink: 0, background: COL.surface, borderTop: `1px solid ${COL.border}`, boxShadow: '0 -4px 12px rgba(0,0,0,0.06)', zIndex: 2 }}>
          <Boton COL={COL} ghost onClick={onClose}>Cancelar</Boton>
          <Boton COL={COL} onClick={onClose}><FiCheck size={15} /> Listo</Boton>
        </div>
      </div>
    </Overlay>
  );
}

function ModalDetalle({ COL, isMobile, detalle, v, onClose, puedeDevolver, onDevolucion, puedeEliminarAbono, onEliminarAbono }) {
  const { compra, items, pagos } = detalle;
  const [devolver, setDevolver] = useState(false);

  if (devolver) {
    return (
      <ModalDevolucion
        COL={COL} isMobile={isMobile} compra={compra} items={items}
        onClose={() => setDevolver(false)}
        onDevolucion={onDevolucion}
      />
    );
  }

  return (
    <Overlay COL={COL} isMobile={isMobile} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader
          icon={FiEye}
          title={compra.numero_compra}
          subtitle={[compra.proveedor, compra.proveedor_rif].filter(Boolean).join(' · ') || 'Sin proveedor'}
          onClose={onClose}
          COL={COL}
        />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 8 }}>
            <MiniStat label="Fecha" value={compra.fecha_compra ? String(compra.fecha_compra).slice(0, 10) : '—'} COL={COL} />
            <MiniStat label="Factura" value={compra.numero_factura || '—'} COL={COL} />
            <MiniStat label="Condición" value={compra.tipo_pago === 'contado' ? 'Contado' : 'Crédito'} COL={COL} />
            <MiniStat label="Sede" value={compra.sede_nombre || compra.sede_codigo || '—'} COL={COL} />
            <MiniStat label="Recibido por" value={compra.recepcion_nombre || compra.recepcion_por || '—'} COL={COL} />
            <MiniStat label="Registrado por" value={compra.usuario_nombre || compra.usuario_cedula || '—'} COL={COL} />
          </div>

          {compra.anulada && (
            <div style={{ padding: '10px 12px', borderRadius: 8, background: COL.redBg, color: COL.red, fontSize: 13 }}>
              Anulada{compra.motivo_anulacion ? ` — ${compra.motivo_anulacion}` : ''}
            </div>
          )}

          {compra.notas && (
            <div style={{ padding: '10px 12px', borderRadius: 8, background: COL.bg2, fontSize: 13, color: COL.text }}>
              <span style={{ fontWeight: 700, color: COL.muted }}>Notas: </span>{compra.notas}
            </div>
          )}

          <Tabla>
            <thead>
              <tr>
                <th style={TH}>Item</th>
                <th style={{ ...TH, textAlign: 'right' }}>Cant.</th>
                {v('vis.compras.costo') && <th style={{ ...TH, textAlign: 'right' }}>Costo ant → nuevo</th>}
                {v('vis.compras.precio') && <th style={{ ...TH, textAlign: 'right' }}>Precio</th>}
                {v('vis.compras.total') && <th style={{ ...TH, textAlign: 'right' }}>Subtotal</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} style={{ borderBottom: `1px solid ${COL.border}` }}>
                  <td style={TD}>
                    <div style={{ fontWeight: 600 }}>{it.descripcion}</div>
                    <div style={{ fontSize: 11, color: COL.muted }}>{it.tipo_item === 'producto' ? it.producto_codigo : `Insumo #${it.insumo_id}`}</div>
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }}>{num(it.cantidad)}</td>
                  {v('vis.compras.costo') && <td style={{ ...TD, textAlign: 'right' }}>{money(it.costo_anterior)} → {money(it.costo_nuevo)}</td>}
                  {v('vis.compras.precio') && <td style={{ ...TD, textAlign: 'right' }}>{it.precio_nuevo != null ? money(it.precio_nuevo) : '—'}</td>}
                  {v('vis.compras.total') && <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{money(it.subtotal)}</td>}
                </tr>
              ))}
            </tbody>
          </Tabla>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8, borderTop: `1px solid ${COL.border}`, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {v('vis.compras.total') && (
                <div style={{ fontSize: 12, color: COL.muted }}>
                  Pagado <span style={{ fontWeight: 700, color: COL.green }}>{money(compra.monto_pagado)}</span>
                </div>
              )}
              {compra.tipo_pago === 'credito' && compra.fecha_vencimiento && (
                <div style={{ fontSize: 12, color: COL.muted }}>
                  Vence <span style={{ fontWeight: 700, color: COL.text }}>{String(compra.fecha_vencimiento).slice(0, 10)}</span>
                </div>
              )}
            </div>
            {v('vis.compras.total') && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: 13, color: COL.muted }}>Total</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: COL.text }}>{money(compra.total)}</span>
              </div>
            )}
          </div>

          {compra.tipo_pago === 'credito' && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: COL.bg2, gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: COL.text }}>Saldo pendiente {money(compra.saldo_pendiente)}</span>
              <EstadoPago estado={compra.estado_pago} COL={COL} />
            </div>
          )}

          {pagos && pagos.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Abonos ({pagos.length})</div>
              {pagos.map((p) => (
                <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', borderRadius: 8, background: COL.bg2, gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: COL.text, minWidth: 0 }}>
                    {p.fecha_pago ? String(p.fecha_pago).slice(0, 10) : '—'} · {p.metodo_pago || 'Pago'}{p.referencia ? ` · ${p.referencia}` : ''}
                    {p.comprobante_url && (
                      <a href={getImagenUrl(p.comprobante_url)} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: COL.pri2, textDecoration: 'underline', marginLeft: 6 }}>ver</a>
                    )}
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: COL.green }}>{money(p.monto)}</span>
                    {puedeEliminarAbono && (
                      <IconBtn COL={COL} danger onClick={() => onEliminarAbono(p.id)}><FiTrash2 size={13} /></IconBtn>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {compra.comprobante_url && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Comprobante</div>
              <a href={getImagenUrl(compra.comprobante_url)} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: COL.pri2, textDecoration: 'underline', wordBreak: 'break-all' }}>
                Ver comprobante
              </a>
            </div>
          )}

          {puedeDevolver && !compra.anulada && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Boton COL={COL} ghost onClick={() => setDevolver(true)}><FiXCircle size={15} /> Devolver mercancía</Boton>
            </div>
          )}
        </div>
      </div>
    </Overlay>
  );
}

function ModalDevolucion({ COL, isMobile, compra, items, onClose, onDevolucion }) {
  const [motivo, setMotivo] = useState('');
  const [notas, setNotas] = useState('');
  const [cantidades, setCantidades] = useState(() => {
    const map = {};
    items.forEach((it) => { map[it.id] = ''; });
    return map;
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const devolverItems = items
    .filter((it) => Number(cantidades[it.id]) > 0)
    .map((it) => ({ compra_item_id: it.id, cantidad: Number(cantidades[it.id]) }));

  const guardar = async () => {
    setError('');
    if (!motivo.trim()) { setError('El motivo es obligatorio'); return; }
    if (!devolverItems.length) { setError('Indica la cantidad a devolver de al menos un item'); return; }
    setGuardando(true);
    try {
      await API.post(`/compras/${compra.id}/devolucion`, {
        motivo: motivo.trim(),
        notas: notas || null,
        items: devolverItems,
      });
      onDevolucion();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo registrar la devolución');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiXCircle} title="Devolver mercancía" subtitle="Nota de crédito al proveedor" onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Field label="Motivo" icon={FiAlertTriangle} required COL={COL}>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} style={inputStyle(COL)} placeholder="Producto dañado, vencido…" autoFocus />
          </Field>
          <Field label="Notas" icon={FiClipboard} COL={COL}>
            <input value={notas} onChange={(e) => setNotas(e.target.value)} style={inputStyle(COL)} />
          </Field>

          {items.map((it) => (
            <div key={it.id} style={{ border: `1px solid ${COL.border}`, borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COL.text, wordBreak: 'break-word' }}>{it.descripcion}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Field label="Cantidad a devolver" icon={FiHash} COL={COL}>
                  <input type="number" inputMode="decimal" min="0" step="0.01" max={it.cantidad} value={cantidades[it.id]} onChange={(e) => setCantidades((f) => ({ ...f, [it.id]: e.target.value }))} style={inputStyle(COL)} placeholder={`Máx ${num(it.cantidad)}`} />
                </Field>
                <span style={{ fontSize: 11, color: COL.muted, whiteSpace: 'nowrap' }}>comprado {num(it.cantidad)}</span>
              </div>
            </div>
          ))}

          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label="Registrar devolución" guardando={guardando} />
      </div>
    </Overlay>
  );
}

function ModalAnular({ COL, isMobile, onClose, motivo, setMotivo, onConfirm }) {
  return (
    <Overlay COL={COL} isMobile={isMobile} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiXCircle} title="Anular compra" subtitle="Se revertirá el stock y los costos" onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Motivo" icon={FiAlertTriangle} required COL={COL}>
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} style={inputStyle(COL)} placeholder="¿Por qué se anula?" autoFocus />
          </Field>
        </div>
        <ModalFooter onClose={onClose} onSave={onConfirm} COL={COL} label="Anular compra" guardando={false} />
      </div>
    </Overlay>
  );
}

function ModalConfirmar({ COL, isMobile, titulo, mensaje, labelConfirmar, onClose, onConfirm, guardando }) {
  return (
    <Overlay COL={COL} isMobile={isMobile} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface, maxWidth: isMobile ? '100%' : 420 }}>
        <ModalHeader icon={FiAlertTriangle} title={titulo} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px' }}>
          <div style={{ fontSize: 14, color: COL.text, lineHeight: 1.5 }}>{mensaje}</div>
        </div>
        <ModalFooter onClose={onClose} onSave={onConfirm} COL={COL} label={labelConfirmar || 'Confirmar'} guardando={guardando} />
      </div>
    </Overlay>
  );
}

/* ─────────────────────────── Órdenes de compra ─────────────────────────── */

function FaseBadge({ fase, COL }) {
  const meta = FASE_META[fase] || { label: fase || '—', bg: 'bg2', color: 'muted' };
  return <span style={{ ...S.badge, background: COL[meta.bg], color: COL[meta.color] }}>{meta.label}</span>;
}

function FaseStepper({ fase, COL }) {
  const idx = FASES_ORDEN.findIndex((f) => f.id === fase);
  const short = {
    borrador: 'Borrador',
    enviada: 'Enviada',
    cotizacion: 'Cotización',
    revision: 'Revisión',
    aprobada: 'Aprobada',
    transito: 'Tránsito',
    recepcion: 'Recepción',
    revision_costos: 'Rev. costos',
    completada: 'Completada',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
      {FASES_ORDEN.map((f, i) => {
        const done = i < idx;
        const active = i === idx;
        const color = active ? COL.pri2 : done ? COL.green : COL.faint;
        return (
          <div key={f.id} style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: active ? COL.pri2 : done ? COL.greenBg : COL.bg2, color: active ? '#fff' : color,
                border: `1px solid ${active ? COL.pri2 : COL.border}`, fontSize: 10, fontWeight: 700, flexShrink: 0,
              }}>
                {done ? <FiCheck size={11} /> : i + 1}
              </div>
              {i < FASES_ORDEN.length - 1 && (
                <div style={{ flex: 1, height: 2, background: done ? COL.green : COL.border, borderRadius: 1, margin: '0 4px' }} />
              )}
            </div>
            <div style={{ fontSize: 9, fontWeight: active ? 700 : 500, color: active ? COL.pri2 : COL.muted, textAlign: 'center', lineHeight: 1.2, marginTop: 4, whiteSpace: 'nowrap' }}>{short[f.id] || f.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function OrdenEstado({ estado, COL }) {
  const cfg = {
    solicitada: { bg: COL.blueBg, color: COL.pri2, label: 'Solicitada' },
    parcial: { bg: COL.amberBg, color: COL.amber, label: 'Parcial' },
    completa: { bg: COL.greenBg, color: COL.green, label: 'Completa' },
    cancelada: { bg: COL.redBg, color: COL.red, label: 'Cancelada' },
  }[estado] || { bg: COL.bg2, color: COL.muted, label: estado };
  return <span style={{ ...S.badge, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
}

function OrdenesTab({ sede, sedes, COL, isMobile, puedeGestionar, v }) {
  const [ordenes, setOrdenes] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modalCrear, setModalCrear] = useState(false);
  const [detalle, setDetalle] = useState(null);
  const [editando, setEditando] = useState(null);
  const [cancelarOrden, setCancelarOrden] = useState(null);
  const [cancelando, setCancelando] = useState(false);
  const [modalProductos, setModalProductos] = useState(false);
  const [modalColumnas, setModalColumnas] = useState(false);

  const load = useCallback(async () => {
    setCargando(true);
    try {
      const r = await API.get('/compras/ordenes');
      setOrdenes(r.data?.data || []);
    } catch (e) {
      console.error('Error cargando ordenes', e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const abrirDetalle = async (id) => {
    try {
      const r = await API.get(`/compras/ordenes/${id}`);
      if (r.data.status === 'success') setDetalle(r.data.data);
    } catch (e) {
      console.error('Error cargando orden', e);
    }
  };

  const confirmarCancelar = async () => {
    if (!cancelarOrden) return;
    setCancelando(true);
    try {
      await API.post(`/compras/ordenes/${cancelarOrden.id}/cancelar`);
      setCancelarOrden(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo cancelar');
    } finally {
      setCancelando(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, color: COL.muted }}>{ordenes.length} órdenes</div>
        {puedeGestionar && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Boton COL={COL} ghost onClick={() => setModalColumnas(true)}><FiBox size={15} /> Especificaciones globales</Boton>
            <Boton COL={COL} ghost onClick={() => setModalProductos(true)}><FiBox size={15} /> Especificaciones de productos</Boton>
            <Boton COL={COL} onClick={() => setModalCrear(true)}><FiPlus size={15} /> Nueva orden</Boton>
          </div>
        )}
      </div>

      {ordenes.length === 0 && !cargando ? (
        <EmptyState icon={FiShoppingBag} COL={COL} title="Sin órdenes de compra" desc="Crea una solicitud a tu proveedor y recíbela total o parcialmente." actionLabel={puedeGestionar ? 'Nueva orden' : null} onAction={puedeGestionar ? () => setModalCrear(true) : null} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ordenes.map((o) => (
            <div key={o.id} style={{ padding: 12, borderRadius: 10, background: COL.surface, border: `1px solid ${COL.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: COL.text }}>{o.numero_orden}</div>
                <div style={{ fontSize: 12, color: COL.muted }}>{o.proveedor || 'Sin proveedor'} · {o.fecha_orden ? String(o.fecha_orden).slice(0, 10) : '—'} · {o.n_items} items</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {o.estado === 'cancelada' ? <OrdenEstado estado={o.estado} COL={COL} /> : <FaseBadge fase={o.fase} COL={COL} />}
                <IconBtn COL={COL} onClick={() => abrirDetalle(o.id)}><FiEye size={15} /></IconBtn>
                {puedeGestionar && o.estado !== 'cancelada' && o.fase !== 'recepcion' && o.fase !== 'revision_costos' && (
                  <IconBtn COL={COL} danger onClick={() => setCancelarOrden(o)}><FiXCircle size={15} /></IconBtn>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {modalCrear && (
        <ModalNuevaOrden
          COL={COL} isMobile={isMobile} sede={sede} sedes={sedes}
          onClose={() => setModalCrear(false)}
          onSaved={() => { setModalCrear(false); load(); }}
        />
      )}

      {modalProductos && (
        <ModalProductosEspecificaciones COL={COL} isMobile={isMobile} onClose={() => setModalProductos(false)} />
      )}

      {modalColumnas && (
        <ModalEspecificacionesGlobales COL={COL} isMobile={isMobile} onClose={() => setModalColumnas(false)} />
      )}

      {detalle && (
        <ModalDetalleOrden
          COL={COL} isMobile={isMobile} detalle={detalle} v={v} puedeGestionar={puedeGestionar}
          sede={sede} sedes={sedes}
          onClose={() => setDetalle(null)}
          onRecibido={() => { setDetalle(null); load(); }}
          onRefresh={abrirDetalle}
          onEditar={() => { setEditando(detalle); setDetalle(null); }}
        />
      )}

      {editando && (
        <ModalNuevaOrden
          COL={COL} isMobile={isMobile} sede={sede} sedes={sedes}
          ordenEdit={editando}
          onClose={() => setEditando(null)}
          onSaved={() => { setEditando(null); load(); }}
        />
      )}

      {cancelarOrden && (
        <ModalConfirmar
          COL={COL} isMobile={isMobile}
          titulo="Cancelar orden"
          mensaje={`¿Cancelar la orden ${cancelarOrden.numero_orden}? Esta acción no se puede deshacer.`}
          labelConfirmar="Cancelar orden"
          onClose={() => setCancelarOrden(null)}
          onConfirm={confirmarCancelar}
          guardando={cancelando}
        />
      )}
    </div>
  );
}

function ModalProductosEspecificaciones({ COL, isMobile, onClose }) {
  const [tab, setTab] = useState('registrados'); // 'registrados' | 'no_registrados'
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null); // {codigo, descripcion, incompleto}
  const [seleccionado, setSeleccionado] = useState(null); // producto registrado cargado
  const [noRegistrados, setNoRegistrados] = useState([]);
  const [cargandoNoReg, setCargandoNoReg] = useState(false);
  const [editandoNoReg, setEditandoNoReg] = useState(null); // item no registrado
  const [soloIncompletos, setSoloIncompletos] = useState(false);
  const [totalRegistrados, setTotalRegistrados] = useState(0);
  const [totalIncompletos, setTotalIncompletos] = useState(0);
  const [totalNoRegistrados, setTotalNoRegistrados] = useState(0);
  const [modalCompletarLote, setModalCompletarLote] = useState(false);
  const listRef = React.useRef(null);
  const abortRef = React.useRef(null);

  const buscar = useCallback(async (limpiar = true) => {
    if (abortRef.current) { try { abortRef.current.abort(); } catch (_) { /* noop */ } }
    const controller = new AbortController();
    abortRef.current = controller;
    setCargando(true);
    try {
      const r = await API.get('/compras/productos-simples', { params: { q: q || undefined, limite: 50, solo_incompletos: soloIncompletos || undefined }, signal: controller.signal });
      const items = r.data?.data?.items || [];
      setResultados(items);
      setHasMore(!!r.data?.data?.has_more);
      setTotalRegistrados(r.data?.data?.total ?? 0);
      setTotalIncompletos(r.data?.data?.total_incompletos ?? 0);
      const last = items[items.length - 1];
      setCursor(last ? { codigo: last.codigo, descripcion: last.descripcion, incompleto: !last.completo } : null);
      if (limpiar && listRef.current) listRef.current.scrollTop = 0;
    } catch (e) {
      if (e?.name !== 'AbortError' && e?.code !== 'ERR_CANCELED') {
        console.error('Error buscando productos', e);
      }
    } finally {
      setCargando(false);
    }
  }, [q, soloIncompletos]);

  // Debounce de 350ms para no disparar una peticion por pulsacion.
  useEffect(() => {
    const t = setTimeout(() => { buscar(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscar]);

  const cargarNoRegistrados = useCallback(async () => {
    setCargandoNoReg(true);
    try {
      const r = await API.get('/compras/productos-no-registrados', { params: { q: q || undefined } });
      setNoRegistrados(r.data?.data?.items || []);
      setTotalNoRegistrados(r.data?.data?.total ?? 0);
    } catch (e) {
      console.error('Error cargando no registrados', e);
    } finally {
      setCargandoNoReg(false);
    }
  }, [q]);

  // Cargar el contador de "No registrados" desde el inicio (no solo al abrir la pestaña).
  useEffect(() => { cargarNoRegistrados(); }, [cargarNoRegistrados]);

  const cargarMas = useCallback(async () => {
    if (cargandoMas || !hasMore || !cursor) return;
    setCargandoMas(true);
    try {
      const r = await API.get('/compras/productos-simples', {
        params: { q: q || undefined, limite: 50, solo_incompletos: soloIncompletos || undefined, ultimo_codigo: cursor.codigo, ultimo_descripcion: cursor.descripcion, ultimo_incompleto: cursor.incompleto },
      });
      const items = r.data?.data?.items || [];
      setResultados((prev) => {
        const seen = new Set(prev.map((p) => p.codigo));
        return [...prev, ...items.filter((it) => !seen.has(it.codigo))];
      });
      setHasMore(!!r.data?.data?.has_more);
      const last = items[items.length - 1];
      if (last) setCursor({ codigo: last.codigo, descripcion: last.descripcion, incompleto: !last.completo });
    } catch (e) {
      console.error('Error cargando más productos', e);
    } finally {
      setCargandoMas(false);
    }
  }, [cargandoMas, hasMore, cursor, q, soloIncompletos]);

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) cargarMas();
  };

  const abrirProducto = (p) => {
    setSeleccionado({ codigo: p.codigo, descripcion: p.descripcion, referencia: p.referencia || '' });
  };

  const abrirNoRegistrado = (it) => {
    setEditandoNoReg({ it, orden: { id: it.orden_id } });
  };

  if (seleccionado) {
    return (
      <ModalProductoEspecificacionesForm
        COL={COL} isMobile={isMobile}
        seleccionado={seleccionado}
        onClose={() => setSeleccionado(null)}
        onGuardado={() => { setSeleccionado(null); buscar(); }}
      />
    );
  }

  if (editandoNoReg) {
    return (
      <ModalEditarSpecsItem
        COL={COL} isMobile={isMobile}
        item={editandoNoReg.it}
        orden={editandoNoReg.orden}
        onClose={() => setEditandoNoReg(null)}
        onGuardado={() => { setEditandoNoReg(null); cargarNoRegistrados(); }}
      />
    );
  }

  if (modalCompletarLote) {
    return (
      <ModalCompletarLote
        COL={COL} isMobile={isMobile}
        onClose={() => setModalCompletarLote(false)}
        onAplicado={(n) => {
          setModalCompletarLote(false);
          alert(`Se actualizaron ${n} productos.`);
          buscar();
        }}
      />
    );
  }

  const visibles = resultados;
  const incompletos = totalIncompletos;

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={760} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiBox} title="Especificaciones de productos" subtitle="Asigna un valor de cada columna global al producto" onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, código o referencia…" style={{ ...inputStyle(COL), flex: 1, minWidth: 200 }} autoFocus />
            {tab === 'registrados' && (
              <>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: COL.text, whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={soloIncompletos} onChange={(e) => setSoloIncompletos(e.target.checked)} style={{ width: 15, height: 15, cursor: 'pointer' }} />
                  Solo incompletos ({incompletos})
                </label>
                {incompletos > 0 && (
                  <Boton COL={COL} ghost onClick={() => setModalCompletarLote(true)}><FiZap size={14} /> Completar en lote</Boton>
                )}
              </>
            )}
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button" onClick={() => setTab('registrados')} style={{ padding: '6px 14px', borderRadius: 8, border: tab === 'registrados' ? `1px solid ${COL.pri2}` : `1px solid ${COL.border}`, background: tab === 'registrados' ? COL.blueBg : 'transparent', color: tab === 'registrados' ? COL.pri2 : COL.muted, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              Registrados ({totalRegistrados})
            </button>
            <button type="button" onClick={() => setTab('no_registrados')} style={{ padding: '6px 14px', borderRadius: 8, border: tab === 'no_registrados' ? `1px solid ${COL.pri2}` : `1px solid ${COL.border}`, background: tab === 'no_registrados' ? COL.blueBg : 'transparent', color: tab === 'no_registrados' ? COL.pri2 : COL.muted, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              No registrados ({totalNoRegistrados})
            </button>
          </div>

          {tab === 'registrados' ? (
            cargando && resultados.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: COL.muted }}>Buscando…</div>
            ) : visibles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: COL.muted }}>Sin resultados</div>
            ) : (
              <div ref={listRef} onScroll={onScroll} style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', minHeight: 0, flex: 1 }}>
                {visibles.map((p) => (
                  <button key={p.codigo} type="button" onClick={() => abrirProducto(p)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, border: `1px solid ${p.completo ? COL.border : COL.amber}`, background: p.completo ? COL.inputBg : COL.amberBg, cursor: 'pointer', textAlign: 'left', color: COL.text }}>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{p.descripcion}</span>
                      <span style={{ display: 'block', fontSize: 11, color: COL.muted }}>{p.codigo}{p.referencia ? ` · ${p.referencia}` : ''}</span>
                      {!p.completo && (
                        <span style={{ display: 'block', fontSize: 10.5, color: COL.amber, marginTop: 2 }}>
                          <FiAlertTriangle size={11} style={{ verticalAlign: '-2px' }} /> Falta: {p.faltantes.join(', ')}
                        </span>
                      )}
                    </span>
                    {!p.completo ? (
                      <span style={{ ...S.badge, background: COL.amberBg, color: COL.amber, flexShrink: 0 }}>Incompleto</span>
                    ) : (
                      <FiEdit2 size={14} style={{ color: COL.muted, flexShrink: 0 }} />
                    )}
                  </button>
                ))}
                {cargandoMas && <div style={{ textAlign: 'center', padding: 12, color: COL.muted }}>Cargando más…</div>}
                {!hasMore && visibles.length > 0 && (
                  <div style={{ textAlign: 'center', padding: 10, color: COL.faint, fontSize: 11 }}>Fin de la lista ({resultados.length} productos)</div>
                )}
              </div>
            )
          ) : (
            cargandoNoReg ? (
              <div style={{ textAlign: 'center', padding: 24, color: COL.muted }}>Cargando…</div>
            ) : noRegistrados.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: COL.muted }}>Sin productos no registrados</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', minHeight: 0, flex: 1 }}>
                {noRegistrados.map((it) => (
                  <button key={it.id} type="button" onClick={() => abrirNoRegistrado(it)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.inputBg, cursor: 'pointer', textAlign: 'left', color: COL.text }}>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{it.descripcion}</span>
                      <span style={{ display: 'block', fontSize: 11, color: COL.muted }}>Ref: {it.referencia || '—'}{it.numero_orden ? ` · ${it.numero_orden}` : ''}</span>
                    </span>
                    <FiEdit2 size={14} style={{ color: COL.muted, flexShrink: 0 }} />
                  </button>
                ))}
              </div>
            )
          )}
        </div>
        <ModalFooter onClose={onClose} onSave={onClose} COL={COL} label="Cerrar" guardando={false} />
      </div>
    </Overlay>
  );
}

// Selector de valor por columna global + agregar valor al vuelo.
function SelectorValorColumna({ COL, columna, valor, onChange, onNuevoValor, isMobile }) {
  const [agregando, setAgregando] = useState(false);
  const [nuevo, setNuevo] = useState('');
  const [agregar, setAgregar] = useState(false);

  const valores = (columna.valores || []).filter((v) => v.activa !== false);

  const confirmarAgregar = async () => {
    const v = nuevo.trim();
    if (!v) return;
    setAgregar(true);
    try {
      await onNuevoValor(columna.id, v);
      onChange(v);
      setAgregando(false);
      setNuevo('');
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo agregar el valor');
    } finally {
      setAgregar(false);
    }
  };

  if (agregando) {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input value={nuevo} onChange={(e) => setNuevo(e.target.value)} style={inputStyle(COL)} placeholder="Nuevo valor…" autoFocus />
        <Boton COL={COL} onClick={confirmarAgregar} disabled={agregar}>{agregar ? '…' : 'Guardar'}</Boton>
        <IconBtn COL={COL} onClick={() => setAgregando(false)}><FiX size={14} /></IconBtn>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <select value={valor || ''} onChange={(e) => onChange(e.target.value)} style={selectStyle(COL)}>
        <option value="">— Sin asignar —</option>
        {valores.map((v) => <option key={v.id} value={v.valor}>{v.valor}</option>)}
      </select>
      <IconBtn COL={COL} onClick={() => setAgregando(true)}><FiPlus size={14} /></IconBtn>
    </div>
  );
}

// Asigna un valor de una columna a TODOS los productos que la tienen vacía.
function ModalCompletarLote({ COL, isMobile, onClose, onAplicado }) {
  const [columnas, setColumnas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [columnaId, setColumnaId] = useState('');
  const [valor, setValor] = useState('');
  const [proveedorId, setProveedorId] = useState('');
  const [aplicando, setAplicando] = useState(false);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let on = true;
    Promise.all([
      API.get('/compras/especificaciones-columnas'),
      API.get('/compras/proveedores', { params: { limite: 200 } }),
    ]).then(([colRes, provRes]) => {
      if (!on) return;
      const cols = (colRes.data?.data || []).filter((c) => c.activa !== false && c.requerida !== false);
      setColumnas(cols);
      const provs = provRes.data?.data?.items || [];
      setProveedores(provs);
      if (provs.length) setProveedorId(String(provs[0].id));
    }).catch(() => {}).finally(() => { if (on) setCargando(false); });
    return () => { on = false; };
  }, []);

  const columna = columnas.find((c) => String(c.id) === String(columnaId)) || null;
  const tipo = columna?.tipo || 'enum';
  const valores = (columna?.valores || []).filter((v) => v.activa !== false);

  const aplicar = async () => {
    if (!columna) { alert('Selecciona una columna'); return; }
    if (!valor.trim()) { alert('Indica el valor a aplicar'); return; }
    setAplicando(true);
    try {
      const r = await API.post('/compras/especificaciones-aplicar-masivo', {
        columna_id: columna.id,
        valor: valor.trim(),
        proveedor_id: tipo === 'proveedor' && proveedorId ? Number(proveedorId) : null,
      });
      onAplicado(r.data?.data?.actualizados ?? 0);
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo aplicar');
    } finally {
      setAplicando(false);
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={560} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiZap} title="Completar en lote" subtitle="Asigna un valor a todos los productos que lo tienen vacío" onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {cargando ? (
            <div style={{ textAlign: 'center', padding: 20, color: COL.muted }}>Cargando…</div>
          ) : columnas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: COL.muted }}>No hay columnas requeridas configuradas.</div>
          ) : (
            <>
              <Field label="Columna" icon={FiBox} required COL={COL}>
                <select value={columnaId} onChange={(e) => { setColumnaId(e.target.value); setValor(''); }} style={selectStyle(COL)}>
                  <option value="">— Selecciona —</option>
                  {columnas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
              </Field>

              {columna && tipo === 'enum' && (
                <Field label="Valor" icon={FiCheck} required COL={COL}>
                  <select value={valor} onChange={(e) => setValor(e.target.value)} style={selectStyle(COL)}>
                    <option value="">— Selecciona —</option>
                    {valores.map((v) => <option key={v.id} value={v.valor}>{v.valor}</option>)}
                  </select>
                </Field>
              )}

              {columna && tipo === 'proveedor' && (
                <>
                  <Field label="Proveedor" icon={FiTruck} required COL={COL}>
                    <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} style={selectStyle(COL)}>
                      {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                    </select>
                  </Field>
                  <Field label="Código del proveedor" icon={FiHash} required COL={COL}>
                    <input value={valor} onChange={(e) => setValor(e.target.value)} style={inputStyle(COL)} placeholder="Ej. LBQQ-0611" />
                  </Field>
                </>
              )}

              {columna && (tipo === 'texto' || tipo === 'idioma') && (
                <Field label={tipo === 'idioma' ? `Nombre en ${columna.idioma || columna.nombre}` : 'Valor'} icon={FiHash} required COL={COL}>
                  <input value={valor} onChange={(e) => setValor(e.target.value)} style={inputStyle(COL)} placeholder="Valor a aplicar a todos" />
                </Field>
              )}

              {columna && (
                <div style={{ fontSize: 11.5, color: COL.muted }}>
                  Se aplicará a todos los productos que tengan esta columna vacía.
                </div>
              )}
            </>
          )}
        </div>
        <ModalFooter onClose={onClose} onSave={aplicar} COL={COL} label="Aplicar a todos" guardando={aplicando} />
      </div>
    </Overlay>
  );
}

function ModalProductoEspecificacionesForm({ COL, isMobile, seleccionado, onClose, onGuardado }) {
  const [columnas, setColumnas] = useState([]);
  const [valores, setValores] = useState({}); // {nombre_columna: valor} (enum + derivadas)
  const [nombres, setNombres] = useState({}); // {idioma: nombre} (tipo idioma)
  const [proveedores, setProveedores] = useState([]);
  const [proveedorId, setProveedorId] = useState('');
  const [proveedorCodigo, setProveedorCodigo] = useState(''); // (tipo proveedor)
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let on = true;
    Promise.all([
      API.get('/compras/especificaciones-columnas'),
      API.get('/compras/proveedores', { params: { limite: 200 } }),
      API.get(`/compras/producto/${seleccionado.codigo}/especificaciones`),
      API.get(`/compras/producto/${seleccionado.codigo}/nombres`).catch(() => ({ data: { data: {} } })),
      API.get(`/compras/producto/${seleccionado.codigo}/proveedor-codigos`).catch(() => ({ data: { data: {} } })),
    ]).then(([colRes, provRes, prodRes, nomRes, codRes]) => {
      if (!on) return;
      setColumnas(colRes.data?.data || []);
      const provs = provRes.data?.data?.items || [];
      setProveedores(provs);
      const mapa = {};
      (prodRes.data?.data?.especificaciones || []).forEach((s) => { mapa[s.etiqueta] = s.valor; });
      setValores(mapa);
      setNombres(nomRes.data?.data || {});
      const pc = codRes.data?.data || {};
      const ids = Object.keys(pc);
      if (ids.length) {
        setProveedorId(String(ids[0]));
        setProveedorCodigo(pc[ids[0]] || '');
      } else if (provs.length) {
        setProveedorId(String(provs[0].id));
        setProveedorCodigo('');
      }
    }).catch(() => {}).finally(() => { if (on) setCargando(false); });
    return () => { on = false; };
  }, [seleccionado.codigo]);

  const derivadaDe = (col) => (columnas || []).find((c) => c.id === col.derivada_de);
  const esDerivada = (col) => !!col.derivada_de;
  const tipoDe = (col) => col.tipo || 'enum';

  // Auto-relleno generico: al elegir un valor de una columna que tiene columnas
  // derivadas (hijas), se rellenan las hijas desde `valor.derivados`. Si se limpia
  // el padre, se limpian tambien las hijas derivadas.
  const onCambioValor = (col, v) => {
    setValores((p) => {
      const mapa = { ...p, [col.nombre]: v };
      const hijos = (columnas || []).filter((c) => c.derivada_de === col.id);
      if (hijos.length) {
        const valorObj = (col.valores || []).find((vv) => vv.valor === v);
        const derivados = valorObj?.derivados || {};
        hijos.forEach((h) => { mapa[h.nombre] = derivados[h.nombre] != null ? derivados[h.nombre] : ''; });
      }
      return mapa;
    });
  };

  const onNuevoValor = async (columnaId, valor) => {
    await API.post(`/compras/especificaciones-columnas/${columnaId}/valores`, { valor });
    const r = await API.get('/compras/especificaciones-columnas');
    setColumnas(r.data?.data || []);
  };

  const guardar = async () => {
    setError('');
    setGuardando(true);
    try {
      // Enum + derivadas -> ma_productos.especificaciones.
      const especificaciones = Object.entries(valores)
        .filter(([, v]) => v != null && String(v).trim() !== '')
        .map(([etiqueta, valor]) => ({ etiqueta, valor: String(valor).trim() }));
      await API.put(`/compras/producto/${seleccionado.codigo}/especificaciones`, { columnas: especificaciones });
      // Idiomas -> ma_producto_nombres.
      const idiomaCols = (columnas || []).filter((c) => tipoDe(c) === 'idioma' && c.idioma);
      if (idiomaCols.length) {
        const nombresPayload = idiomaCols.map((c) => ({ idioma: c.idioma, nombre: (nombres[c.idioma] || '').trim() }));
        await API.put(`/compras/producto/${seleccionado.codigo}/nombres`, { nombres: nombresPayload });
      }
      // Proveedor -> producto_proveedor (con proveedor_id explicito; soporta limpiar).
      const proveedorCols = (columnas || []).filter((c) => tipoDe(c) === 'proveedor');
      if (proveedorCols.length) {
        await API.put(`/compras/producto/${seleccionado.codigo}/proveedor-codigo`, {
          proveedor_id: proveedorId ? Number(proveedorId) : null,
          codigo_proveedor: proveedorCodigo.trim(),
        });
      }
      if (onGuardado) onGuardado();
      else onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={640} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiBox} title="Especificaciones del producto" subtitle={seleccionado.descripcion} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ fontSize: 12, color: COL.muted }}>
            {seleccionado.codigo}{seleccionado.referencia ? ` · Ref: ${seleccionado.referencia}` : ''}
          </div>

          {cargando ? (
            <div style={{ textAlign: 'center', padding: 20, color: COL.muted }}>Cargando…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {columnas.filter((c) => c.activa !== false).map((col) => {
                const esDer = esDerivada(col);
                const padre = esDer ? derivadaDe(col) : null;
                const tipo = tipoDe(col);
                return (
                  <div key={col.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>{col.nombre}</span>
                      {esDer && padre && <span style={{ fontSize: 10, color: COL.faint }}>(auto · {padre.nombre})</span>}
                      {tipo === 'idioma' && <span style={{ fontSize: 10, color: COL.pri2 }}>idioma · {col.idioma}</span>}
                      {tipo === 'proveedor' && <span style={{ fontSize: 10, color: COL.pri2 }}>proveedor</span>}
                    </div>
                    {esDer ? (
                      <input value={valores[col.nombre] || ''} onChange={(e) => setValores((p) => ({ ...p, [col.nombre]: e.target.value }))} style={inputStyle(COL)} placeholder="Se rellena automáticamente al elegir el padre" />
                    ) : tipo === 'idioma' ? (
                      <input value={nombres[col.idioma] || ''} onChange={(e) => setNombres((p) => ({ ...p, [col.idioma]: e.target.value }))} style={inputStyle(COL)} placeholder={`Nombre en ${col.idioma}`} />
                    ) : tipo === 'proveedor' ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} style={{ ...selectStyle(COL), maxWidth: 200 }}>
                          <option value="">Sin proveedor</option>
                          {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
                        </select>
                        <input value={proveedorCodigo} onChange={(e) => setProveedorCodigo(e.target.value)} style={inputStyle(COL)} placeholder="Código del proveedor" />
                      </div>
                    ) : tipo === 'texto' ? (
                      <input value={valores[col.nombre] || ''} onChange={(e) => setValores((p) => ({ ...p, [col.nombre]: e.target.value }))} style={inputStyle(COL)} placeholder={`${col.nombre}…`} />
                    ) : (
                      <SelectorValorColumna COL={COL} columna={col} valor={valores[col.nombre]} onChange={(v) => onCambioValor(col, v)} onNuevoValor={onNuevoValor} isMobile={isMobile} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label="Guardar especificaciones" guardando={guardando} />
      </div>
    </Overlay>
  );
}

// Editor de columnas globales para items de orden (registrados o manuales/no_registrado).
// Carga las columnas y renderiza: select (enum, con auto-relleno de derivadas),
// input de idioma y input de proveedor. Expone los valores vía onCambio.
function EditorColumnasGlobales({ COL, isMobile, item, onCambio }) {
  const [columnas, setColumnas] = useState([]);
  const [valores, setValores] = useState({});
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    let on = true;
    API.get('/compras/especificaciones-columnas')
      .then((r) => {
        if (!on) return;
        const cols = r.data?.data || [];
        setColumnas(cols);
        setValores(valoresDesdeItem(cols, item || {}));
      })
      .catch(() => {})
      .finally(() => { if (on) setCargando(false); });
    return () => { on = false; };
  }, [item]);

  const esDerivada = (col) => !!col.derivada_de;
  const derivadaDe = (col) => columnas.find((c) => c.id === col.derivada_de);
  const tipoDe = (col) => col.tipo || 'enum';

  // Sincroniza el mapa de valores hacia el padre en cada cambio (incluida la carga inicial).
  useEffect(() => {
    if (onCambio) onCambio(valores);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valores]);

  const setValor = (nombre, v) => {
    setValores((p) => ({ ...p, [nombre]: v }));
  };

  const onCambioValor = (col, v) => {
    setValores((p) => {
      const mapa = { ...p, [col.nombre]: v };
      const hijos = columnas.filter((c) => c.derivada_de === col.id);
      if (hijos.length) {
        const valorObj = (col.valores || []).find((vv) => vv.valor === v);
        const derivados = valorObj?.derivados || {};
        hijos.forEach((h) => { mapa[h.nombre] = derivados[h.nombre] != null ? derivados[h.nombre] : ''; });
      }
      return mapa;
    });
  };

  const onNuevoValor = async (columnaId, valor) => {
    await API.post(`/compras/especificaciones-columnas/${columnaId}/valores`, { valor });
    const r = await API.get('/compras/especificaciones-columnas');
    setColumnas(r.data?.data || []);
  };

  if (cargando) {
    return <div style={{ textAlign: 'center', padding: 16, color: COL.muted }}>Cargando…</div>;
  }
  if (columnas.length === 0) {
    return <div style={{ textAlign: 'center', padding: 16, color: COL.muted }}>Sin columnas configuradas.</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {columnas.filter((c) => c.activa !== false).map((col) => {
        const esDer = esDerivada(col);
        const padre = esDer ? derivadaDe(col) : null;
        const tipo = tipoDe(col);
        return (
          <div key={col.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>{col.nombre}</span>
              {esDer && padre && <span style={{ fontSize: 10, color: COL.faint }}>(auto · {padre.nombre})</span>}
              {tipo === 'idioma' && <span style={{ fontSize: 10, color: COL.pri2 }}>idioma · {col.idioma}</span>}
              {tipo === 'proveedor' && <span style={{ fontSize: 10, color: COL.pri2 }}>proveedor</span>}
            </div>
            {esDer ? (
              <input value={valores[col.nombre] || ''} onChange={(e) => setValor(col.nombre, e.target.value)} style={inputStyle(COL)} placeholder="Se rellena automáticamente al elegir el padre" />
            ) : tipo === 'idioma' ? (
              <input value={valores[col.nombre] || ''} onChange={(e) => setValor(col.nombre, e.target.value)} style={inputStyle(COL)} placeholder={`Nombre en ${col.idioma || col.nombre}`} />
            ) : tipo === 'proveedor' ? (
              <input value={valores[col.nombre] || ''} onChange={(e) => setValor(col.nombre, e.target.value)} style={inputStyle(COL)} placeholder="Código del proveedor" />
            ) : tipo === 'texto' ? (
              <input value={valores[col.nombre] || ''} onChange={(e) => setValor(col.nombre, e.target.value)} style={inputStyle(COL)} placeholder={`${col.nombre}…`} />
            ) : (
              <SelectorValorColumna COL={COL} columna={col} valor={valores[col.nombre]} onChange={(v) => onCambioValor(col, v)} onNuevoValor={onNuevoValor} isMobile={isMobile} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// Gestión de las columnas globales de especificación (CRUD columnas + valores).
// Permite a cada empresa armar su propia estructura: crear columnas, marcarlas
// como derivadas de otra, agregar valores y (para columnas padre) definir los
// valores derivados de cada opción.
function ModalEspecificacionesGlobales({ COL, isMobile, onClose, onChanged }) {
  const [columnas, setColumnas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [nueva, setNueva] = useState('');
  const [nuevaTipo, setNuevaTipo] = useState('enum');
  const [nuevaIdioma, setNuevaIdioma] = useState('');
  const [nuevaDerivadaDe, setNuevaDerivadaDe] = useState('');
  const [nuevoValorPorCol, setNuevoValorPorCol] = useState({}); // {colId: texto}
  const [editandoDerivados, setEditandoDerivados] = useState(null); // {col, valor}
  const [renombrando, setRenombrando] = useState(null); // {id, nombre}
  const [plantillas, setPlantillas] = useState([]);
  const [aplicandoPlantilla, setAplicandoPlantilla] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await API.get('/compras/especificaciones-columnas');
      setColumnas(r.data?.data || []);
    } catch (e) {
      console.error('Error cargando columnas', e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get('/compras/especificaciones-plantillas')
      .then((r) => setPlantillas(r.data?.data || []))
      .catch(() => {});
  }, []);

  const aplicarPlantilla = async (pid) => {
    setAplicandoPlantilla(true);
    try {
      await API.post('/compras/especificaciones-aplicar-plantilla', { plantilla_id: pid });
      load();
      if (onChanged) onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo aplicar la plantilla');
    } finally {
      setAplicandoPlantilla(false);
    }
  };

  const hijosDe = (colId) => (columnas || []).filter((c) => c.derivada_de === colId);

  const crearColumna = async () => {
    const n = nueva.trim();
    if (!n) return;
    if (nuevaTipo === 'idioma' && !nuevaIdioma.trim()) {
      alert('Indica el código de idioma (ej. CHINESE)');
      return;
    }
    try {
      await API.post('/compras/especificaciones-columnas', {
        nombre: n,
        tipo: nuevaTipo,
        idioma: nuevaTipo === 'idioma' ? nuevaIdioma.trim() : null,
        derivada_de: nuevaTipo === 'enum' && nuevaDerivadaDe ? Number(nuevaDerivadaDe) : null,
      });
      setNueva('');
      setNuevaTipo('enum');
      setNuevaIdioma('');
      setNuevaDerivadaDe('');
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo crear la columna');
    }
  };

  const agregarValor = async (col) => {
    const v = (nuevoValorPorCol[col.id] || '').trim();
    if (!v) return;
    try {
      await API.post(`/compras/especificaciones-columnas/${col.id}/valores`, { valor: v });
      setNuevoValorPorCol((p) => ({ ...p, [col.id]: '' }));
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo agregar el valor');
    }
  };

  const eliminarValor = async (v) => {
    if (!window.confirm(`¿Eliminar el valor "${v.valor}"?`)) return;
    try {
      await API.delete(`/compras/especificaciones-valores/${v.id}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const eliminarColumna = async (col) => {
    if (!window.confirm(`¿Eliminar la columna "${col.nombre}" y sus valores?`)) return;
    try {
      await API.delete(`/compras/especificaciones-columnas/${col.id}`);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const renombrarColumna = async () => {
    if (!renombrando) return;
    const n = renombrando.nombre.trim();
    if (!n) { setRenombrando(null); return; }
    try {
      await API.put(`/compras/especificaciones-columnas/${renombrando.id}`, { nombre: n });
      setRenombrando(null);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo renombrar');
    }
  };

  const toggleRequerida = async (col) => {
    const nuevo = col.requerida === false ? true : false;
    try {
      await API.put(`/compras/especificaciones-columnas/${col.id}`, { requerida: nuevo });
      load();
      if (onChanged) onChanged();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo actualizar');
    }
  };

  const tipoLabel = (t) => ({ enum: 'Lista de opciones', texto: 'Texto libre', idioma: 'Nombre por idioma', proveedor: 'Código de proveedor' }[t] || t);

  if (editandoDerivados) {
    return (
      <ModalEditarDerivados
        COL={COL} isMobile={isMobile}
        columna={editandoDerivados.col}
        valor={editandoDerivados.valor}
        hijos={hijosDe(editandoDerivados.col.id)}
        onClose={() => setEditandoDerivados(null)}
        onGuardado={() => { setEditandoDerivados(null); load(); }}
      />
    );
  }

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={760} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiBox} title="Especificaciones globales" subtitle="Arma la estructura de especificación de tu empresa" onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={nueva} onChange={(e) => setNueva(e.target.value)} style={{ ...inputStyle(COL), flex: 1, minWidth: 160 }} placeholder="Nueva columna (ej. COLOR)" />
            <select value={nuevaTipo} onChange={(e) => setNuevaTipo(e.target.value)} style={{ ...selectStyle(COL), width: 150 }}>
              <option value="enum">Lista de opciones</option>
              <option value="texto">Texto libre</option>
              <option value="idioma">Nombre por idioma</option>
              <option value="proveedor">Código de proveedor</option>
            </select>
            {nuevaTipo === 'idioma' && (
              <input value={nuevaIdioma} onChange={(e) => setNuevaIdioma(e.target.value)} style={{ ...inputStyle(COL), width: 120 }} placeholder="Código (CHINESE)" />
            )}
            {nuevaTipo === 'enum' && (
              <select value={nuevaDerivadaDe} onChange={(e) => setNuevaDerivadaDe(e.target.value)} style={{ ...selectStyle(COL), width: 200 }}>
                <option value="">Sin derivación</option>
                {columnas.filter((c) => (c.tipo || 'enum') === 'enum').map((c) => <option key={c.id} value={c.id}>Deriva de: {c.nombre}</option>)}
              </select>
            )}
            <Boton COL={COL} onClick={crearColumna}><FiPlus size={14} /> Crear</Boton>
          </div>

          {cargando ? (
            <div style={{ textAlign: 'center', padding: 20, color: COL.muted }}>Cargando…</div>
          ) : columnas.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12 }}>
              <div style={{ padding: 14, borderRadius: 10, background: COL.blueBg, fontSize: 13, color: COL.text }}>
                <strong>Empieza tu estructura de especificaciones.</strong> Elige una plantilla según tu negocio o crea la primera columna arriba. Después podrás editar todo libremente.
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>Plantillas por tipo de negocio</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {plantillas.map((p) => (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 10, border: `1px solid ${COL.border}`, background: COL.surface }}>
                    <span style={{ minWidth: 0 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>{p.nombre}</span>
                      <span style={{ display: 'block', fontSize: 11.5, color: COL.muted }}>{p.descripcion}</span>
                    </span>
                    <Boton COL={COL} ghost onClick={() => aplicarPlantilla(p.id)} disabled={aplicandoPlantilla}>
                      {aplicandoPlantilla ? 'Aplicando…' : 'Usar plantilla'}
                    </Boton>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {columnas.map((col) => {
                const hijos = hijosDe(col.id);
                const tipo = col.tipo || 'enum';
                return (
                  <div key={col.id} style={{ padding: '10px 12px', borderRadius: 10, border: `1px solid ${COL.border}`, background: COL.bg2 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                      {renombrando && renombrando.id === col.id ? (
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
                          <input value={renombrando.nombre} onChange={(e) => setRenombrando({ id: col.id, nombre: e.target.value })} style={inputStyle(COL)} autoFocus />
                          <Boton COL={COL} onClick={renombrarColumna}><FiCheck size={13} /></Boton>
                          <IconBtn COL={COL} onClick={() => setRenombrando(null)}><FiX size={13} /></IconBtn>
                        </div>
                      ) : (
                        <span style={{ fontSize: 13, fontWeight: 700, color: COL.text }}>
                          {col.nombre}
                          <span style={{ fontSize: 10, color: COL.pri2, fontWeight: 600, marginLeft: 6 }}>{tipoLabel(tipo)}{tipo === 'idioma' && col.idioma ? ` · ${col.idioma}` : ''}</span>
                          {col.derivada_de && <span style={{ fontSize: 10, color: COL.faint, fontWeight: 400 }}> · derivada</span>}
                          <button type="button" onClick={() => setRenombrando({ id: col.id, nombre: col.nombre })} style={{ border: 'none', background: 'transparent', color: COL.pri2, cursor: 'pointer', padding: 0, marginLeft: 6, verticalAlign: '-2px' }}>
                            <FiEdit2 size={12} />
                          </button>
                        </span>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button type="button" onClick={() => toggleRequerida(col)} title={col.requerida === false ? 'Ignorada en la revisión de incompletos' : 'Requerida en la revisión de incompletos'} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: col.requerida === false ? COL.faint : COL.green, whiteSpace: 'nowrap' }}>
                          {col.requerida === false ? <FiEyeOff size={13} /> : <FiEye size={13} />}
                          {col.requerida === false ? 'Ignorada' : 'Requerida'}
                        </button>
                        <IconBtn COL={COL} danger onClick={() => eliminarColumna(col)}><FiTrash2 size={13} /></IconBtn>
                      </div>
                    </div>
                    {tipo === 'enum' && (
                      <>
                        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                          <input value={nuevoValorPorCol[col.id] || ''} onChange={(e) => setNuevoValorPorCol((p) => ({ ...p, [col.id]: e.target.value }))} style={inputStyle(COL)} placeholder="Nuevo valor…" />
                          <Boton COL={COL} ghost onClick={() => agregarValor(col)}><FiPlus size={13} /></Boton>
                        </div>
                        {(col.valores || []).length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                            {col.valores.map((v) => {
                              const derivados = v.derivados || {};
                              const derivadosActivos = hijos.filter((h) => derivados[h.nombre] != null && String(derivados[h.nombre]).trim() !== '');
                              return (
                              <div key={v.id} style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '4px 8px', borderRadius: 6, background: COL.surface }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                  <span style={{ fontSize: 12.5, color: COL.text, wordBreak: 'break-word', flex: 1 }}>{v.valor}</span>
                                  {hijos.length > 0 && (
                                    <button type="button" onClick={() => setEditandoDerivados({ col, valor: v })} style={{ border: 'none', background: 'transparent', color: COL.pri2, cursor: 'pointer', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                      <FiEdit2 size={12} /> Derivados
                                    </button>
                                  )}
                                  <IconBtn COL={COL} danger onClick={() => eliminarValor(v)}><FiX size={12} /></IconBtn>
                                </div>
                                {derivadosActivos.length > 0 && (
                                  <div style={{ fontSize: 10.5, color: COL.faint }}>
                                    {derivadosActivos.map((h) => (
                                      <span key={h.id} style={{ display: 'inline-block', marginRight: 8 }}>
                                        → {h.nombre}: <span style={{ color: COL.text }}>{derivados[h.nombre]}</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <ModalFooter onClose={onClose} onSave={onClose} COL={COL} label="Cerrar" guardando={false} />
      </div>
    </Overlay>
  );
}

// Edita los valores derivados (hijos) de una opción de una columna padre.
function ModalEditarDerivados({ COL, isMobile, columna, valor, hijos, onClose, onGuardado }) {
  const [derivados, setDerivados] = useState(() => {
    const d = valor.derivados || {};
    return hijos.map((h) => ({ id: h.id, nombre: h.nombre, valor: d[h.nombre] != null ? d[h.nombre] : '' }));
  });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const setHijo = (idx, v) => setDerivados((p) => p.map((h, i) => (i === idx ? { ...h, valor: v } : h)));

  const guardar = async () => {
    setError('');
    const mapa = {};
    derivados.forEach((h) => { if (h.valor != null && String(h.valor).trim() !== '') mapa[h.nombre] = String(h.valor).trim(); });
    setGuardando(true);
    try {
      await API.put(`/compras/especificaciones-valores/${valor.id}`, { derivados: mapa });
      onGuardado();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar');
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={560} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiEdit2} title="Valores derivados" subtitle={`${columna.nombre} = ${valor.valor}`} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ fontSize: 12, color: COL.muted }}>Al elegir este valor, se rellenan automáticamente estas columnas derivadas:</div>
          {derivados.map((h, idx) => (
            <div key={h.id}>
              <div style={{ fontSize: 11, fontWeight: 700, color: COL.muted, marginBottom: 4 }}>{h.nombre}</div>
              <input value={h.valor} onChange={(e) => setHijo(idx, e.target.value)} style={inputStyle(COL)} placeholder="Valor derivado…" />
            </div>
          ))}
          {derivados.length === 0 && <div style={{ color: COL.muted, fontSize: 12 }}>Esta columna no tiene columnas derivadas.</div>}
          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label="Guardar derivados" guardando={guardando} />
      </div>
    </Overlay>
  );
}

function ModalNuevaOrden({ COL, isMobile, sede, sedes, onClose, onSaved, ordenEdit }) {
  const [draft] = useState(ordenEdit ? null : cargarOrdenDraft);
  const [proveedores, setProveedores] = useState([]);
  const [proveedorId, setProveedorId] = useState(ordenEdit ? (ordenEdit.orden.proveedor_id ?? '') : (draft?.proveedorId ?? ''));
  const [sedeSel, setSedeSel] = useState(ordenEdit ? (ordenEdit.orden.sede_codigo ?? sede ?? '') : (draft?.sedeSel ?? sede ?? ''));
  const [fecha, setFecha] = useState(ordenEdit ? (ordenEdit.orden.fecha_orden ? String(ordenEdit.orden.fecha_orden).slice(0, 10) : '') : (draft?.fecha ?? (() => new Date().toISOString().slice(0, 10))));
  const [notas, setNotas] = useState(ordenEdit ? (ordenEdit.orden.notas ?? '') : (draft?.notas ?? ''));
  const [items, setItems] = useState(ordenEdit ? (ordenEdit.items || []).map((it) => ({
    tipo_item: it.tipo_item,
    producto_codigo: it.producto_codigo,
    insumo_id: it.insumo_id,
    descripcion: it.descripcion,
    cantidad: it.cantidad_solicitada,
    costo: it.precio_proveedor ?? it.costo_estimado,
    referencia: it.referencia,
  })) : (draft?.items ?? []));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [buscador, setBuscador] = useState(false);

  useEffect(() => {
    API.get('/compras/proveedores', { params: { limite: 200 } })
      .then((r) => setProveedores(r.data?.data?.items || []))
      .catch(() => {});
  }, []);

  // Guarda el borrador en localStorage en cada cambio (solo al CREAR, no al editar).
  useEffect(() => {
    if (ordenEdit) return;
    const data = { proveedorId, sedeSel, fecha, notas, items };
    try { localStorage.setItem(ORDEN_DRAFT_KEY, JSON.stringify(data)); } catch (e) { /* noop */ }
  }, [ordenEdit, proveedorId, sedeSel, fecha, notas, items]);

  const buildItems = () => items.map((it) => ({
    tipo_item: it.tipo_item,
    producto_codigo: it.tipo_item === 'producto' ? it.producto_codigo : null,
    insumo_id: it.tipo_item === 'insumo' ? (it.insumo_id || null) : null,
    descripcion: it.descripcion,
    cantidad: Number(it.cantidad),
    costo_estimado: Number(it.costo) || null,
    referencia: it.referencia || null,
    unidad: it.unidad || null,
    codigo_fchp: it.codigo_fchp || null,
    codigo_proveedor: it.codigo_proveedor || null,
  }));

  const guardar = async (fase) => {
    setError('');
    if (!items.length) { setError('Agrega al menos un item'); return; }
    setGuardando(true);
    try {
      if (ordenEdit) {
        await API.put(`/compras/ordenes/${ordenEdit.orden.id}`, {
          proveedor_id: proveedorId ? Number(proveedorId) : null,
          sede_codigo: sedeSel,
          fecha_orden: fecha || null,
          notas: notas || null,
          items: buildItems(),
        });
      } else {
        await API.post('/compras/ordenes', {
          proveedor_id: proveedorId ? Number(proveedorId) : null,
          sede_codigo: sedeSel,
          fecha_orden: fecha || null,
          notas: notas || null,
          fase,
          items: buildItems(),
        });
        limpiarOrdenDraft();
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar la orden');
    } finally {
      setGuardando(false);
    }
  };

  const descartar = () => {
    limpiarOrdenDraft();
    setProveedorId('');
    setSedeSel(sede || '');
    setFecha(new Date().toISOString().slice(0, 10));
    setNotas('');
    setItems([]);
  };

  const tieneBorrador = !ordenEdit && !!(draft && (draft.items?.length || draft.notas || draft.proveedorId));
  const total = items.reduce((s, it) => s + Number(it.cantidad || 0) * Number(it.costo || 0), 0);

  const quitarItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const setItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  if (buscador) {
    return (
      <SelectorItem
        COL={COL} isMobile={isMobile}
        onClose={() => setBuscador(false)}
        items={items}
        onPick={(it) => { setItems((prev) => sumarItem(prev, { ...it, costo: Number(it.costo || 0) })); }}
      />
    );
  }

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={1000} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader
          icon={FiShoppingBag}
          title={ordenEdit ? `Editar ${ordenEdit.orden.numero_orden}` : 'Nueva orden de compra'}
          subtitle={ordenEdit ? 'Modifica los items y cantidades solicitadas' : 'Se guarda como borrador hasta que la envíes al proveedor'}
          onClose={onClose} COL={COL}
        />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {tieneBorrador && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', borderRadius: 8, background: COL.blueBg, fontSize: 12, color: COL.text }}>
              <span>Se restauró un borrador guardado.</span>
              <button type="button" onClick={descartar} style={{ border: 'none', background: 'transparent', color: COL.pri2, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                Descartar
              </button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Proveedor" icon={FiTruck} COL={COL}>
              <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} style={selectStyle(COL)}>
                <option value="">Sin proveedor</option>
                {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
              </select>
            </Field>
            <Field label="Sede" icon={FiPackage} required COL={COL}>
              <select value={sedeSel} onChange={(e) => setSedeSel(e.target.value)} style={selectStyle(COL)}>
                {sedes.map((s) => <option key={s.codigo} value={s.codigo}>{s.nombre || s.codigo}</option>)}
              </select>
            </Field>
            <Field label="Fecha" icon={FiCalendar} COL={COL}>
              <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={inputStyle(COL)} />
            </Field>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <Field label="Notas" icon={FiClipboard} COL={COL}>
                <input value={notas} onChange={(e) => setNotas(e.target.value)} style={inputStyle(COL)} placeholder="Instrucciones o referencia para el proveedor…" />
              </Field>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>Items ({items.length})</span>
            <Boton COL={COL} ghost onClick={() => setBuscador(true)}><FiPlus size={14} /> Agregar ítem</Boton>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 12, color: COL.muted }}>Total estimado</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: COL.text }}>{money(total)}</span>
            </div>
          </div>

          {items.length > 0 && (
            <div style={{ border: `1px solid ${COL.border}`, borderRadius: 10, overflow: 'hidden', flexShrink: 0 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr) 70px 44px' : 'minmax(0,1fr) 130px 110px 100px 44px', gap: 8, padding: '8px 12px', background: COL.bg2, borderBottom: `1px solid ${COL.border}`, fontWeight: 700, fontSize: 11, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4, position: 'sticky', top: 0, zIndex: 1 }}>
                <span>Producto</span>
                <span>Cantidad</span>
                {!isMobile && <span style={{ textAlign: 'right' }}>Costo</span>}
                {!isMobile && <span style={{ textAlign: 'right' }}>Subtotal</span>}
                <span></span>
              </div>
              {items.map((it, idx) => (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0,1fr) 70px 44px' : 'minmax(0,1fr) 130px 110px 100px 44px', gap: 8, alignItems: 'center', padding: '8px 12px', borderBottom: `1px solid ${COL.border}`, background: COL.surface }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: it.tipo_item === 'producto' ? COL.blueBg : COL.greenBg, color: it.tipo_item === 'producto' ? COL.pri2 : COL.green, whiteSpace: 'nowrap' }}>
                        {it.tipo_item === 'producto' ? 'P' : 'I'}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: COL.text, wordBreak: 'break-word' }}>{it.descripcion}</span>
                    </div>
                    {(it.referencia || it.producto_codigo) && (
                      <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>
                        {it.referencia ? `Ref: ${it.referencia}` : ''}{it.referencia && it.producto_codigo ? ' · ' : ''}{it.producto_codigo || ''}
                      </div>
                    )}
                  </div>
                  <div>
                    <input type="number" inputMode="decimal" min="0" step="0.01" value={it.cantidad} onChange={(e) => setItem(idx, { cantidad: e.target.value })} style={{ ...inputStyle(COL), padding: '6px 8px' }} placeholder={it.unidad || ''} />
                  </div>
                  {!isMobile && (
                    <div>
                      <input type="number" inputMode="decimal" min="0" step="0.01" value={it.costo} onChange={(e) => setItem(idx, { costo: e.target.value })} style={{ ...inputStyle(COL), padding: '6px 8px', textAlign: 'right' }} />
                    </div>
                  )}
                  {!isMobile && (
                    <div style={{ textAlign: 'right', fontWeight: 700, color: COL.text }}>{money(Number(it.cantidad || 0) * Number(it.costo || 0))}</div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconBtn COL={COL} danger onClick={() => quitarItem(idx)}><FiX size={14} /></IconBtn>
                  </div>
                </div>
              ))}
            </div>
          )}

          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 16, flexShrink: 0, background: COL.surface, borderTop: `1px solid ${COL.border}`, flexWrap: 'wrap' }}>
          <Boton COL={COL} ghost onClick={onClose} disabled={guardando}>Cerrar</Boton>
          {ordenEdit ? (
            <Boton COL={COL} onClick={() => guardar()} disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar cambios'}</Boton>
          ) : (
            <>
              <Boton COL={COL} ghost onClick={() => guardar('borrador')} disabled={guardando}><FiEdit2 size={15} /> Guardar borrador</Boton>
              <Boton COL={COL} onClick={() => guardar('enviada')} disabled={guardando}>{guardando ? 'Guardando…' : 'Enviar orden'}</Boton>
            </>
          )}
        </div>
      </div>
    </Overlay>
  );
}

const precioDeItem = (it) => (it.precio_proveedor != null ? it.precio_proveedor : it.costo_estimado != null ? it.costo_estimado : it.costo != null ? it.costo : null);

// Campos legacy del item (ordenes_compra_items) por nombre canonico de columna enum.
const ENUM_A_LEGACY_ITEM = {
  'UNIT': 'unidad',
  'VEHICLE MODEL': 'vehicle_model',
  'ENGINE AND BODY MODELS': 'engine_body_models',
  'ENGINE MODEL / SERIAL': 'engine_model_serial',
  'SERIAL VIN': 'serial_vin',
  'CODIGO FCHP': 'codigo_fchp',
  'OBSERVATION': 'notas',
};

const _normKey = (s) => (s || '').trim().toUpperCase();

// Convierte {nombre_columna: valor} (guiado por las columnas globales) en el payload
// de item: enum conocidas -> campo legacy, proveedor -> codigo_proveedor, idioma y
// el resto -> columnas_extra.
const valoresAPayloadItem = (columnas, valores) => {
  const payload = { columnas_extra: {} };
  (columnas || []).forEach((col) => {
    const v = valores[col.nombre];
    if (v == null || String(v).trim() === '') return;
    const tipo = col.tipo || 'enum';
    const limpio = String(v).trim();
    if (tipo === 'proveedor') {
      payload.codigo_proveedor = limpio;
    } else if (tipo === 'idioma') {
      payload.columnas_extra[col.nombre] = limpio;
    } else {
      const campo = ENUM_A_LEGACY_ITEM[_normKey(col.nombre)];
      if (campo) payload[campo] = limpio;
      else payload.columnas_extra[col.nombre] = limpio;
    }
  });
  payload.columnas_extra = Object.keys(payload.columnas_extra).length ? payload.columnas_extra : null;
  return payload;
};

// Reconstruye {nombre_columna: valor} desde un item (legacy + columnas_extra).
const valoresDesdeItem = (columnas, it) => {
  const mapa = {};
  const extra = it?.columnas_extra || {};
  (columnas || []).forEach((col) => {
    const tipo = col.tipo || 'enum';
    let v;
    if (tipo === 'proveedor') {
      v = it?.codigo_proveedor;
    } else if (tipo === 'idioma') {
      v = extra[col.nombre];
    } else {
      const campo = ENUM_A_LEGACY_ITEM[_normKey(col.nombre)];
      v = campo ? it?.[campo] : extra[col.nombre];
    }
    if (v != null && String(v).trim() !== '') mapa[col.nombre] = String(v).trim();
  });
  return mapa;
};

// Moneda del proveedor + conversion a base. `orden` viene enriquecido por el backend
// con moneda_codigo/moneda_simbolo/moneda_tasa/moneda_base_codigo/moneda_base_simbolo.
const esMonedaExtranjera = (orden) => !!orden?.moneda_codigo;

const PrecioOrden = ({ orden, precio, COL }) => {
  if (precio == null) return <span style={{ color: COL.muted }}>—</span>;
  if (!esMonedaExtranjera(orden)) {
    return <span>{money(precio)}</span>;
  }
  const simbolo = orden.moneda_simbolo || orden.moneda_codigo;
  const baseSimbolo = orden.moneda_base_simbolo || '$';
  const tasa = Number(orden.moneda_tasa || 1);
  const enBase = tasa > 0 ? precio / tasa : null;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
      <span>{simbolo} {money(precio)}</span>
      <span style={{ fontSize: 11, color: COL.muted }}>
        ≈ {baseSimbolo} {enBase != null ? money(enBase) : '—'}
      </span>
    </span>
  );
};


const COLUMNAS_POR_FASE = {
  borrador: ['item', 'cantidad', 'precio'],
  enviada: ['item', 'referencia', 'modelo', 'cantidad'],
  cotizacion: ['item', 'referencia', 'cantidad', 'precio', 'estado'],
  revision: ['item', 'referencia', 'cantidad', 'precio', 'estado'],
  aprobada: ['item', 'referencia', 'cantidad', 'precio'],
  transito: ['item', 'cantidad', 'precio'],
  recepcion: ['item', 'cantidad', 'recibida', 'pendiente', 'precio'],
  revision_costos: ['item', 'cantidad', 'recibida', 'precio'],
  completada: ['item', 'cantidad', 'recibida', 'precio'],
};

const ETIQUETA_COLUMNA = {
  item: 'Item',
  referencia: 'Referencia (OEM)',
  modelo: 'Modelo vehículo',
  codigos: 'Códigos',
  cantidad: 'Solicitada',
  recibida: 'Recibida',
  pendiente: 'Pendiente',
  precio: 'Precio',
  estado: 'Estado',
};

function ModalDetalleOrden({ COL, isMobile, detalle, v, puedeGestionar, sede, sedes, onClose, onRecibido, onRefresh, onEditar }) {
  const { orden, items } = detalle;
  const [recibir, setRecibir] = useState(false);
  const [analizando, setAnalizando] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [msgImport, setMsgImport] = useState('');
  const [cambiandoFase, setCambiandoFase] = useState(false);
  const [itemDetalle, setItemDetalle] = useState(null);
  const [zoomImagen, setZoomImagen] = useState(null);
  const [confirmExport, setConfirmExport] = useState(false);
  const fileRef = React.useRef(null);
  const scrollRef = React.useRef(null);
  const dragRef = React.useRef({ down: false, moved: false, x: 0, y: 0, left: 0, top: 0 });

  const onTableMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { down: true, moved: false, x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
  };
  const onTableMouseMove = (e) => {
    const d = dragRef.current;
    const el = scrollRef.current;
    if (!d.down || !el) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    el.scrollLeft = d.left - dx;
    el.scrollTop = d.top - dy;
  };
  const endTableDrag = () => { dragRef.current.down = false; };
  const onRowClick = (it) => {
    if (dragRef.current.moved) { dragRef.current.moved = false; return; }
    setItemDetalle(it);
  };

  const cambiarFase = async (fase) => {
    setCambiandoFase(true);
    try {
      await API.post(`/compras/ordenes/${orden.id}/fase`, { fase });
      if (onRefresh) onRefresh(orden.id);
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo cambiar la fase');
    } finally {
      setCambiandoFase(false);
    }
  };

  const descargar = async (path, nombre) => {
    try {
      const r = await API.get(path, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = 'No se pudo generar el Excel';
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const texto = await data.text();
          const j = JSON.parse(texto);
          if (j?.detail) msg = j.detail;
        } catch (_) { /* ignorar: el body no es JSON */ }
      } else if (data?.detail) {
        msg = data.detail;
      }
      alert(msg);
    }
  };

  const exportarCotizacion = () => descargar(`/compras/ordenes/${orden.id}/cotizacion`, `${orden.numero_orden} INICIAL COTIZACION.xlsx`);

  const contarEstados = () => {
    const c = { pendiente: 0, aprobado: 0, rechazado: 0, advertencia: 0 };
    (items || []).forEach((it) => {
      const est = it.estado_cotizacion || 'pendiente';
      c[est] = (c[est] || 0) + 1;
    });
    return c;
  };

  const exportarAprobado = () => setConfirmExport(true);

  const descargarAprobado = () => {
    setConfirmExport(false);
    descargar(`/compras/ordenes/${orden.id}/exportar-aprobado`, `${orden.numero_orden} APROBADO.xlsx`);
  };

  const confirmarDescargaAprobado = () => {
    const c = contarEstados();
    if (c.pendiente > 0) return;
    descargarAprobado();
  };

  const onPickArchivo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setAnalizando(true);
    setMsgImport('');
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const r = await API.post(`/compras/ordenes/${orden.id}/analizar-respuesta`, fd);
      setAnalisis(r.data?.data || null);
    } catch (err) {
      setMsgImport(err?.response?.data?.detail || 'No se pudo analizar el archivo');
    } finally {
      setAnalizando(false);
    }
  };

  if (recibir) {
    return (
      <ModalRecibirOrden
        COL={COL} isMobile={isMobile} orden={orden} items={items} v={v}
        onClose={() => setRecibir(false)}
        onRecibido={onRecibido}
      />
    );
  }

  if (analisis) {
    return (
      <ModalAnalisisRespuesta
        COL={COL} isMobile={isMobile} orden={orden} analisis={analisis}
        onClose={() => setAnalisis(null)}
        onAplicado={() => { setAnalisis(null); if (onRefresh) onRefresh(orden.id); }}
      />
    );
  }

  if (itemDetalle) {
    return (
      <ModalDetalleItemOrden
        COL={COL} isMobile={isMobile} item={itemDetalle} orden={orden}
        onClose={() => setItemDetalle(null)}
        onItemUpdated={() => { if (onRefresh) onRefresh(orden.id); }}
      />
    );
  }

  if (confirmExport) {
    const c = contarEstados();
    const hayPendientes = c.pendiente > 0;
    return (
      <Overlay COL={COL} isMobile={isMobile} maxWidth={480} onClose={() => setConfirmExport(false)}>
        <div style={{ ...S.modal, background: COL.surface }}>
          <ModalHeader icon={FiDownload} title="Exportar aprobado" subtitle={orden.numero_orden} onClose={() => setConfirmExport(false)} COL={COL} />
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {hayPendientes && (
              <div style={{ padding: '10px 12px', borderRadius: 8, background: COL.amberBg, color: COL.amber, fontSize: 12.5, lineHeight: 1.5 }}>
                Hay <b>{c.pendiente}</b> producto(s) pendientes por revisar. Debes asignar un estado a todos los productos antes de descargar el archivo.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <MiniStat label="Pendientes" value={<span style={{ color: c.pendiente > 0 ? COL.amber : COL.muted }}>{c.pendiente}</span>} COL={COL} />
              <MiniStat label="Aprobados" value={<span style={{ color: COL.green }}>{c.aprobado}</span>} COL={COL} />
              <MiniStat label="Rechazados" value={<span style={{ color: COL.red }}>{c.rechazado}</span>} COL={COL} />
              <MiniStat label="Advertencias" value={<span style={{ color: COL.amber }}>{c.advertencia}</span>} COL={COL} />
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 16, flexShrink: 0, background: COL.surface, borderTop: `1px solid ${COL.border}` }}>
            <Boton COL={COL} ghost onClick={() => setConfirmExport(false)}>Cancelar</Boton>
            {hayPendientes ? (
              <Boton COL={COL} disabled>Revisa todos los productos</Boton>
            ) : (
              <Boton COL={COL} onClick={confirmarDescargaAprobado}><FiDownload size={15} /> Descargar archivo</Boton>
            )}
          </div>
        </div>
      </Overlay>
    );
  }

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={1180} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiEye} title={orden.numero_orden} subtitle={orden.proveedor || 'Sin proveedor'} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0, overflowY: orden.fase === 'borrador' ? 'auto' : 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <FaseBadge fase={orden.fase} COL={COL} />
            <span style={{ fontSize: 12, color: COL.muted }}>· {orden.fecha_orden ? String(orden.fecha_orden).slice(0, 10) : '—'}</span>
          </div>

          <FaseStepper fase={orden.fase} COL={COL} />

          {puedeGestionar && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {orden.fase === 'borrador' && (
                <Boton COL={COL} onClick={() => cambiarFase('enviada')} disabled={cambiandoFase}><FiCheck size={15} /> Enviar orden</Boton>
              )}
              {orden.fase === 'enviada' && (
                <>
                  <Boton COL={COL} ghost onClick={exportarCotizacion}><FiDownload size={15} /> Descargar cotización inicial</Boton>
                  <Boton COL={COL} onClick={() => cambiarFase('cotizacion')} disabled={cambiandoFase}><FiCheck size={15} /> Recibir cotización</Boton>
                  <Boton COL={COL} ghost onClick={() => cambiarFase('borrador')} disabled={cambiandoFase}><FiArrowLeft size={15} /> Devolver a borrador</Boton>
                </>
              )}
              {orden.fase === 'cotizacion' && (
                <>
                  <Boton COL={COL} onClick={() => fileRef.current && fileRef.current.click()} disabled={analizando}>
                    <FiUpload size={15} /> {analizando ? 'Analizando…' : 'Cargar respuesta del proveedor'}
                  </Boton>
                  <Boton COL={COL} ghost onClick={() => cambiarFase('enviada')} disabled={cambiandoFase}><FiArrowLeft size={15} /> Devolver a enviada</Boton>
                </>
              )}
              {orden.fase === 'revision' && (
                <>
                  <Boton COL={COL} ghost onClick={() => fileRef.current && fileRef.current.click()} disabled={analizando}>
                    <FiUpload size={15} /> {analizando ? 'Analizando…' : 'Cargar revisión del proveedor'}
                  </Boton>
                  <Boton COL={COL} ghost onClick={exportarAprobado}><FiDownload size={15} /> Exportar aprobado</Boton>
                  <Boton COL={COL} onClick={() => cambiarFase('aprobada')} disabled={cambiandoFase}><FiCheck size={15} /> Marcar aprobada</Boton>
                  <Boton COL={COL} ghost onClick={() => cambiarFase('cotizacion')} disabled={cambiandoFase}><FiArrowLeft size={15} /> Devolver a cotización</Boton>
                </>
              )}
              {orden.fase === 'aprobada' && (
                <Boton COL={COL} onClick={() => cambiarFase('transito')} disabled={cambiandoFase}><FiTruck size={15} /> Marcar en tránsito</Boton>
              )}
              {(orden.fase === 'transito' || orden.fase === 'recepcion') && (
                <>
                  <Boton COL={COL} onClick={() => setRecibir(true)}><FiCheck size={15} /> Recibir mercancía</Boton>
                  {orden.fase === 'recepcion' && (
                    <Boton COL={COL} ghost onClick={() => cambiarFase('revision_costos')} disabled={cambiandoFase}><FiCheck size={15} /> Finalizar recepción</Boton>
                  )}
                </>
              )}
            </div>
          )}

          {msgImport && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: COL.blueBg, fontSize: 12.5, color: COL.text }}>{msgImport}</div>
          )}

          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={onPickArchivo} />

          {orden.fase === 'revision' ? (
            <RevisionInline COL={COL} isMobile={isMobile} orden={orden} items={items} onRefresh={onRefresh} />
          ) : orden.fase === 'borrador' ? (
            <BorradorInline COL={COL} isMobile={isMobile} orden={orden} items={items} sede={sede} sedes={sedes} onRefresh={onRefresh} />
          ) : (
          <div ref={scrollRef}
            onMouseDown={onTableMouseDown} onMouseMove={onTableMouseMove} onMouseUp={endTableDrag} onMouseLeave={endTableDrag}
            onDragStart={(e) => e.preventDefault()}
            style={{ flex: 1, minHeight: 0, overflow: 'auto', cursor: 'grab', userSelect: 'none', WebkitUserSelect: 'none', border: `1px solid ${COL.border}`, borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ ...TH, position: 'sticky', top: 0, background: COL.surface, zIndex: 2 }}></th>
                {(COLUMNAS_POR_FASE[orden.fase] || COLUMNAS_POR_FASE.borrador).map((col) => (
                  <th key={col} style={{ ...TH, textAlign: col === 'item' ? 'left' : 'right', position: 'sticky', top: 0, background: COL.surface, zIndex: 2 }}>{ETIQUETA_COLUMNA[col]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const cols = COLUMNAS_POR_FASE[orden.fase] || COLUMNAS_POR_FASE.borrador;
                const cambios = cambiosSignificativosDe(it);
                const resaltado = tieneCambiosSignificativos(it);
                return (
                  <tr
                    key={it.id}
                    onClick={() => onRowClick(it)}
                    style={{ borderBottom: `1px solid ${COL.border}`, borderLeft: `3px solid ${resaltado ? COL.amber : 'transparent'}`, background: it.no_registrado ? COL.amberBg : (resaltado ? COL.amberBg : 'transparent'), cursor: 'pointer' }}
                    title="Ver detalle del producto"
                  >
                    <td style={{ ...TD, padding: '6px 8px', width: 46 }} onClick={(e) => e.stopPropagation()}>
                      {it.imagen_url ? (
                        <button type="button" onClick={(e) => { e.stopPropagation(); setZoomImagen(it.imagen_url); }}
                          style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'zoom-in' }}>
                          <ImagenProducto imagen_url={it.imagen_url} size={36} />
                        </button>
                      ) : (
                        <span style={{ color: COL.faint, fontSize: 10, fontStyle: 'italic' }}>s/img</span>
                      )}
                    </td>
                    {cols.map((col) => {
                      if (col === 'item') {
                        return (
                          <td key={col} style={TD}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 13, fontWeight: 600, color: COL.text }}>
                              <span style={{ wordBreak: 'break-word' }}>{it.descripcion}</span>
                              {it.no_registrado && (
                                <span style={{ ...S.badge, background: COL.amberBg, color: COL.amber }}>No registrado</span>
                              )}
                              {cambios.map((c, i) => (
                                <span key={i} style={{ ...S.badge, background: COL.amberBg, color: COL.amber, fontSize: 10 }}>
                                  {c.tipo === 'eliminado' ? 'Eliminado' : (CAMBIO_LABEL[c.tipo] || c.tipo)}
                                </span>
                              ))}
                            </div>
                            {it.notas && <div style={{ fontSize: 11, color: COL.muted }}>{it.notas}</div>}
                            {it.notas_recepcion && <div style={{ fontSize: 11, color: COL.green }}>Rec: {it.notas_recepcion}</div>}
                          </td>
                        );
                      }
                      if (col === 'referencia') {
                        return <td key={col} style={{ ...TD, textAlign: 'right' }}>{it.referencia || it.producto_codigo || '—'}</td>;
                      }
                      if (col === 'modelo') {
                        return <td key={col} style={{ ...TD, textAlign: 'right' }}>{it.vehicle_model ? `${it.vehicle_model}${it.engine_body_models ? ` · ${it.engine_body_models}` : ''}` : '—'}</td>;
                      }
                      if (col === 'codigos') {
                        return (
                          <td key={col} style={{ ...TD, textAlign: 'right' }}>
                            {it.codigo_fchp ? <div style={{ fontSize: 12 }}>FCHP {it.codigo_fchp}</div> : null}
                            {it.codigo_proveedor ? <div style={{ fontSize: 12, color: COL.muted }}>Prov {it.codigo_proveedor}</div> : null}
                            {!it.codigo_fchp && !it.codigo_proveedor ? '—' : null}
                          </td>
                        );
                      }
                      if (col === 'cantidad') {
                        return <td key={col} style={{ ...TD, textAlign: 'right' }}>{it.cantidad_solicitada != null ? `${num(it.cantidad_solicitada)}${it.unidad ? ` ${it.unidad}` : ''}` : '—'}</td>;
                      }
                      if (col === 'recibida') {
                        return <td key={col} style={{ ...TD, textAlign: 'right' }}>{num(it.cantidad_recibida)}</td>;
                      }
                      if (col === 'pendiente') {
                        return <td key={col} style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{num(Number(it.cantidad_solicitada) - Number(it.cantidad_recibida))}</td>;
                      }
                      if (col === 'precio') {
                        const precio = precioDeItem(it);
                        return <td key={col} style={{ ...TD, textAlign: 'right', fontWeight: 600 }}><PrecioOrden orden={orden} precio={precio} COL={COL} /></td>;
                      }
                      if (col === 'estado') {
                        return <td key={col} style={{ ...TD, textAlign: 'right' }}><EstadoCotizacion estado={it.estado_cotizacion} COL={COL} /></td>;
                      }
                      return null;
                    })}
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
          )}
        </div>
      </div>

      {zoomImagen && (
        <Overlay COL={COL} isMobile={isMobile} onClose={() => setZoomImagen(null)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', cursor: 'zoom-out' }} onClick={() => setZoomImagen(null)}>
            <img src={getImagenUrl(zoomImagen)} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} />
          </div>
        </Overlay>
      )}
    </Overlay>
  );
}

function ModalDetalleItemOrden({ COL, isMobile, item, orden, onClose, onItemUpdated }) {
  const [productSpecs, setProductSpecs] = useState([]);
  const [preciosCompetidores, setPreciosCompetidores] = useState([]);
  const [baseSimb, setBaseSimb] = useState('$');
  const [editando, setEditando] = useState(null); // null | {modo:'producto', seleccionado} | {modo:'item'}

  const cargarProductSpecs = useCallback(() => {
    if (item.tipo_item === 'producto' && item.producto_codigo) {
      API.get(`/compras/producto/${item.producto_codigo}/especificaciones`)
        .then((r) => setProductSpecs(r.data?.data?.especificaciones || []))
        .catch(() => setProductSpecs([]));
    }
  }, [item]);

  useEffect(() => {
    let on = true;
    if (item.tipo_item === 'producto' && item.producto_codigo) {
      API.get(`/compras/producto/${item.producto_codigo}/especificaciones`)
        .then((r) => { if (on) setProductSpecs(r.data?.data?.especificaciones || []); })
        .catch(() => { if (on) setProductSpecs([]); });
      API.get(`/competidores/producto/${item.producto_codigo}/precios`)
        .then((r) => { if (on) setPreciosCompetidores(r.data?.data || []); })
        .catch(() => { if (on) setPreciosCompetidores([]); });
    }
    API.get('/compras/monedas')
      .then((r) => { const b = r.data?.data?.base; if (b?.simbolo) setBaseSimb(b.simbolo); })
      .catch(() => {});
    return () => { on = false; };
  }, [item]);

  if (editando?.modo === 'producto') {
    return (
      <ModalProductoEspecificacionesForm
        COL={COL} isMobile={isMobile}
        seleccionado={editando.seleccionado}
        onClose={() => { setEditando(null); cargarProductSpecs(); }}
      />
    );
  }
  if (editando?.modo === 'item') {
    return (
      <ModalEditarSpecsItem
        COL={COL} isMobile={isMobile} item={item} orden={orden}
        onClose={() => setEditando(null)}
        onGuardado={(payload) => {
          setEditando(null);
          if (onItemUpdated) onItemUpdated(payload);
        }}
      />
    );
  }

  const extras = Object.entries(item.columnas_extra || {})
    .filter(([, v]) => v != null && String(v).trim() !== '');
  const specs = [
    ...(productSpecs || []).filter((s) => (s.etiqueta || '').trim() && String(s.valor ?? '').trim() !== ''),
    ...extras.map(([k, v]) => ({ etiqueta: k, valor: v })),
  ];

  const meta = [
    ['Referencia (OEM)', item.referencia],
    ['Modelo vehículo', item.vehicle_model],
    ['Engine & Body', item.engine_body_models],
    ['Engine Model / Serial', item.engine_model_serial],
    ['Serial VIN', item.serial_vin],
    ['Código FCHP', item.codigo_fchp],
    ['Código proveedor', item.codigo_proveedor],
  ].filter(([, v]) => v != null && String(v).trim() !== '');

  const precio = precioDeItem(item);
  const cantidad = item.cantidad_solicitada != null ? item.cantidad_solicitada : item.cantidad;

  const abrirEditar = () => {
    if (item.producto_codigo) {
      setEditando({
        modo: 'producto',
        seleccionado: {
          codigo: item.producto_codigo,
          descripcion: item.descripcion,
          referencia: item.referencia,
          specs: productSpecs.length ? productSpecs : [{ etiqueta: '', valor: '' }],
        },
      });
    } else {
      setEditando({ modo: 'item' });
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={640} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiBox} title="Detalle del producto" subtitle={item.descripcion} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {item.no_registrado && (
            <div style={{ padding: '10px 12px', borderRadius: 8, background: COL.amberBg, color: COL.amber, fontSize: 13 }}>
              Artículo no registrado en el catálogo
            </div>
          )}

          {item.imagen_url && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <img src={getImagenUrl(item.imagen_url)} alt="" style={{ maxHeight: 200, maxWidth: '100%', borderRadius: 12, objectFit: 'contain', background: '#f1f3f4' }} />
            </div>
          )}

          {meta.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
              {meta.map(([l, val]) => (
                <div key={l} style={{ padding: '10px 12px', borderRadius: 10, background: COL.bg2 }}>
                  <div style={{ fontSize: 11, color: COL.muted }}>{l}</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: COL.text, wordBreak: 'break-word' }}>{val}</div>
                </div>
              ))}
            </div>
          )}

          {item.tipo_item === 'producto' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Especificaciones</div>
                <button type="button" onClick={abrirEditar} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: `1px solid ${COL.border}`, background: 'transparent', color: COL.pri2, borderRadius: 7, padding: '5px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  <FiEdit2 size={13} /> Editar especificaciones
                </button>
              </div>
              {specs.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {specs.map((s, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 10px', borderRadius: 8, background: COL.bg2, fontSize: 12.5 }}>
                      <span style={{ fontWeight: 700, color: COL.muted, whiteSpace: 'nowrap' }}>{s.etiqueta}:</span>
                      <span style={{ color: COL.text, wordBreak: 'break-word' }}>{s.valor}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: '8px 10px', borderRadius: 8, background: COL.bg2, fontSize: 12, color: COL.muted }}>
                  Sin especificaciones registradas.
                </div>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 8 }}>
            <MiniStat label="Solicitada" value={cantidad != null ? `${num(cantidad)}${item.unidad ? ` ${item.unidad}` : ''}` : '—'} COL={COL} />
            <MiniStat label="Recibida" value={num(item.cantidad_recibida ?? 0)} COL={COL} />
            <MiniStat label="Precio" value={<PrecioOrden orden={orden} precio={precio} COL={COL} />} COL={COL} />
          </div>

          {preciosCompetidores.length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Precios de competidores</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {preciosCompetidores.map((c) => (
                  <div key={c.competidor_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: COL.bg2, fontSize: 12.5 }}>
                    <span style={{ color: COL.text, wordBreak: 'break-word' }}>{c.competidor}</span>
                    <span style={{ fontWeight: 700, color: COL.pri2, whiteSpace: 'nowrap' }}>
                      {c.precio != null ? moneySimbolo(c.precio, baseSimb) : '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {item.estado_cotizacion && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: COL.muted }}>Estado</span>
              <EstadoCotizacion estado={item.estado_cotizacion} COL={COL} />
            </div>
          )}

          {(item.notas || item.nota_proveedor || item.notas_recepcion) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {item.notas && <div style={{ fontSize: 12.5, color: COL.text }}><span style={{ fontWeight: 700, color: COL.muted }}>Notas: </span>{item.notas}</div>}
              {item.nota_proveedor && <div style={{ fontSize: 12.5, color: COL.text }}><span style={{ fontWeight: 700, color: COL.muted }}>Nota proveedor: </span>{item.nota_proveedor}</div>}
              {item.notas_recepcion && <div style={{ fontSize: 12.5, color: COL.green }}><span style={{ fontWeight: 700 }}>Recepción: </span>{item.notas_recepcion}</div>}
            </div>
          )}
        </div>
      </div>
    </Overlay>
  );
}

function ModalEditarSpecsItem({ COL, isMobile, item, orden, onClose, onGuardado }) {
  const [valores, setValores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const guardar = async () => {
    setError('');
    const columnas = await API.get('/compras/especificaciones-columnas').then((r) => r.data?.data || []).catch(() => []);
    const payload = valoresAPayloadItem(columnas, valores);
    setGuardando(true);
    try {
      if (item.id != null && orden?.id != null) {
        await API.put(`/compras/ordenes/${orden.id}/items/${item.id}`, payload);
      }
      if (onGuardado) onGuardado(payload);
      else onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar las especificaciones');
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={640} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiEdit2} title="Especificaciones del artículo" subtitle={item.descripcion} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {item.referencia && <div style={{ fontSize: 12, color: COL.muted }}>Ref: {item.referencia}</div>}
          <EditorColumnasGlobales COL={COL} isMobile={isMobile} item={item} onCambio={setValores} />
          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label="Guardar especificaciones" guardando={guardando} />
      </div>
    </Overlay>
  );
}

function ModalProductoManual({ COL, isMobile, onClose, onAdd }) {
  const [nombre, setNombre] = useState('');
  const [referencia, setReferencia] = useState('');
  const [cantidad, setCantidad] = useState('1');
  const [costo, setCosto] = useState('');
  const [valores, setValores] = useState({});
  const [error, setError] = useState('');

  const guardar = async () => {
    if (!nombre.trim()) { setError('Indica el nombre del producto'); return; }
    if (!referencia.trim()) { setError('Indica la referencia (OEM) para identificarlo'); return; }
    const cant = Number(cantidad);
    if (!cant || cant <= 0) { setError('Indica una cantidad válida'); return; }

    const columnas = await API.get('/compras/especificaciones-columnas').then((r) => r.data?.data || []).catch(() => []);
    const payload = valoresAPayloadItem(columnas, valores);
    onAdd({
      id: null,
      tipo_item: 'producto',
      producto_codigo: null,
      insumo_id: null,
      descripcion: nombre.trim(),
      cantidad: cant,
      costo: Number(costo) || 0,
      referencia: referencia.trim(),
      ...payload,
      no_registrado: true,
    });
    onClose();
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={680} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiPlus} title="Producto manual" subtitle="Artículo que aún no está registrado en el inventario" onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
              <Field label="Nombre del producto" icon={FiBox} required COL={COL}>
                <input value={nombre} onChange={(e) => setNombre(e.target.value)} style={inputStyle(COL)} placeholder="Ej. BOMBA FRENO ZNA 4X2" autoFocus />
              </Field>
            </div>
            <Field label="Referencia (OEM)" icon={FiHash} required COL={COL}>
              <input value={referencia} onChange={(e) => setReferencia(e.target.value)} style={inputStyle(COL)} placeholder="Ej. ZN-460103S400+D505" />
            </Field>
            <Field label="Cantidad" icon={FiHash} required COL={COL}>
              <input type="number" inputMode="decimal" min="0" step="0.01" value={cantidad} onChange={(e) => setCantidad(e.target.value)} style={inputStyle(COL)} />
            </Field>
            <Field label="Costo unitario" icon={FiDollarSign} COL={COL}>
              <input type="number" inputMode="decimal" min="0" step="0.01" value={costo} onChange={(e) => setCosto(e.target.value)} style={inputStyle(COL)} placeholder="0.00" />
            </Field>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted, marginBottom: 6 }}>Especificaciones</div>
            <EditorColumnasGlobales COL={COL} isMobile={isMobile} item={null} onCambio={setValores} />
          </div>

          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label="Agregar producto" guardando={false} />
      </div>
    </Overlay>
  );
}

function EstadoCotizacion({ estado, COL }) {
  const cfg = {
    aprobado: { bg: COL.greenBg, color: COL.green, label: 'Aprobado' },
    rechazado: { bg: COL.redBg, color: COL.red, label: 'Rechazado' },
    advertencia: { bg: COL.amberBg, color: COL.amber, label: 'Advertencia' },
    pendiente: { bg: COL.bg2, color: COL.muted, label: 'Pendiente' },
  }[estado] || { bg: COL.bg2, color: COL.muted, label: estado || 'Pendiente' };
  return <span style={{ ...S.badge, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
}

function ModalAnalisisRespuesta({ COL, isMobile, orden, analisis, onClose, onAplicado }) {
  const [campoSel, setCampoSel] = useState({ precio: true, cantidad: true, nota: true, estado: true });
  const [persOff, setPersOff] = useState({});
  // Clasificacion de columnas NUEVAS: 'nota' | 'personalizacion' | 'ignorar'.
  const [colClasif, setColClasif] = useState({});
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState('');

  const mapeo = analisis.mapeo || {};
  const columnasNuevas = analisis.columnas_nuevas || [];
  const personalizaciones = analisis.personalizaciones || [];
  const preview = analisis.preview || [];
  const comparacion = analisis.comparacion || [];

  const clasifDe = (header) => colClasif[header] || 'nota';

  const simb = simboloMonedaOrden(orden);
  const modificados = comparacion.filter((c) => c.es_cambio && !c.no_encontrado);
  const noEncontrados = comparacion.filter((c) => c.no_encontrado);
  const sinCambios = comparacion.filter((c) => !c.es_cambio);

  const cambiosDe = (tipo) => modificados.flatMap((c) =>
    c.cambios.filter((ch) => ch.tipo === tipo).map((ch) => ({ ...ch, item: c })));

  const cambiosPrecio = cambiosDe('precio');
  const cambiosCantidad = cambiosDe('cantidad');
  const cambiosNota = cambiosDe('nota');
  const cambiosEstado = cambiosDe('estado');
  const cambiosPers = cambiosDe('personalizacion');
  const cambiosEliminados = cambiosDe('eliminado');

  const persEtiquetas = [...new Set(cambiosPers.map((c) => c.etiqueta).filter(Boolean))];

  const fmtPrecio = (v) => (v == null ? '—' : moneySimbolo(v, simb));
  const fmtCambio = (c) => {
    if (c.tipo === 'precio') return `${fmtPrecio(c.anterior)} → ${fmtPrecio(c.nuevo)}`;
    if (c.tipo === 'cantidad') return `${num(c.anterior)} → ${num(c.nuevo)}`;
    if (c.tipo === 'personalizacion') {
      const a = c.anterior == null || String(c.anterior).trim() === '' ? '—' : c.anterior;
      return `${a} → ${c.nuevo}`;
    }
    if (c.tipo === 'nueva') return c.header ? `${c.header}: ${c.nuevo}` : c.nuevo;
    if (c.tipo === 'eliminado') return 'No aparece en la respuesta';
    return c.nuevo || '';
  };

  const aplicar = async () => {
    setAplicando(true);
    setError('');
    // Columnas nuevas clasificadas como "nota" -> columnas_extra.
    const columnasAceptadasLista = columnasNuevas
      .filter((c) => clasifDe(c.header) === 'nota')
      .map((c) => c.header);
    // Personalizaciones: las reconocidas + las columnas nuevas clasificadas como tal.
    const persAceptadas = personalizaciones
      .filter((p) => persEtiquetas.includes(p.etiqueta) && !persOff[p.etiqueta])
      .map((p) => ({ header: p.header, etiqueta: p.etiqueta }));
    columnasNuevas
      .filter((c) => clasifDe(c.header) === 'personalizacion')
      .forEach((c) => persAceptadas.push({ header: c.header, etiqueta: c.header }));
    try {
      await API.post(`/compras/ordenes/${orden.id}/aplicar-respuesta`, {
        mapeo,
        aplicar_campos: campoSel,
        columnas_aceptadas: columnasAceptadasLista,
        personalizaciones_aceptadas: persAceptadas,
        items: preview,
      });
      onAplicado();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo aplicar la respuesta');
    } finally {
      setAplicando(false);
    }
  };

  const toggleCampo = (tipo) => setCampoSel((p) => ({ ...p, [tipo]: !p[tipo] }));
  const togglePers = (et) => setPersOff((p) => ({ ...p, [et]: !p[et] }));
  const setClasif = (header, val) => setColClasif((p) => ({ ...p, [header]: val }));

  const muestraDe = (header) => {
    for (const row of preview || []) {
      const v = row[header];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  };

  const toggleHeader = ({ titulo, activo, onToggle, color, count }) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
      <input type="checkbox" checked={activo} onChange={onToggle} style={{ width: 15, height: 15, cursor: 'pointer' }} />
      <span style={{ fontSize: 11.5, fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: 0.4 }}>{titulo}</span>
      <span style={{ fontSize: 11, color: COL.muted }}>({count})</span>
    </label>
  );

  const ListaCambios = ({ cambios }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {cambios.map((c, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 10px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: COL.text, wordBreak: 'break-word' }}>{c.item.descripcion || '—'}</div>
            <div style={{ fontSize: 10.5, color: COL.muted }}>{c.item.referencia}</div>
          </div>
          <div style={{ flexShrink: 0, fontSize: 12, color: COL.text, textAlign: 'right' }}>{fmtCambio(c)}</div>
        </div>
      ))}
    </div>
  );

  const Seccion = ({ titulo, color, activo, onToggle, count, children }) => (
    <div>
      {toggleHeader({ titulo, activo, onToggle, color, count })}
      {activo && <div style={{ marginTop: 6, marginLeft: 4 }}>{children}</div>}
    </div>
  );

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={960} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiUpload} title="Respuesta del proveedor" subtitle={`${orden.numero_orden} · moneda ${simb}`} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 14, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {analisis.error_ia && (
            <div style={{ padding: '8px 12px', borderRadius: 8, background: COL.amberBg, fontSize: 12, color: COL.amber }}>
              IA no disponible ({analisis.error_ia}). Se usó detección heurística.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ ...S.badge, background: COL.amberBg, color: COL.amber, padding: '6px 10px' }}>{modificados.length} modificados</div>
            <div style={{ ...S.badge, background: COL.redBg, color: COL.red, padding: '6px 10px' }}>{noEncontrados.length} sin coincidir</div>
            <div style={{ ...S.badge, background: COL.bg2, color: COL.muted, padding: '6px 10px' }}>{sinCambios.length} sin cambios</div>
          </div>

          {modificados.length === 0 && (
            <div style={{ padding: '14px', borderRadius: 8, background: COL.bg2, color: COL.muted, fontSize: 13, textAlign: 'center' }}>
              No se detectaron cambios en el archivo.
            </div>
          )}

          {cambiosPrecio.length > 0 && (
            <Seccion titulo="Precios" color={COL.green} activo={campoSel.precio} onToggle={() => toggleCampo('precio')} count={cambiosPrecio.length}>
              <ListaCambios cambios={cambiosPrecio} />
            </Seccion>
          )}

          {cambiosCantidad.length > 0 && (
            <Seccion titulo="Cantidades" color={COL.pri2} activo={campoSel.cantidad} onToggle={() => toggleCampo('cantidad')} count={cambiosCantidad.length}>
              <ListaCambios cambios={cambiosCantidad} />
            </Seccion>
          )}

          {cambiosNota.length > 0 && (
            <Seccion titulo="Notas" color={COL.amber} activo={campoSel.nota} onToggle={() => toggleCampo('nota')} count={cambiosNota.length}>
              <ListaCambios cambios={cambiosNota} />
            </Seccion>
          )}

          {cambiosEstado.length > 0 && (
            <Seccion titulo="Estados" color={COL.infoText} activo={campoSel.estado} onToggle={() => toggleCampo('estado')} count={cambiosEstado.length}>
              <ListaCambios cambios={cambiosEstado} />
            </Seccion>
          )}

          {cambiosEliminados.length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.red, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Productos eliminados ({cambiosEliminados.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {cambiosEliminados.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 10px', borderRadius: 8, border: `1px solid ${COL.red}`, borderLeft: `4px solid ${COL.red}`, background: COL.redBg }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: COL.text, wordBreak: 'break-word' }}>{c.item.descripcion || '—'}</div>
                      <div style={{ fontSize: 10.5, color: COL.muted }}>{c.item.referencia}</div>
                    </div>
                    <div style={{ flexShrink: 0, fontSize: 12, color: COL.red, textAlign: 'right' }}>No aparece en la respuesta</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {persEtiquetas.length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.pri2, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Personalizaciones del producto ({persEtiquetas.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {persEtiquetas.map((et) => {
                  const list = cambiosPers.filter((c) => c.etiqueta === et);
                  const activo = !persOff[et];
                  return (
                    <div key={et} style={{ border: `1px solid ${COL.border}`, borderLeft: `4px solid ${COL.pri2}`, borderRadius: 8, padding: '8px 10px', background: COL.priLight }}>
                      {toggleHeader({ titulo: et, activo, onToggle: () => togglePers(et), color: COL.pri2, count: list.length })}
                      {activo && <div style={{ marginTop: 6 }}><ListaCambios cambios={list} /></div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {columnasNuevas.length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Columnas nuevas detectadas ({columnasNuevas.length})</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {columnasNuevas.map((c) => {
                  const clasif = clasifDe(c.header);
                  const muestra = muestraDe(c.header);
                  const esCampoFijo = CAMPOS_FIJOS_HEADERS.some((h) => h === c.header.toUpperCase().trim());
                  const opciones = esCampoFijo
                    ? [{ val: 'ignorar', label: 'Ignorar', color: COL.muted }]
                    : [
                        { val: 'nota', label: 'Nota', color: COL.amber },
                        { val: 'personalizacion', label: 'Personalización', color: COL.pri2 },
                        { val: 'ignorar', label: 'Ignorar', color: COL.muted },
                      ];
                  return (
                    <div key={c.header} style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.inputBg, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 700, color: COL.text, wordBreak: 'break-word' }}>{c.header}</span>
                          <span style={{ fontSize: 11, color: COL.muted, marginLeft: 6 }}>({c.tipo})</span>
                          {c.sugerencia && <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>{c.sugerencia}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          {opciones.map((o) => (
                            <button
                              key={o.val}
                              type="button"
                              onClick={() => setClasif(c.header, o.val)}
                              style={{
                                padding: '5px 10px', borderRadius: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
                                border: clasif === o.val ? `1px solid ${o.color}` : `1px solid ${COL.border}`,
                                background: clasif === o.val ? o.color : 'transparent',
                                color: clasif === o.val ? '#fff' : COL.muted,
                              }}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      {muestra && (
                        <div style={{ fontSize: 11.5, color: COL.text, padding: '6px 8px', borderRadius: 6, background: COL.surface, wordBreak: 'break-word' }}>
                          <span style={{ color: COL.muted, fontWeight: 700 }}>Ejemplo: </span>{muestra}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {noEncontrados.length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.red, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Sin coincidir en la orden ({noEncontrados.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {noEncontrados.map((c, i) => (
                  <div key={i} style={{ border: `1px solid ${COL.border}`, borderRadius: 8, padding: '10px 12px', background: COL.surface, opacity: 0.85 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: COL.text, wordBreak: 'break-word' }}>{c.descripcion || '—'}</div>
                    <div style={{ fontSize: 11, color: COL.muted }}>{c.referencia} — no se aplicará (no coincide con la orden)</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {Object.keys(mapeo).filter((k) => mapeo[k]).length > 0 && (
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 800, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>Columnas mapeadas</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {Object.entries(mapeo).filter(([, v]) => v).map(([campo, header]) => (
                  <span key={campo} style={{ ...S.badge, background: COL.blueBg, color: COL.pri2 }}>{campo} → {header}</span>
                ))}
              </div>
            </div>
          )}

          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={aplicar} COL={COL} label="Aplicar selección" guardando={aplicando} />
      </div>
    </Overlay>
  );
}

function BorradorInline({ COL, isMobile, orden, items, sede, sedes, onRefresh }) {
  const [proveedores, setProveedores] = useState([]);
  const [proveedorId, setProveedorId] = useState(orden.proveedor_id ?? '');
  const [sedeSel, setSedeSel] = useState(orden.sede_codigo ?? sede ?? '');
  const [fecha, setFecha] = useState(orden.fecha_orden ? String(orden.fecha_orden).slice(0, 10) : '');
  const [notas, setNotas] = useState(orden.notas ?? '');
  const [listaItems, setListaItems] = useState(() => (items || []).map((it) => ({
    id: it.id,
    tipo_item: it.tipo_item,
    producto_codigo: it.producto_codigo,
    insumo_id: it.insumo_id,
    descripcion: it.descripcion,
    cantidad: it.cantidad_solicitada,
    costo: it.precio_proveedor ?? it.costo_estimado,
    referencia: it.referencia,
    unidad: it.unidad,
    codigo_fchp: it.codigo_fchp,
    codigo_proveedor: it.codigo_proveedor,
    vehicle_model: it.vehicle_model,
    engine_body_models: it.engine_body_models,
    engine_model_serial: it.engine_model_serial,
    serial_vin: it.serial_vin,
    columnas_extra: it.columnas_extra,
    no_registrado: it.no_registrado,
    imagen_url: it.imagen_url,
    notas: it.notas,
  })));
  const [buscador, setBuscador] = useState(false);
  const [modalManual, setModalManual] = useState(false);
  const [error, setError] = useState('');
  const [itemDetalle, setItemDetalle] = useState(null);
  const [zoomImagen, setZoomImagen] = useState(null);

  useEffect(() => {
    API.get('/compras/proveedores', { params: { limite: 200 } })
      .then((r) => setProveedores(r.data?.data?.items || []))
      .catch(() => {});
  }, []);

  const buildItems = () => listaItems.map((it) => ({
    tipo_item: it.tipo_item,
    producto_codigo: it.tipo_item === 'producto' ? it.producto_codigo : null,
    insumo_id: it.tipo_item === 'insumo' ? (it.insumo_id || null) : null,
    descripcion: it.descripcion,
    cantidad: Number(it.cantidad),
    costo_estimado: Number(it.costo) || null,
    precio_proveedor: Number(it.costo) || null,
    referencia: it.referencia || null,
    unidad: it.unidad || null,
    codigo_fchp: it.codigo_fchp || null,
    codigo_proveedor: it.codigo_proveedor || null,
    vehicle_model: it.vehicle_model || null,
    engine_body_models: it.engine_body_models || null,
    engine_model_serial: it.engine_model_serial || null,
    serial_vin: it.serial_vin || null,
    columnas_extra: it.columnas_extra || null,
    no_registrado: !!it.no_registrado,
    notas: it.notas || null,
  }));

  const quitarItem = (idx) => setListaItems((prev) => prev.filter((_, i) => i !== idx));
  const setItem = (idx, patch) => setListaItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  // Auto-guardado con debounce (solo cuando hay cambios reales), sin recargar para no perder foco.
  const savedRef = React.useRef('');
  const initRef = React.useRef(false);
  const guardar = useCallback(async () => {
    const body = {
      proveedor_id: proveedorId ? Number(proveedorId) : null,
      sede_codigo: sedeSel,
      fecha_orden: fecha || null,
      notas: notas || null,
      items: buildItems(),
    };
    const sig = JSON.stringify(body);
    if (sig === savedRef.current) return;
    try {
      await API.put(`/compras/ordenes/${orden.id}`, body);
      savedRef.current = sig;
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar la orden');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orden.id, proveedorId, sedeSel, fecha, notas, listaItems]);

  useEffect(() => {
    if (!initRef.current) {
      initRef.current = true;
      savedRef.current = JSON.stringify({
        proveedor_id: proveedorId ? Number(proveedorId) : null,
        sede_codigo: sedeSel,
        fecha_orden: fecha || null,
        notas: notas || null,
        items: buildItems(),
      });
      return;
    }
    const t = setTimeout(guardar, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveedorId, sedeSel, fecha, notas, listaItems]);

  if (buscador) {
    return (
      <SelectorItem
        COL={COL} isMobile={isMobile}
        onClose={() => setBuscador(false)}
        items={listaItems}
        onPick={(it) => { setListaItems((prev) => sumarItem(prev, { ...it, costo: Number(it.costo || 0) })); }}
      />
    );
  }

  if (modalManual) {
    return (
      <ModalProductoManual
        COL={COL} isMobile={isMobile}
        onClose={() => setModalManual(false)}
        onAdd={(it) => setListaItems((prev) => [...prev, it])}
      />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Proveedor" icon={FiTruck} COL={COL}>
          <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} style={selectStyle(COL)}>
            <option value="">Sin proveedor</option>
            {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
          </select>
        </Field>
        <Field label="Sede" icon={FiPackage} required COL={COL}>
          <select value={sedeSel} onChange={(e) => setSedeSel(e.target.value)} style={selectStyle(COL)}>
            {(sedes || []).map((s) => <option key={s.codigo} value={s.codigo}>{s.nombre || s.codigo}</option>)}
          </select>
        </Field>
        <Field label="Fecha" icon={FiCalendar} COL={COL}>
          <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={inputStyle(COL)} />
        </Field>
        <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
          <Field label="Notas" icon={FiClipboard} COL={COL}>
            <input value={notas} onChange={(e) => setNotas(e.target.value)} style={inputStyle(COL)} placeholder="Instrucciones o referencia para el proveedor…" />
          </Field>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>Items ({listaItems.length})</span>
        <Boton COL={COL} ghost onClick={() => setBuscador(true)}><FiPlus size={14} /> Agregar ítem</Boton>
        <Boton COL={COL} ghost onClick={() => setModalManual(true)}><FiPlus size={14} /> Agregar producto manual</Boton>
      </div>

      <div style={{ border: `1px solid ${COL.border}`, borderRadius: 10 }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '40px minmax(0,1fr) 58px 74px 32px' : '40px minmax(0,1fr) 80px 90px 90px 32px', gap: 8, padding: '8px 12px', background: COL.bg2, borderBottom: `1px solid ${COL.border}`, borderTopLeftRadius: 10, borderTopRightRadius: 10, fontWeight: 700, fontSize: 11, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
          <span></span>
          <span>Producto</span>
          <span>Cantidad</span>
          {!isMobile && <span style={{ textAlign: 'right' }}>Costo</span>}
          <span style={{ textAlign: 'right' }}>Monto</span>
          <span></span>
        </div>
        {listaItems.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: COL.muted, fontSize: 13 }}>
            Sin items. Agrega productos o insumos.
          </div>
        ) : (
          listaItems.map((it, idx) => (
            <div key={idx} onClick={() => setItemDetalle({ it, idx })} style={{ display: 'grid', gridTemplateColumns: isMobile ? '40px minmax(0,1fr) 58px 74px 32px' : '40px minmax(0,1fr) 80px 90px 90px 32px', gap: 8, alignItems: 'center', padding: '8px 12px', borderBottom: `1px solid ${COL.border}`, background: COL.surface, cursor: 'pointer' }}>
              <div onClick={(e) => e.stopPropagation()}>
                {it.imagen_url ? (
                  <button type="button" onClick={(e) => { e.stopPropagation(); setZoomImagen(it.imagen_url); }} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'zoom-in' }}>
                    <ImagenProducto imagen_url={it.imagen_url} size={36} />
                  </button>
                ) : (
                  <span style={{ color: COL.faint, fontSize: 10, fontStyle: 'italic' }}>s/img</span>
                )}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: it.tipo_item === 'producto' ? COL.blueBg : COL.greenBg, color: it.tipo_item === 'producto' ? COL.pri2 : COL.green, whiteSpace: 'nowrap' }}>
                    {it.tipo_item === 'producto' ? 'P' : 'I'}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: COL.text, wordBreak: 'break-word' }}>{it.descripcion}</span>
                  {it.no_registrado && (
                    <span style={{ ...S.badge, background: COL.amberBg, color: COL.amber }}>No registrado</span>
                  )}
                </div>
                {(it.referencia || it.producto_codigo) && (
                  <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>
                    {it.referencia ? `Ref: ${it.referencia}` : ''}{it.referencia && it.producto_codigo ? ' · ' : ''}{it.producto_codigo || ''}
                  </div>
                )}
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <input type="number" inputMode="decimal" min="0" step="0.01" value={it.cantidad} onChange={(e) => setItem(idx, { cantidad: e.target.value })} style={{ ...inputStyle(COL), padding: '6px 8px' }} placeholder={it.unidad || ''} />
              </div>
              {!isMobile && (
                <div onClick={(e) => e.stopPropagation()}>
                  <input type="number" inputMode="decimal" min="0" step="0.01" value={it.costo ?? ''} onChange={(e) => setItem(idx, { costo: e.target.value })} style={{ ...inputStyle(COL), padding: '6px 8px', textAlign: 'right' }} />
                </div>
              )}
              <div style={{ textAlign: 'right', fontSize: 12.5, fontWeight: 700, color: COL.text, whiteSpace: 'nowrap' }}>
                {money(Number(it.cantidad || 0) * Number(it.costo || 0))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                <IconBtn COL={COL} danger onClick={() => quitarItem(idx)}><FiX size={14} /></IconBtn>
              </div>
            </div>
          ))
        )}
        {listaItems.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 12px', background: COL.bg2, borderTop: `1px solid ${COL.border}`, borderBottomLeftRadius: 10, borderBottomRightRadius: 10, position: 'sticky', bottom: 0, zIndex: 5, fontSize: 12.5, color: COL.text }}>
            <span style={{ fontWeight: 600, color: COL.muted }}>{listaItems.length} ítem{listaItems.length !== 1 ? 's' : ''}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <span>Cantidad total: <span style={{ fontWeight: 800, color: COL.pri2 }}>{num(listaItems.reduce((s, it) => s + Number(it.cantidad || 0), 0))}</span></span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Monto esperado: <span style={{ fontWeight: 800, color: COL.pri2 }}><PrecioOrden orden={orden} precio={listaItems.reduce((s, it) => s + Number(it.cantidad || 0) * Number(it.costo || 0), 0)} COL={COL} /></span></span>
            </span>
          </div>
        )}
      </div>

      {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}

      {itemDetalle && (
        <ModalDetalleItemOrden
          COL={COL} isMobile={isMobile} item={itemDetalle.it} orden={orden}
          onClose={() => setItemDetalle(null)}
          onItemUpdated={(payload) => {
            const idx = itemDetalle.idx;
            setItemDetalle(null);
            setListaItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...payload } : it)));
          }}
        />
      )}

      {zoomImagen && (
        <Overlay COL={COL} isMobile={isMobile} onClose={() => setZoomImagen(null)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', cursor: 'zoom-out' }} onClick={() => setZoomImagen(null)}>
            <img src={getImagenUrl(zoomImagen)} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} />
          </div>
        </Overlay>
      )}
    </div>
  );
}

function RevisionInline({ COL, isMobile, orden, items, onRefresh }) {
  const [estados, setEstados] = useState(() => {
    const map = {};
    items.forEach((it) => { map[it.id] = it.estado_cotizacion || 'pendiente'; });
    return map;
  });
  const [notas, setNotas] = useState(() => {
    const map = {};
    items.forEach((it) => { map[it.id] = it.nota_revision || ''; });
    return map;
  });
  const [cantidades, setCantidades] = useState(() => {
    const map = {};
    items.forEach((it) => { map[it.id] = String(it.cantidad_solicitada ?? ''); });
    return map;
  });
  const [preciosVenta, setPreciosVenta] = useState(() => {
    const map = {};
    items.forEach((it) => { map[it.id] = it.precio_venta != null ? String(it.precio_venta) : ''; });
    return map;
  });
  const [error, setError] = useState('');
  const [pctGasto, setPctGasto] = useState('10');
  const [q, setQ] = useState('');
  const [filtroEstado, setFiltroEstado] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [zoomImagen, setZoomImagen] = useState(null);
  const [detalleItem, setDetalleItem] = useState(null);

  const simb = simboloMonedaOrden(orden);
  const baseSimb = orden.moneda_base_simbolo || '$';
  const tasa = Number(orden.moneda_tasa || 1);
  const esExtranjera = !!orden.moneda_codigo;
  const pct = Number(pctGasto || 0);

  const costoBaseDe = (it) => {
    const precio = Number(it.precio_proveedor || 0);
    return esExtranjera && tasa > 0 ? precio / tasa : precio;
  };
  const costoFinalDe = (it) => costoBaseDe(it) * (1 + pct / 100);

  const toggleEstado = (id, est) => setEstados((p) => ({ ...p, [id]: p[id] === est ? 'pendiente' : est }));

  // ── Auto-guardado: solo persiste cuando hay un cambio real ──
  const buildItems = () => items.map((it) => ({
    item_id: it.id,
    estado: estados[it.id],
    nota: (notas[it.id] || '').trim() || null,
    cantidad: Number(cantidades[it.id]) || null,
  }));
  const savedRef = React.useRef('');
  const inicializado = React.useRef(false);

  const guardar = useCallback(async () => {
    const itemsBody = buildItems();
    const sig = JSON.stringify({ items: itemsBody });
    if (sig === savedRef.current) return;
    try {
      await API.post(`/compras/ordenes/${orden.id}/revisar-items`, { items: itemsBody });
      savedRef.current = sig;
      setError('');
      if (onRefresh) onRefresh(orden.id);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar la revisión');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orden.id, items, estados, notas, cantidades, onRefresh]);

  useEffect(() => {
    if (!inicializado.current) {
      inicializado.current = true;
      savedRef.current = JSON.stringify({ items: buildItems() });
      return;
    }
    const t = setTimeout(guardar, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estados, notas, cantidades]);

  const extrasDe = (it) => Object.entries(it.columnas_extra || {})
    .filter(([, v]) => v != null && String(v).trim() !== '');

  // ── Filtrado + búsqueda ──
  const ql = (q || '').trim().toLowerCase();
  const filtrados = items.filter((it) => {
    if (filtroEstado && estados[it.id] !== filtroEstado) return false;
    if (!ql) return true;
    const campos = [it.descripcion, it.referencia, it.producto_codigo, it.codigo_fchp, it.codigo_proveedor,
      it.vehicle_model, it.engine_body_models, it.engine_model_serial, it.serial_vin];
    if (campos.some((v) => (v || '').toLowerCase().includes(ql))) return true;
    return Object.entries(it.columnas_extra || {}).some(([k, v]) => `${k} ${v}`.toLowerCase().includes(ql));
  });

  // ── Ordenamiento ──
  const valorDe = (it, key) => {
    switch (key) {
      case 'descripcion': return (it.descripcion || '').toLowerCase();
      case 'cantidad': return Number(cantidades[it.id] || 0);
      case 'precio': return Number(it.precio_proveedor || 0);
      case 'costo': return costoFinalDe(it);
      case 'precioVenta': return Number(preciosVenta[it.id] || 0);
      case 'margen': {
        const c = costoFinalDe(it);
        return c > 0 ? (Number(preciosVenta[it.id] || 0) - c) / c : -Infinity;
      }
      default: return 0;
    }
  };
  const lista = sortKey
    ? [...filtrados].sort((a, b) => {
        const va = valorDe(a, sortKey);
        const vb = valorDe(b, sortKey);
        if (va < vb) return -1 * sortDir;
        if (va > vb) return 1 * sortDir;
        return 0;
      })
    : filtrados;

  const ordenar = (key) => {
    if (sortKey === key) setSortDir((d) => d * -1);
    else { setSortKey(key); setSortDir(1); }
  };

  const nPend = items.filter((it) => estados[it.id] === 'pendiente').length;
  const nAprob = items.filter((it) => estados[it.id] === 'aprobado').length;
  const nAdv = items.filter((it) => estados[it.id] === 'advertencia').length;
  const nRech = items.filter((it) => estados[it.id] === 'rechazado').length;

  const totCosto = items.reduce((s, it) => s + (it.precio_proveedor != null ? costoFinalDe(it) * Number(cantidades[it.id] || 0) : 0), 0);
  const totVenta = items.reduce((s, it) => s + (Number(preciosVenta[it.id] || 0) * Number(cantidades[it.id] || 0)), 0);
  const totMargen = totVenta - totCosto;
  const totMargenPct = totCosto > 0 ? (totMargen / totCosto) * 100 : null;

  const estadoBorder = { aprobado: COL.green, advertencia: COL.amber, rechazado: COL.red, pendiente: 'transparent' };

  // ── Arrastrar para hacer scroll (tipo tablet) ──
  const scrollRef = React.useRef(null);
  const dragRef = React.useRef({ down: false, moved: false, x: 0, y: 0, left: 0, top: 0 });
  const onMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { down: true, moved: false, x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop };
  };
  const onMouseMove = (e) => {
    const d = dragRef.current;
    const el = scrollRef.current;
    if (!d.down || !el) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
    el.scrollLeft = d.left - dx;
    el.scrollTop = d.top - dy;
  };
  const endDrag = () => { dragRef.current.down = false; };
  const onRowClick = (it) => {
    if (dragRef.current.moved) { dragRef.current.moved = false; return; }
    setDetalleItem(it);
  };

  const estadoIcono = (id, est, Icon, color, title) => {
    const activo = estados[id] === est;
    return (
      <button type="button" title={title}
        onClick={(e) => { e.stopPropagation(); toggleEstado(id, est); }}
        style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${activo ? color : COL.border}`, background: activo ? color : 'transparent', color: activo ? '#fff' : COL.muted, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={13} />
      </button>
    );
  };

  const filtroChip = (id, label, count, bg, color) => {
    const activo = filtroEstado === id;
    return (
      <button type="button" onClick={() => setFiltroEstado(activo ? null : id)}
        style={{ padding: '4px 10px', borderRadius: 999, border: activo ? 'none' : `1px solid ${COL.border}`, background: activo ? color : 'transparent', color: activo ? '#fff' : COL.muted, cursor: 'pointer', fontSize: 11.5, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {label} · {count}
      </button>
    );
  };

  const ThSort = ({ label, right, sortableKey }) => {
    const activo = sortKey === sortableKey;
    return (
      <th style={{ ...TH, textAlign: right ? 'right' : 'left', cursor: 'pointer', userSelect: 'none', position: 'sticky', top: 0, background: COL.surface, zIndex: 2 }}
        onClick={() => ordenar(sortableKey)} title="Ordenar">
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
          {label}
          {activo ? (sortDir === 1 ? <FiArrowUp size={11} /> : <FiArrowDown size={11} />) : null}
        </span>
      </th>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0 }}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 200 }}>
          <FiSearch size={14} style={{ color: COL.muted }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, referencia, código o personalización…" style={{ ...inputStyle(COL), maxWidth: 420 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>Gasto global</span>
          <input type="number" inputMode="decimal" min="0" step="0.01" value={pctGasto} onChange={(e) => setPctGasto(e.target.value)} style={{ ...inputStyle(COL), width: 70, textAlign: 'right' }} />
          <span style={{ fontSize: 12, color: COL.muted }}>%</span>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {filtroChip('pendiente', 'Pendientes', nPend, COL.bg2, COL.muted)}
        {filtroChip('aprobado', 'Aprobados', nAprob, COL.greenBg, COL.green)}
        {filtroChip('advertencia', 'Advertencias', nAdv, COL.amberBg, COL.amber)}
        {filtroChip('rechazado', 'Rechazados', nRech, COL.redBg, COL.red)}
        <span style={{ fontSize: 11, color: COL.muted, marginLeft: 'auto' }}>
          {lista.length} de {items.length} · Simulación: costo en {simb} → {baseSimb} (+{pct}%)
        </span>
      </div>

      <div ref={scrollRef}
        onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={endDrag} onMouseLeave={endDrag}
        onDragStart={(e) => e.preventDefault()}
        style={{ flex: 1, minHeight: 0, overflow: 'auto', cursor: 'grab', userSelect: 'none', WebkitUserSelect: 'none', border: `1px solid ${COL.border}`, borderRadius: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13, minWidth: 1100 }}>
          <thead>
            <tr>
              <th style={{ ...TH, position: 'sticky', top: 0, background: COL.surface, zIndex: 2 }}></th>
              <ThSort label="Producto" sortableKey="descripcion" />
              <ThSort label="Cantidad" right sortableKey="cantidad" />
              <ThSort label="Precio" right sortableKey="precio" />
              <ThSort label={`Costo ${baseSimb}`} right sortableKey="costo" />
              <ThSort label={`P. venta ${baseSimb}`} right sortableKey="precioVenta" />
              <ThSort label="Margen %" right sortableKey="margen" />
              <th style={{ ...TH, textAlign: 'right', position: 'sticky', top: 0, background: COL.surface, zIndex: 2 }}>Margen {baseSimb}</th>
              <th style={{ ...TH, position: 'sticky', top: 0, background: COL.surface, zIndex: 2 }}>Nota de revisión</th>
              <th style={{ ...TH, position: 'sticky', top: 0, background: COL.surface, zIndex: 2 }}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((it) => {
              const costoBase = costoBaseDe(it);
              const costoFinal = costoFinalDe(it);
              const precioVenta = Number(preciosVenta[it.id] || 0);
              const margenBase = precioVenta - costoFinal;
              const margenPct = costoFinal > 0 ? (margenBase / costoFinal) * 100 : null;
              const extras = extrasDe(it);
              const cambios = cambiosSignificativosDe(it);
              const eliminado = !!it.eliminado_en_respuesta;
              const resaltado = cambios.length > 0 || eliminado;
              return (
                <tr key={it.id} onClick={() => onRowClick(it)}
                  style={{ borderBottom: `1px solid ${COL.border}`, borderLeft: `3px solid ${eliminado ? COL.red : (cambios.length > 0 ? COL.amber : (estadoBorder[estados[it.id]] || 'transparent'))}`, background: eliminado ? COL.redBg : (estados[it.id] === 'rechazado' ? COL.redBg : (cambios.length > 0 ? COL.amberBg : 'transparent')), cursor: 'pointer' }}>
                  <td style={{ ...TD, padding: '6px 8px', width: 46 }} onClick={(e) => e.stopPropagation()}>
                    {it.imagen_url ? (
                      <button type="button" onClick={(e) => { e.stopPropagation(); setZoomImagen(it.imagen_url); }}
                        style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'zoom-in' }}>
                        <ImagenProducto imagen_url={it.imagen_url} size={36} />
                      </button>
                    ) : (
                      <span style={{ color: COL.faint, fontSize: 10, fontStyle: 'italic' }}>s/img</span>
                    )}
                  </td>
                  <td style={TD}>
                    <div style={{ fontWeight: 600, color: COL.text, wordBreak: 'break-word' }}>{it.descripcion}</div>
                    <div style={{ fontSize: 11, color: COL.muted }}>{it.referencia || it.producto_codigo || '—'}{it.unidad ? ` · ${it.unidad}` : ''}</div>
                    {resaltado && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {cambios.map((c, i) => (
                          <span key={i} style={{ ...S.badge, background: c.tipo === 'eliminado' ? COL.redBg : COL.amberBg, color: c.tipo === 'eliminado' ? COL.red : COL.amber, fontSize: 10.5 }}>
                            {c.tipo === 'eliminado' ? 'Eliminado' : (CAMBIO_LABEL[c.tipo] || c.tipo)}{c.tipo === 'personalizacion' && c.etiqueta ? `: ${c.etiqueta}` : ''}
                          </span>
                        ))}
                      </div>
                    )}
                    {(it.nota_proveedor || extras.length > 0) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                        {it.nota_proveedor && (
                          <span style={{ ...S.badge, background: COL.amberBg, color: COL.amber, fontSize: 10.5 }}>
                            Proveedor: {it.nota_proveedor}
                          </span>
                        )}
                        {extras.map(([k, v]) => (
                          <span key={k} style={{ ...S.badge, background: COL.amberBg, color: COL.amber, fontSize: 10.5 }}>
                            {k}: {v}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <input type="number" inputMode="decimal" min="0" step="1" value={cantidades[it.id]} onChange={(e) => setCantidades((p) => ({ ...p, [it.id]: e.target.value }))} style={{ ...inputStyle(COL), width: 76, padding: '5px 8px', textAlign: 'right' }} />
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }}><PrecioOrden orden={orden} precio={precioDeItem(it)} COL={COL} /></td>
                  <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {it.precio_proveedor != null ? moneySimbolo(costoFinal, baseSimb) : '—'}
                    {pct > 0 && <div style={{ fontSize: 10, color: COL.muted }}>({moneySimbolo(costoBase, baseSimb)})</div>}
                  </td>
                  <td style={{ ...TD, textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                    <input type="number" inputMode="decimal" min="0" step="0.01" value={preciosVenta[it.id]} onChange={(e) => setPreciosVenta((p) => ({ ...p, [it.id]: e.target.value }))} style={{ ...inputStyle(COL), width: 86, padding: '5px 8px', textAlign: 'right' }} />
                  </td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: margenBase >= 0 ? COL.green : COL.red }}>
                    {margenPct == null ? '—' : `${margenPct.toFixed(1)}%`}
                  </td>
                  <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: margenBase >= 0 ? COL.green : COL.red }}>
                    {it.precio_proveedor != null ? moneySimbolo(margenBase, baseSimb) : '—'}
                  </td>
                  <td style={{ ...TD, minWidth: 220 }} onClick={(e) => e.stopPropagation()}>
                    <textarea
                      value={notas[it.id] || ''}
                      onChange={(e) => setNotas((p) => ({ ...p, [it.id]: e.target.value }))}
                      placeholder="Nota de revisión…"
                      rows={2}
                      style={{ ...inputStyle(COL), resize: 'vertical', minHeight: 40, fontFamily: 'inherit', lineHeight: 1.4 }}
                    />
                  </td>
                  <td style={{ ...TD, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {estadoIcono(it.id, 'aprobado', FiCheck, COL.green, 'Aprobar')}
                      {estadoIcono(it.id, 'advertencia', FiAlertTriangle, COL.amber, 'Advertencia')}
                      {estadoIcono(it.id, 'rechazado', FiXCircle, COL.red, 'Rechazar')}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: `2px solid ${COL.border}`, background: COL.bg2 }}>
              <td style={{ ...TD, fontWeight: 800, color: COL.text, position: 'sticky', bottom: 0, background: COL.bg2, zIndex: 2 }} colSpan={4}>Totales</td>
              <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: COL.text, position: 'sticky', bottom: 0, background: COL.bg2, zIndex: 2 }}>{moneySimbolo(totCosto, baseSimb)}</td>
              <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: COL.text, position: 'sticky', bottom: 0, background: COL.bg2, zIndex: 2 }}>{moneySimbolo(totVenta, baseSimb)}</td>
              <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: totMargen >= 0 ? COL.green : COL.red, position: 'sticky', bottom: 0, background: COL.bg2, zIndex: 2 }}>
                {totMargenPct == null ? '—' : `${totMargenPct.toFixed(1)}%`}
              </td>
              <td style={{ ...TD, textAlign: 'right', fontWeight: 800, color: totMargen >= 0 ? COL.green : COL.red, position: 'sticky', bottom: 0, background: COL.bg2, zIndex: 2 }}>
                {moneySimbolo(totMargen, baseSimb)}
              </td>
              <td style={{ ...TD, position: 'sticky', bottom: 0, background: COL.bg2, zIndex: 2 }} colSpan={2}></td>
            </tr>
          </tfoot>
        </table>
      </div>

      {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}

      {detalleItem && (
        <ModalDetalleItemOrden COL={COL} isMobile={isMobile} item={detalleItem} orden={orden} onClose={() => setDetalleItem(null)} onItemUpdated={() => { if (onRefresh) onRefresh(orden.id); }} />
      )}

      {zoomImagen && (
        <Overlay COL={COL} isMobile={isMobile} onClose={() => setZoomImagen(null)}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', cursor: 'zoom-out' }} onClick={() => setZoomImagen(null)}>
            <img src={getImagenUrl(zoomImagen)} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} />
          </div>
        </Overlay>
      )}
    </div>
  );
}

function ModalRecibirOrden({ COL, isMobile, orden, items, v, onClose, onRecibido }) {
  const [form, setForm] = useState(() => {
    const map = {};
    items.forEach((it) => {
      const pend = Number(it.cantidad_solicitada) - Number(it.cantidad_recibida);
      map[it.id] = pend > 0 ? String(pend) : '';
    });
    return map;
  });
  const [notas, setNotas] = useState({});
  const [costos, setCostos] = useState(() => {
    const map = {};
    items.forEach((it) => { map[it.id] = String(it.precio_proveedor ?? it.costo_estimado ?? 0); });
    return map;
  });
  const [precios, setPrecios] = useState({});
  const [tipoPago, setTipoPago] = useState('contado');
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [numeroFactura, setNumeroFactura] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [zoomImagen, setZoomImagen] = useState(null);

  const recibirItems = items
    .filter((it) => Number(form[it.id]) > 0)
    .map((it) => ({
      orden_item_id: it.id,
      cantidad: Number(form[it.id]),
      costo: Number(costos[it.id] || 0),
      precio: it.tipo_item === 'producto' ? (precios[it.id] != null && precios[it.id] !== '' ? Number(precios[it.id]) : null) : null,
      nota: (notas[it.id] || '').trim() || null,
    }));

  const diffDe = (it) => {
    const ya = Number(it.cantidad_recibida);
    const esta = Number(form[it.id] || 0);
    return (ya + esta) - Number(it.cantidad_solicitada);
  };

  const badgeDe = (it) => {
    const d = diffDe(it);
    if (d > 0.001) return { label: `De más +${num(d)}`, bg: COL.greenBg, color: COL.green };
    if (d < -0.001) return { label: `De menos ${num(Math.abs(d))}`, bg: COL.amberBg, color: COL.amber };
    return { label: 'Completo', bg: COL.blueBg, color: COL.pri2 };
  };

  const guardar = async () => {
    setError('');
    if (!recibirItems.length) { setError('Indica la cantidad a recibir de al menos un item'); return; }
    if (tipoPago === 'credito' && !fechaVencimiento) { setError('Indica la fecha de vencimiento'); return; }
    setGuardando(true);
    try {
      await API.post(`/compras/ordenes/${orden.id}/recibir`, {
        numero_factura: numeroFactura || null,
        tipo_pago: tipoPago,
        fecha_vencimiento: tipoPago === 'credito' ? (fechaVencimiento || null) : null,
        items: recibirItems,
      });
      onRecibido();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo recibir la orden');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={1000} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiCheck} title="Recibir mercancía" subtitle={`Checklist de ${orden.numero_orden}`} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: 12 }}>
            <Field label="Nº factura" icon={FiFileText} COL={COL}>
              <input value={numeroFactura} onChange={(e) => setNumeroFactura(e.target.value)} style={inputStyle(COL)} />
            </Field>
            <Field label="Condición de pago" icon={FiCreditCard} COL={COL}>
              <select value={tipoPago} onChange={(e) => setTipoPago(e.target.value)} style={selectStyle(COL)}>
                <option value="contado">Contado</option>
                <option value="credito">Crédito</option>
              </select>
            </Field>
            {tipoPago === 'credito' && (
              <Field label="Fecha de vencimiento" icon={FiCalendar} required COL={COL} hint="Fecha límite para pagar la factura (por abonos)">
                <input type="date" value={fechaVencimiento} onChange={(e) => setFechaVencimiento(e.target.value)} style={inputStyle(COL)} />
              </Field>
            )}
          </div>

          <div style={{ fontSize: 11, fontWeight: 700, color: COL.muted, textTransform: 'uppercase', letterSpacing: 0.4 }}>Checklist de llegada</div>

          {items.map((it) => {
            const b = badgeDe(it);
            const esta = Number(form[it.id] || 0);
            return (
              <div key={it.id} style={{ border: `1px solid ${COL.border}`, borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                    {it.imagen_url ? (
                      <button type="button" onClick={() => setZoomImagen(it.imagen_url)} style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'zoom-in', flexShrink: 0 }}>
                        <ImagenProducto imagen_url={it.imagen_url} size={40} />
                      </button>
                    ) : null}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: COL.text, wordBreak: 'break-word' }}>{it.descripcion}</div>
                      <div style={{ fontSize: 11, color: COL.muted, marginTop: 2 }}>
                        Solicitado {num(it.cantidad_solicitada)} · Recibido {num(it.cantidad_recibida)}
                      </div>
                    </div>
                  </div>
                  <span style={{ ...S.badge, background: b.bg, color: b.color }}>{b.label}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 8 }}>
                  <Field label="Recibido ahora" icon={FiHash} COL={COL}>
                    <input type="number" inputMode="decimal" min="0" step="0.01" value={form[it.id]} onChange={(e) => setForm((f) => ({ ...f, [it.id]: e.target.value }))} style={inputStyle(COL)} />
                  </Field>
                  {v('vis.compras.costo') && (
                    <Field label="Costo" icon={FiDollarSign} COL={COL} hint={orden.moneda_codigo ? `En ${simboloMonedaOrden(orden)} (se convierte a moneda base)` : null}>
                      <input type="number" inputMode="decimal" min="0" step="0.01" value={costos[it.id]} onChange={(e) => setCostos((f) => ({ ...f, [it.id]: e.target.value }))} style={inputStyle(COL)} />
                    </Field>
                  )}
                  {it.tipo_item === 'producto' && v('vis.compras.precio') && (
                    <Field label="Precio venta" icon={FiDollarSign} COL={COL}>
                      <input type="number" inputMode="decimal" min="0" step="0.01" value={precios[it.id] || ''} onChange={(e) => setPrecios((f) => ({ ...f, [it.id]: e.target.value }))} style={inputStyle(COL)} placeholder="Actual" />
                    </Field>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 8 }}>
                  <button type="button" onClick={() => setForm((f) => ({ ...f, [it.id]: esta > 0 ? '' : String(Math.max(Number(it.cantidad_solicitada) - Number(it.cantidad_recibida), 0)) }))} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: esta > 0 ? COL.muted : COL.pri2, cursor: 'pointer', fontSize: 12, fontWeight: 600, alignSelf: 'flex-start' }}>
                    <span style={{ width: 18, height: 18, borderRadius: 5, border: `1.5px solid ${esta > 0 ? COL.pri2 : COL.border}`, background: esta > 0 ? COL.pri2 : 'transparent', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11 }}>
                      {esta > 0 ? <FiCheck size={12} /> : null}
                    </span>
                    Llegó completo
                  </button>
                  <div style={{ flex: 1 }}>
                    <input value={notas[it.id] || ''} onChange={(e) => setNotas((f) => ({ ...f, [it.id]: e.target.value }))} style={inputStyle(COL)} placeholder="Nota (ej. dañado, pendiente, sobrante)…" />
                  </div>
                </div>
              </div>
            );
          })}

          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label="Registrar recepción" guardando={guardando} />

        {zoomImagen && (
          <Overlay COL={COL} isMobile={isMobile} onClose={() => setZoomImagen(null)}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', cursor: 'zoom-out' }} onClick={() => setZoomImagen(null)}>
              <img src={getImagenUrl(zoomImagen)} alt="" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }} />
            </div>
          </Overlay>
        )}
      </div>
    </Overlay>
  );
}

/* ─────────────────────────── Cuentas por pagar ─────────────────────────── */

function CuentasPorPagarTab({ COL, isMobile, puedeGestionar, v }) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [proveedorId, setProveedorId] = useState('');
  const [pago, setPago] = useState(null); // { compra } | null

  useEffect(() => {
    API.get('/compras/proveedores', { params: { limite: 200, solo_activos: false } })
      .then((r) => setProveedores(r.data?.data?.items || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setCargando(true);
    try {
      const params = { solo_pendientes: true };
      if (proveedorId) params.proveedor_id = Number(proveedorId);
      const r = await API.get('/compras/cuentas-por-pagar', { params });
      setData(r.data?.data || { compras: [], aging: {} });
    } catch (e) {
      console.error('Error cargando cuentas por pagar', e);
    } finally {
      setCargando(false);
    }
  }, [proveedorId]);

  useEffect(() => { load(); }, [load]);

  const aging = data?.aging || {};
  const compras = data?.compras || [];

  const agingCards = [
    { label: 'Total pendiente', value: aging.total ?? 0, color: COL.text },
    { label: 'Corriente', value: aging.corriente ?? 0, color: COL.green },
    { label: 'Vencidas 1-30', value: aging.vencidas_30 ?? 0, color: COL.amber },
    { label: 'Vencidas 31-60', value: aging.vencidas_60 ?? 0, color: COL.red },
    { label: 'Vencidas 61+', value: aging.vencidas_90 ?? 0, color: COL.red },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <select value={proveedorId} onChange={(e) => setProveedorId(e.target.value)} style={{ ...selectStyle(COL), width: isMobile ? '100%' : 240 }}>
          <option value="">Todos los proveedores</option>
          {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social}</option>)}
        </select>
      </div>

      {v('vis.compras.total') && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
          {agingCards.map((c) => (
            <div key={c.label} style={{ padding: isMobile ? 10 : 12, borderRadius: 10, background: COL.surface, border: `1px solid ${COL.border}` }}>
              <div style={{ fontSize: 11, color: COL.muted }}>{c.label}</div>
              <div style={{ fontSize: isMobile ? 15 : 17, fontWeight: 800, color: c.color }}>{money(c.value)}</div>
            </div>
          ))}
        </div>
      )}

      {compras.length === 0 && !cargando ? (
        <EmptyState icon={FiCreditCard} COL={COL} title="Sin cuentas por pagar" desc="Las compras a crédito aparecerán aquí con su fecha de vencimiento y saldo pendiente." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {compras.map((c) => (
            <div key={c.id} style={{ padding: 12, borderRadius: 10, background: COL.surface, border: `1px solid ${COL.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 700, color: COL.text }}>{c.numero_compra}</div>
                  <div style={{ fontSize: 12, color: COL.muted }}>
                    {c.proveedor || 'Sin proveedor'}
                    {c.fecha_vencimiento ? ` · vence ${String(c.fecha_vencimiento).slice(0, 10)}` : ''}
                  </div>
                </div>
                <EstadoPago estado={c.estado_pago} COL={COL} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontSize: 13, color: COL.muted }}>
                  Total {money(c.total)} · Pagado {money(c.monto_pagado)}
                </div>
                {v('vis.compras.total') && (
                  <div style={{ fontSize: 16, fontWeight: 800, color: c.saldo_pendiente > 0 ? COL.red : COL.green }}>
                    {money(c.saldo_pendiente)}
                  </div>
                )}
              </div>
              {puedeGestionar && c.saldo_pendiente > 0 && (
                <div style={{ marginTop: 10 }}>
                  <Boton COL={COL} ghost onClick={() => setPago({ compra: c })}><FiCreditCard size={14} /> Registrar abono</Boton>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {pago && (
        <ModalAbono
          COL={COL} isMobile={isMobile} compra={pago.compra}
          onClose={() => setPago(null)}
          onSaved={() => { setPago(null); load(); }}
        />
      )}
    </div>
  );
}

function EstadoPago({ estado, COL }) {
  const cfg = {
    pagada: { bg: COL.greenBg, color: COL.green, label: 'Pagada' },
    parcial: { bg: COL.amberBg, color: COL.amber, label: 'Parcial' },
    pendiente: { bg: COL.redBg, color: COL.red, label: 'Pendiente' },
  }[estado] || { bg: COL.bg2, color: COL.muted, label: estado };
  return <span style={{ ...S.badge, background: cfg.bg, color: cfg.color }}>{cfg.label}</span>;
}

function ModalAbono({ COL, isMobile, compra, onClose, onSaved }) {
  const [monto, setMonto] = useState('');
  const [metodo, setMetodo] = useState('');
  const [referencia, setReferencia] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [comprobante, setComprobante] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setMonto(String(Number(compra.saldo_pendiente || 0)));
  }, [compra]);

  const guardar = async () => {
    setError('');
    const m = Number(monto);
    if (!m || m <= 0) { setError('El monto debe ser mayor a 0'); return; }
    setGuardando(true);
    try {
      const r = await API.post('/compras/abonar', {
        compra_id: compra.id,
        monto: m,
        metodo_pago: metodo || null,
        referencia: referencia || null,
        fecha_pago: fecha || null,
      });
      // Subir comprobante si se adjuntó.
      const pagoId = r.data?.data?.pago_id;
      if (comprobante && pagoId) {
        const fd = new FormData();
        fd.append('comprobante', comprobante);
        try { await API.post(`/compras/abono/${pagoId}/comprobante`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); } catch (e) { /* no bloquea */ }
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo registrar el abono');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiCreditCard} title="Registrar abono" subtitle={compra.numero_compra} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Field label="Monto" icon={FiDollarSign} required COL={COL} hint={`Pendiente: ${money(compra.saldo_pendiente)}`}>
            <input type="number" inputMode="decimal" min="0" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} style={inputStyle(COL)} />
          </Field>
          <Field label="Método de pago" icon={FiCreditCard} COL={COL}>
            <input value={metodo} onChange={(e) => setMetodo(e.target.value)} style={inputStyle(COL)} placeholder="Efectivo, transferencia…" />
          </Field>
          <Field label="Referencia" icon={FiFileText} COL={COL}>
            <input value={referencia} onChange={(e) => setReferencia(e.target.value)} style={inputStyle(COL)} />
          </Field>
          <Field label="Fecha de abono" icon={FiCalendar} COL={COL}>
            <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} style={inputStyle(COL)} />
          </Field>
          <Field label="Comprobante" icon={FiFileText} COL={COL} hint="Opcional: captura de pago móvil / transferencia">
            <input type="file" accept="image/*,.pdf" onChange={(e) => setComprobante(e.target.files[0] || null)} style={inputStyle(COL)} />
          </Field>
          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label="Registrar abono" guardando={guardando} />
      </div>
    </Overlay>
  );
}

/* ─────────────────────────── Proveedores ─────────────────────────── */

function ProveedoresTab({ COL, isMobile, puedeGestionar }) {
  const [proveedores, setProveedores] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [modal, setModal] = useState(null); // null | {editar: proveedor} | {nuevo: true}

  const load = useCallback(async () => {
    setCargando(true);
    try {
      const r = await API.get('/compras/proveedores', { params: { limite: 200, solo_activos: false } });
      setProveedores(r.data?.data?.items || []);
    } catch (e) {
      console.error('Error cargando proveedores', e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const [baja, setBaja] = useState(null);
  const [dandoBaja, setDandoBaja] = useState(false);

  const confirmarBaja = async () => {
    if (!baja) return;
    setDandoBaja(true);
    try {
      await API.delete(`/compras/proveedor/${baja.id}`);
      setBaja(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo dar de baja');
    } finally {
      setDandoBaja(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 13, color: COL.muted }}>{proveedores.length} proveedores</div>
        {puedeGestionar && (
          <Boton COL={COL} onClick={() => setModal({ nuevo: true })}><FiPlus size={15} /> Nuevo proveedor</Boton>
        )}
      </div>

      {proveedores.length === 0 && !cargando ? (
        <EmptyState icon={FiTruck} COL={COL} title="Sin proveedores" desc="Registra tus proveedores para asignarlos a las compras." actionLabel={puedeGestionar ? 'Nuevo proveedor' : null} onAction={puedeGestionar ? () => setModal({ nuevo: true }) : null} />
      ) : (
        <Tabla>
          <thead>
            <tr>
              <th style={TH}>Proveedor</th>
              <th style={TH}>RIF</th>
              <th style={TH}>Contacto</th>
              <th style={TH}>Moneda</th>
              <th style={TH}>Estado</th>
              <th style={{ ...TH, textAlign: 'right' }}></th>
            </tr>
          </thead>
          <tbody>
            {proveedores.map((p) => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${COL.border}` }}>
                <td style={TD}>
                  <div style={{ fontWeight: 700, color: COL.text }}>{p.razon_social}</div>
                  <div style={{ fontSize: 11, color: COL.muted }}>{p.codigo}</div>
                </td>
                <td style={TD}>{p.rif || '—'}</td>
                <td style={TD}>
                  <div>{p.nombre_contacto || '—'}</div>
                  <div style={{ fontSize: 11, color: COL.muted }}>{p.telefono_contacto || p.telefono || ''}</div>
                </td>
                <td style={TD}>{p.moneda_codigo ? `${p.moneda_simbolo || ''} ${p.moneda_codigo}` : 'Base'}</td>
                <td style={TD}>
                  <span style={{ ...S.badge, background: p.activa ? COL.greenBg : COL.redBg, color: p.activa ? COL.green : COL.red }}>
                    {p.activa ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td style={{ ...TD, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {puedeGestionar && (
                    <>
                      <IconBtn COL={COL} onClick={() => setModal({ editar: p })}><FiEdit2 size={15} /></IconBtn>
                      {p.activa && <IconBtn COL={COL} danger onClick={() => setBaja(p)}><FiTrash2 size={15} /></IconBtn>}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Tabla>
      )}

      {modal && (
        <ModalProveedor
          COL={COL} isMobile={isMobile}
          proveedor={modal.editar || null}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}

      {baja && (
        <ModalConfirmar
          COL={COL} isMobile={isMobile}
          titulo="Dar de baja proveedor"
          mensaje={`¿Dar de baja a ${baja.razon_social}? Sus compras históricas se conservan.`}
          labelConfirmar="Dar de baja"
          onClose={() => setBaja(null)}
          onConfirm={confirmarBaja}
          guardando={dandoBaja}
        />
      )}
    </div>
  );
}

function ModalProveedor({ COL, isMobile, proveedor, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    codigo: proveedor?.codigo || '',
    razon_social: proveedor?.razon_social || '',
    rif: proveedor?.rif || '',
    telefono: proveedor?.telefono || '',
    email: proveedor?.email || '',
    direccion: proveedor?.direccion || '',
    nombre_contacto: proveedor?.nombre_contacto || '',
    telefono_contacto: proveedor?.telefono_contacto || '',
    notas: proveedor?.notas || '',
    moneda_codigo: proveedor?.moneda_codigo || '',
    moneda_origen: proveedor?.moneda_origen || 'global',
  }));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [monedas, setMonedas] = useState({ base: null, globales: [], compras: [] });

  useEffect(() => {
    API.get('/compras/monedas')
      .then((r) => setMonedas(r.data?.data || { base: null, globales: [], compras: [] }))
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setMoneda = (valor) => {
    // valor = 'base' | 'global:COD' | 'compras:COD'
    if (!valor || valor === 'base') {
      setForm((f) => ({ ...f, moneda_codigo: '', moneda_origen: 'global' }));
      return;
    }
    const [origen, codigo] = valor.split(':');
    setForm((f) => ({ ...f, moneda_codigo: codigo, moneda_origen: origen }));
  };

  const monedaValue = form.moneda_codigo
    ? `${form.moneda_origen || 'global'}:${form.moneda_codigo}`
    : 'base';

  const guardar = async () => {
    setError('');
    if (!form.razon_social.trim()) { setError('La razón social es obligatoria'); return; }
    setGuardando(true);
    try {
      const body = {
        ...form,
        codigo: form.codigo.trim() || null,
        rif: form.rif.trim() || null,
        moneda_codigo: form.moneda_codigo || null,
        moneda_origen: form.moneda_codigo ? (form.moneda_origen || 'global') : null,
      };
      if (proveedor) await API.put(`/compras/proveedor/${proveedor.id}`, body);
      else await API.post('/compras/proveedor', body);
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo guardar el proveedor');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiTruck} title={proveedor ? 'Editar proveedor' : 'Nuevo proveedor'} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '14px 18px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Field label="Razón social" icon={FiUser} required COL={COL}>
            <input value={form.razon_social} onChange={set('razon_social')} style={inputStyle(COL)} />
          </Field>
          <Field label="RIF" icon={FiHash} COL={COL}>
            <input value={form.rif} onChange={set('rif')} style={inputStyle(COL)} placeholder="Opcional" />
          </Field>
          <Field label="Código" icon={FiHash} COL={COL}>
            <input value={form.codigo} onChange={set('codigo')} style={inputStyle(COL)} placeholder="Auto" />
          </Field>
          <Field label="Teléfono" icon={FiTruck} COL={COL}>
            <input value={form.telefono} onChange={set('telefono')} style={inputStyle(COL)} />
          </Field>
          <Field label="Email" icon={FiUser} COL={COL}>
            <input value={form.email} onChange={set('email')} style={inputStyle(COL)} />
          </Field>
          <Field label="Dirección" icon={FiPackage} COL={COL}>
            <input value={form.direccion} onChange={set('direccion')} style={inputStyle(COL)} />
          </Field>
          <Field label="Contacto" icon={FiUser} COL={COL}>
            <input value={form.nombre_contacto} onChange={set('nombre_contacto')} style={inputStyle(COL)} />
          </Field>
          <Field label="Tel. contacto" icon={FiTruck} COL={COL}>
            <input value={form.telefono_contacto} onChange={set('telefono_contacto')} style={inputStyle(COL)} />
          </Field>
          <Field label="Moneda" icon={FiDollarSign} COL={COL} hint="Moneda en la que cotiza y cobra este proveedor">
            <select value={monedaValue} onChange={(e) => setMoneda(e.target.value)} style={selectStyle(COL)}>
              <option value="base">Moneda base</option>
              {(monedas.globales || []).filter((m) => !m.es_principal).map((m) => (
                <option key={`global:${m.codigo}`} value={`global:${m.codigo}`}>{m.simbolo} {m.nombre} ({m.codigo})</option>
              ))}
              {(monedas.compras || []).map((m) => (
                <option key={`compras:${m.codigo}`} value={`compras:${m.codigo}`}>{m.simbolo} {m.nombre} ({m.codigo})</option>
              ))}
            </select>
          </Field>
          <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
            <Field label="Notas" icon={FiClipboard} COL={COL}>
              <input value={form.notas} onChange={set('notas')} style={inputStyle(COL)} />
            </Field>
          </div>
          {error && <div style={{ fontSize: 12.5, color: COL.red, gridColumn: isMobile ? '1' : '1 / -1' }}>{error}</div>}
        </div>
        <ModalFooter onClose={onClose} onSave={guardar} COL={COL} label={proveedor ? 'Guardar' : 'Crear proveedor'} guardando={guardando} />
      </div>
    </Overlay>
  );
}

/* ─────────────────────────── Competidores ─────────────────────────── */

function CompetidoresTab({ COL, isMobile, puedeGestionar }) {
  const [competidores, setCompetidores] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [abierto, setAbierto] = useState(null); // competidor en detalle (modal)
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [creando, setCreando] = useState(false);
  const [renombrando, setRenombrando] = useState(null); // { id, nombre }
  const [baseSimb, setBaseSimb] = useState('$');

  const load = useCallback(async () => {
    setCargando(true);
    try {
      const r = await API.get('/competidores');
      setCompetidores(r.data?.data || []);
    } catch (e) {
      console.error('Error cargando competidores', e);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get('/compras/monedas')
      .then((r) => { const b = r.data?.data?.base; if (b?.simbolo) setBaseSimb(b.simbolo); })
      .catch(() => {});
  }, []);

  const crear = async () => {
    const nombre = nuevoNombre.trim();
    if (!nombre) { alert('Escribe el nombre del competidor'); return; }
    setCreando(true);
    try {
      await API.post('/competidores', { nombre });
      setNuevoNombre('');
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo crear el competidor');
    } finally {
      setCreando(false);
    }
  };

  const renombrar = async () => {
    const nombre = (renombrando?.nombre || '').trim();
    if (!nombre) { setRenombrando(null); return; }
    try {
      await API.put(`/competidores/${renombrando.id}`, { nombre });
      setRenombrando(null);
      load();
      if (abierto && abierto.competidor.id === renombrando.id) setAbierto({ ...abierto, competidor: { ...abierto.competidor, nombre } });
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo renombrar');
    }
  };

  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el competidor "${c.nombre}" y sus productos vinculados?`)) return;
    try {
      await API.delete(`/competidores/${c.id}`);
      if (abierto && abierto.competidor.id === c.id) setAbierto(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const abrirDetalle = async (id) => {
    try {
      const r = await API.get(`/competidores/${id}`);
      if (r.data.status === 'success') setAbierto(r.data.data);
    } catch (e) {
      console.error('Error cargando detalle', e);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        {puedeGestionar && (
          <>
            <input value={nuevoNombre} onChange={(e) => setNuevoNombre(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') crear(); }} placeholder="Nombre del competidor…" style={{ ...inputStyle(COL), maxWidth: 280 }} />
            <Boton COL={COL} onClick={crear} disabled={creando}><FiPlus size={15} /> Agregar</Boton>
          </>
        )}
        <div style={{ fontSize: 13, color: COL.muted, marginLeft: 'auto' }}>{competidores.length} competidores</div>
      </div>

      {competidores.length === 0 && !cargando ? (
        <EmptyState icon={FiTruck} COL={COL} title="Sin competidores" desc="Registra los competidores y vincula sus productos a tu inventario." actionLabel={puedeGestionar ? 'Nuevo competidor' : null} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {competidores.map((c) => (
            <div key={c.id} onClick={() => abrirDetalle(c.id)} style={{ padding: 12, borderRadius: 10, background: COL.surface, border: `1px solid ${COL.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: COL.text, wordBreak: 'break-word' }}>{c.nombre}</div>
                <div style={{ fontSize: 12, color: COL.muted }}>{c.n_productos} producto{c.n_productos === 1 ? '' : 's'}</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={(e) => e.stopPropagation()}>
                {puedeGestionar && (
                  <>
                    <IconBtn COL={COL} onClick={() => setRenombrando({ id: c.id, nombre: c.nombre })}><FiEdit2 size={15} /></IconBtn>
                    <IconBtn COL={COL} danger onClick={() => eliminar(c)}><FiTrash2 size={15} /></IconBtn>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {abierto && (
        <CompetidorDetalle
          COL={COL} isMobile={isMobile} puedeGestionar={puedeGestionar}
          competidor={abierto.competidor} productos={abierto.productos} baseSimb={baseSimb}
          onClose={() => setAbierto(null)}
          onRefresh={() => abrirDetalle(abierto.competidor.id)}
        />
      )}

      {renombrando && (
        <Overlay COL={COL} isMobile={isMobile} onClose={() => setRenombrando(null)}>
          <div style={{ ...S.modal, background: COL.surface, maxWidth: isMobile ? '100%' : 420 }}>
            <ModalHeader icon={FiEdit2} title="Renombrar competidor" onClose={() => setRenombrando(null)} COL={COL} />
            <div style={{ padding: '14px 18px' }}>
              <Field label="Nombre" icon={FiUser} required COL={COL}>
                <input value={renombrando.nombre} onChange={(e) => setRenombrando((p) => ({ ...p, nombre: e.target.value }))} style={inputStyle(COL)} autoFocus />
              </Field>
            </div>
            <ModalFooter onClose={() => setRenombrando(null)} onSave={renombrar} COL={COL} label="Guardar" guardando={false} />
          </div>
        </Overlay>
      )}
    </div>
  );
}

function CompetidorDetalle({ COL, isMobile, puedeGestionar, competidor, productos, baseSimb, onClose, onRefresh }) {
  const [buscando, setBuscando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [q, setQ] = useState('');

  const ql = (q || '').trim().toLowerCase();
  const filtrados = productos.filter((p) => {
    if (!ql) return true;
    return [p.descripcion, p.producto_codigo, p.referencia].some((v) => (v || '').toLowerCase().includes(ql));
  });

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={900} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiTruck} title={competidor.nombre} subtitle={`${productos.length} producto${productos.length === 1 ? '' : 's'} vinculados · precios en moneda base (${baseSimb})`} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 180 }}>
              <FiSearch size={14} style={{ color: COL.muted }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar por código, referencia o nombre…" style={inputStyle(COL)} />
            </div>
            {puedeGestionar && (
              <>
                <Boton COL={COL} onClick={() => setImportando(true)}><FiUpload size={14} /> Cargar archivo</Boton>
                <Boton COL={COL} ghost onClick={() => setBuscando(true)}><FiPlus size={14} /> Agregar producto</Boton>
              </>
            )}
          </div>

          {filtrados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 24, color: COL.muted, fontSize: 13 }}>
              {productos.length === 0 ? 'Sin productos vinculados. Carga un archivo o agrega productos del inventario.' : 'Sin resultados para el filtro.'}
            </div>
          ) : (
            <Tabla>
              <thead>
                <tr>
                  <th style={TH}></th>
                  <th style={TH}>Producto</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Precio interno ({baseSimb})</th>
                  <th style={{ ...TH, textAlign: 'right' }}>Precio competidor ({baseSimb})</th>
                  {puedeGestionar && <th style={{ ...TH, textAlign: 'right' }}></th>}
                </tr>
              </thead>
              <tbody>
                {filtrados.map((p) => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${COL.border}` }}>
                    <td style={{ ...TD, width: 46 }}>
                      {p.imagen_url ? <ImagenProducto imagen_url={p.imagen_url} size={36} /> : <span style={{ color: COL.faint, fontSize: 10, fontStyle: 'italic' }}>s/img</span>}
                    </td>
                    <td style={TD}>
                      <div style={{ fontWeight: 600, color: COL.text, wordBreak: 'break-word' }}>{p.descripcion}</div>
                      <div style={{ fontSize: 11, color: COL.muted }}>{p.producto_codigo}{p.referencia ? ` · ${p.referencia}` : ''}</div>
                    </td>
                    <td style={{ ...TD, textAlign: 'right' }}>{moneySimbolo(p.precio_interno, baseSimb)}</td>
                    <td style={{ ...TD, textAlign: 'right' }}>
                      <CompetidorPrecioCell COL={COL} p={p} competidorId={competidor.id} baseSimb={baseSimb} onSaved={onRefresh} />
                    </td>
                    {puedeGestionar && (
                      <td style={{ ...TD, textAlign: 'right' }}>
                        <IconBtn COL={COL} danger onClick={() => desvincular(competidor.id, p, onRefresh)}><FiTrash2 size={14} /></IconBtn>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Tabla>
          )}
        </div>
      </div>

      {buscando && (
        <ModalBuscarProducto
          COL={COL} isMobile={isMobile} competidorId={competidor.id} vinculados={productos.map((p) => p.producto_codigo)} baseSimb={baseSimb}
          onClose={() => setBuscando(false)}
          onSaved={() => { setBuscando(false); onRefresh(); }}
        />
      )}

      {importando && (
        <ModalImportarCompetidor
          COL={COL} isMobile={isMobile} competidorId={competidor.id} baseSimb={baseSimb}
          onClose={() => setImportando(false)}
          onSaved={() => { setImportando(false); onRefresh(); }}
        />
      )}
    </Overlay>
  );
}

function ImportFila({ COL, baseSimb, nombre, referencia, precio, activo, acciones, detalle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: `1px solid ${COL.border}`, background: activo ? COL.blueBg : COL.surface }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, wordBreak: 'break-word' }}>{nombre}</div>
        <div style={{ fontSize: 11, color: COL.muted, wordBreak: 'break-word' }}>{referencia || 'sin referencia'}</div>
        {detalle}
      </div>
      {precio != null && <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{moneySimbolo(precio, baseSimb)}</span>}
      {acciones}
    </div>
  );
}

function ModalImportarCompetidor({ COL, isMobile, competidorId, baseSimb, onClose, onSaved }) {
  const [analizando, setAnalizando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [error, setError] = useState('');
  const [analisis, setAnalisis] = useState(null);
  const [tab, setTab] = useState('identificados');
  const [checked, setChecked] = useState({});      // identificados: indice -> bool
  const [asignar, setAsignar] = useState({});      // similares: indice -> producto_codigo
  const [manual, setManual] = useState({});         // sin relación: indice -> producto_codigo
  const [overrides, setOverrides] = useState({});   // identificados: indice -> producto_codigo (cambiado)
  const [pickerTarget, setPickerTarget] = useState(null); // { indice, kind }
  const fileRef = React.useRef(null);
  const bodyRef = React.useRef(null);

  const onPickArchivo = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setAnalizando(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const r = await API.post(`/competidores/${competidorId}/analizar-archivo`, fd, { timeout: 120000 });
      const d = r.data?.data;
      setAnalisis(d);
      const c = {};
      (d.coincidencias || []).forEach((x) => { c[x.indice] = true; });
      setChecked(c);
      setAsignar({});
      setManual({});
      setOverrides({});
      setTab('identificados');
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo analizar el archivo');
    } finally {
      setAnalizando(false);
    }
  };

  const aplicar = async () => {
    if (!analisis) return;
    const items = [];
    (analisis.coincidencias || []).forEach((x) => {
      if (checked[x.indice]) items.push({ producto_codigo: overrides[x.indice] || x.producto_codigo, precio: x.precio ?? null });
    });
    (analisis.similares || []).forEach((x) => {
      if (asignar[x.indice]) items.push({ producto_codigo: asignar[x.indice], precio: x.precio ?? null });
    });
    (analisis.sin_coincidencia || []).forEach((x) => {
      if (manual[x.indice]) items.push({ producto_codigo: manual[x.indice], precio: x.precio ?? null });
    });
    if (!items.length) { setError('Selecciona al menos un producto para vincular'); return; }
    setAplicando(true);
    setError('');
    try {
      await API.post(`/competidores/${competidorId}/productos/bulk`, { items });
      onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo aplicar la vinculación');
    } finally {
      setAplicando(false);
    }
  };

  const cambiarProducto = (indice, kind) => setPickerTarget({ indice, kind });

  const tabDefs = [
    { id: 'identificados', label: 'Identificados', count: (analisis?.coincidencias || []).length, color: COL.green, bg: COL.greenBg },
    { id: 'similares', label: 'Similares', count: (analisis?.similares || []).length, color: COL.amber, bg: COL.amberBg },
    { id: 'noencontrados', label: 'No encontrados', count: (analisis?.sin_coincidencia || []).length, color: COL.muted, bg: COL.bg2 },
  ];

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={860} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiUpload} title="Importar productos del competidor" subtitle="Coincidencias por referencia y nombre" onClose={onClose} COL={COL} />
        <div ref={bodyRef} style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto', overflowAnchor: 'none' }}>
          {!analisis ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', padding: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 13, color: COL.muted, maxWidth: 440, lineHeight: 1.5 }}>
                Sube un archivo (Excel o CSV) con la lista de productos del competidor. El sistema buscará
                coincidencias por <b>referencia</b> y <b>nombre</b> contra tu inventario. Los productos sin
                relación quedarán sin vincular.
              </div>
              <Boton COL={COL} onClick={() => fileRef.current && fileRef.current.click()} disabled={analizando}>
                <FiUpload size={15} /> {analizando ? 'Analizando…' : 'Seleccionar archivo'}
              </Boton>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onPickArchivo} />
              <div style={{ fontSize: 11.5, color: COL.faint }}>Columnas: nombre, código/referencia y precio (opcional).</div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${COL.border}`, flexShrink: 0 }}>
                {tabDefs.map((t) => (
                  <button key={t.id} type="button" onClick={() => setTab(t.id)}
                    style={{ padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 700 : 500, color: tab === t.id ? COL.pri2 : COL.muted, borderBottom: `2px solid ${tab === t.id ? COL.pri2 : 'transparent'}`, whiteSpace: 'nowrap' }}>
                    {t.label} ({t.count})
                  </button>
                ))}
                <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 12, color: COL.muted }}>de {analisis.total}</span>
              </div>

              {analisis.error_ia && (
                <div style={{ padding: '8px 12px', borderRadius: 8, background: COL.amberBg, fontSize: 12, color: COL.amber }}>
                  IA no disponible ({analisis.error_ia}). Se usó solo la coincidencia por código y nombre.
                </div>
              )}

              {tab === 'identificados' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {analisis.coincidencias.length === 0 ? (
                    <div style={{ fontSize: 12, color: COL.muted, padding: 12 }}>Ninguno.</div>
                  ) : (
                    analisis.coincidencias.map((x) => (
                      <ImportFila key={x.indice} COL={COL} baseSimb={baseSimb}
                        nombre={x.nombre} referencia={x.referencia} precio={x.precio} activo={!!checked[x.indice]}
                        detalle={(
                          <div style={{ fontSize: 11, color: COL.green, marginTop: 2, wordBreak: 'break-word' }}>
                            → {overrides[x.indice] || x.producto_codigo} · {x.producto_descripcion}{x.producto_referencia ? ` · ${x.producto_referencia}` : ''}
                          </div>
                        )}
                        acciones={(
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                            <input type="checkbox" checked={!!checked[x.indice]} onChange={(e) => setChecked((p) => ({ ...p, [x.indice]: e.target.checked }))} style={{ width: 16, height: 16, flexShrink: 0 }} />
                            <IconBtn COL={COL} onClick={() => cambiarProducto(x.indice, 'identificados')} title="Cambiar producto"><FiBox size={14} /></IconBtn>
                          </div>
                        )}
                      />
                    ))
                  )}
                </div>
              )}

              {tab === 'similares' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {analisis.similares.length === 0 ? (
                    <div style={{ fontSize: 12, color: COL.muted, padding: 12 }}>Ninguno.</div>
                  ) : (
                    analisis.similares.map((x) => {
                      const elegido = asignar[x.indice];
                      const activo = !!elegido;
                      return (
                        <ImportFila key={x.indice} COL={COL} baseSimb={baseSimb}
                          nombre={x.nombre} referencia={x.referencia} precio={x.precio} activo={activo}
                          detalle={(
                            <div style={{ fontSize: 11, marginTop: 2, wordBreak: 'break-word' }}>
                              <span style={{ color: COL.muted }}>similar a </span>
                              <span style={{ color: activo ? COL.pri2 : COL.amber, fontWeight: 700 }}>
                                {elegido || x.candidato_codigo} · {x.candidato_descripcion}{x.candidato_referencia ? ` · ${x.candidato_referencia}` : ''}
                              </span>
                            </div>
                          )}
                          acciones={(
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                              <Boton COL={COL} ghost={!activo} onClick={() => setAsignar((p) => {
                                const n = { ...p };
                                if (n[x.indice]) delete n[x.indice]; else n[x.indice] = x.candidato_codigo;
                                return n;
                              })}>
                                {activo ? <><FiCheck size={13} /> Asignado</> : 'Asignar'}
                              </Boton>
                              <IconBtn COL={COL} onClick={() => cambiarProducto(x.indice, 'similar')} title="Cambiar producto"><FiBox size={14} /></IconBtn>
                            </div>
                          )}
                        />
                      );
                    })
                  )}
                </div>
              )}

              {tab === 'noencontrados' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {analisis.sin_coincidencia.length === 0 ? (
                    <div style={{ fontSize: 12, color: COL.muted, padding: 12 }}>Ninguno.</div>
                  ) : (
                    analisis.sin_coincidencia.map((x) => (
                      <ImportFila key={x.indice} COL={COL} baseSimb={baseSimb}
                        nombre={x.nombre} referencia={x.referencia} precio={x.precio} activo={!!manual[x.indice]}
                        acciones={(
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                            {manual[x.indice] ? (
                              <span style={{ ...S.badge, background: COL.greenBg, color: COL.green, whiteSpace: 'nowrap' }}><FiCheck size={11} style={{ verticalAlign: '-1px' }} /> {manual[x.indice]}</span>
                            ) : null}
                            <IconBtn COL={COL} onClick={() => cambiarProducto(x.indice, 'sin')} title="Asociar producto"><FiBox size={14} /></IconBtn>
                          </div>
                        )}
                      />
                    ))
                  )}
                </div>
              )}
            </>
          )}

          {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
        </div>
        {analisis && (
          <ModalFooter onClose={onClose} onSave={aplicar} COL={COL} label="Aplicar vinculación" guardando={aplicando} />
        )}
      </div>

      {pickerTarget && (
        <ModalBuscarProducto
          COL={COL} isMobile={isMobile} vinculados={[]} baseSimb={baseSimb} titulo="Asociar producto"
          onClose={() => setPickerTarget(null)}
          onSelect={(codigo) => {
            if (pickerTarget.kind === 'identificados') { setOverrides((p) => ({ ...p, [pickerTarget.indice]: codigo })); setChecked((p) => ({ ...p, [pickerTarget.indice]: true })); }
            if (pickerTarget.kind === 'similar') setAsignar((p) => ({ ...p, [pickerTarget.indice]: codigo }));
            if (pickerTarget.kind === 'sin') setManual((p) => ({ ...p, [pickerTarget.indice]: codigo }));
            setPickerTarget(null);
          }}
        />
      )}
    </Overlay>
  );
}

function CompetidorPrecioCell({ COL, p, competidorId, baseSimb, onSaved }) {
  const [precio, setPrecio] = useState(p.precio != null ? String(p.precio) : '');
  const [guardando, setGuardando] = useState(false);

  const guardar = async () => {
    setGuardando(true);
    try {
      await API.put(`/competidores/${competidorId}/productos/${p.producto_codigo}`, { precio: precio === '' ? null : Number(precio) });
      if (onSaved) onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo guardar el precio');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <input
        type="number" inputMode="decimal" min="0" step="0.01"
        value={precio}
        onChange={(e) => setPrecio(e.target.value)}
        onBlur={guardar}
        style={{ ...inputStyle(COL), width: 110, padding: '6px 8px', textAlign: 'right' }}
        placeholder={`${baseSimb} —`}
      />
      {guardando && <span style={{ fontSize: 11, color: COL.muted }}>…</span>}
    </div>
  );
}

async function desvincular(competidorId, p, onRefresh) {
  try {
    await API.delete(`/competidores/${competidorId}/productos/${p.producto_codigo}`);
    if (onRefresh) onRefresh();
  } catch (e) {
    alert(e?.response?.data?.detail || 'No se pudo desvincular');
  }
}

function ModalBuscarProducto({ COL, isMobile, competidorId, vinculados, baseSimb, onClose, onSaved, onSelect, titulo }) {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [precio, setPrecio] = useState('');
  const [seleccion, setSeleccion] = useState(null);
  const listRef = React.useRef(null);

  const soloSeleccion = typeof onSelect === 'function';

  const buscar = useCallback(async () => {
    setCargando(true);
    try {
      const r = await API.get('/competidores/buscar-productos', { params: { q: q || undefined, limite: 30 } });
      const d = r.data?.data || {};
      setResultados(d.items || []);
      setHasMore(!!d.has_more);
    } catch (e) {
      console.error('Error buscando productos', e);
    } finally {
      setCargando(false);
    }
  }, [q]);

  useEffect(() => { buscar(); }, [buscar]);

  const cargarMas = async () => {
    if (cargandoMas || !hasMore || resultados.length === 0) return;
    const last = resultados[resultados.length - 1];
    setCargandoMas(true);
    try {
      const r = await API.get('/competidores/buscar-productos', {
        params: { q: q || undefined, limite: 30, ultimo_codigo: last.codigo, ultimo_descripcion: last.descripcion },
      });
      const d = r.data?.data || {};
      setResultados((prev) => [...prev, ...(d.items || [])]);
      setHasMore(!!d.has_more);
    } catch (e) {
      console.error('Error cargando más productos', e);
    } finally {
      setCargandoMas(false);
    }
  };

  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 60) cargarMas();
  };

  const confirmar = async () => {
    if (!seleccion) return;
    if (soloSeleccion) {
      onSelect(seleccion.codigo);
      return;
    }
    try {
      await API.post(`/competidores/${competidorId}/productos`, {
        producto_codigo: seleccion.codigo,
        precio: precio === '' ? null : Number(precio),
      });
      onSaved();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo vincular el producto');
    }
  };

  return (
    <Overlay COL={COL} isMobile={isMobile} maxWidth={640} onClose={onClose}>
      <div style={{ ...S.modal, background: COL.surface }}>
        <ModalHeader icon={FiPackage} title={titulo || 'Agregar producto'} subtitle={`Vincula un producto del inventario · precio en ${baseSimb}`} onClose={onClose} COL={COL} />
        <div style={{ padding: isMobile ? '14px 12px' : '16px 20px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, código o referencia…" style={inputStyle(COL)} autoFocus />

          {seleccion && (
            <div style={{ padding: 10, borderRadius: 8, border: `1px solid ${COL.pri2}`, background: COL.blueBg, display: 'flex', alignItems: 'center', gap: 8 }}>
              <ImagenProducto imagen_url={seleccion.imagen_url} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: COL.text, wordBreak: 'break-word' }}>{seleccion.descripcion}</div>
                <div style={{ fontSize: 11, color: COL.muted }}>{seleccion.codigo}</div>
              </div>
              {!soloSeleccion && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type="number" inputMode="decimal" min="0" step="0.01" value={precio} onChange={(e) => setPrecio(e.target.value)} placeholder={`${baseSimb} precio`} style={{ ...inputStyle(COL), width: 100 }} />
                </div>
              )}
            </div>
          )}

          {cargando && resultados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: COL.muted }}>Buscando…</div>
          ) : resultados.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: COL.muted }}>Sin resultados</div>
          ) : (
            <div ref={listRef} onScroll={onScroll} style={{ display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', minHeight: 0, flex: 1 }}>
              {resultados.map((p) => {
                const ya = (vinculados || []).includes(p.codigo);
                return (
                  <button key={p.codigo} type="button" onClick={() => { setSeleccion(p); setPrecio(''); }}
                    disabled={ya}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, border: seleccion?.codigo === p.codigo ? `1px solid ${COL.pri2}` : `1px solid ${COL.border}`, background: seleccion?.codigo === p.codigo ? COL.blueBg : COL.inputBg, cursor: ya ? 'not-allowed' : 'pointer', textAlign: 'left', color: COL.text, opacity: ya ? 0.5 : 1 }}>
                    <ImagenProducto imagen_url={p.imagen_url} size={34} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{p.descripcion}</span>
                      <span style={{ fontSize: 11, color: COL.muted }}>{p.codigo}{p.referencia ? ` · ${p.referencia}` : ''}{ya ? ' · ya vinculado' : ''}</span>
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 12, color: COL.pri2, fontWeight: 700, whiteSpace: 'nowrap' }}>{moneySimbolo(p.precio, baseSimb)}</span>
                  </button>
                );
              })}
              {cargandoMas && <div style={{ textAlign: 'center', padding: 10, color: COL.muted, fontSize: 12 }}>Cargando más…</div>}
              {!hasMore && resultados.length > 0 && <div style={{ textAlign: 'center', padding: 8, color: COL.faint, fontSize: 11 }}>Fin de la lista</div>}
            </div>
          )}
        </div>
        <ModalFooter onClose={onClose} onSave={confirmar} COL={COL} label={soloSeleccion ? 'Seleccionar' : 'Vincular producto'} guardando={false} />
      </div>
    </Overlay>
  );
}

/* ─────────────────────────── Monedas (compras) ─────────────────────────── */

function MonedasTab({ COL, isMobile, puedeGestionar }) {
  const [data, setData] = useState({ base: null, globales: [], compras: [] });
  const [cargando, setCargando] = useState(true);
  const [showNueva, setShowNueva] = useState(false);
  const [nueva, setNueva] = useState({ nombre: '', codigo: '', simbolo: '', tasa_cambio: 1 });
  const [guardando, setGuardando] = useState({});
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    setCargando(true);
    API.get('/compras/monedas')
      .then((r) => setData(r.data?.data || { base: null, globales: [], compras: [] }))
      .catch((e) => console.error('Error cargando monedas', e))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const base = data.base;
  const globales = data.globales || [];
  const secundariasGlobales = globales.filter((m) => !m.es_principal);
  const compras = data.compras || [];

  const actualizarTasa = (id, valor) => {
    setData((prev) => ({ ...prev, compras: prev.compras.map((m) => (m.id === id ? { ...m, tasa: parseFloat(valor) || 0 } : m)) }));
  };

  const guardarTasa = async (m) => {
    setGuardando((prev) => ({ ...prev, [m.id]: true }));
    try {
      await API.put(`/compras/monedas/${m.id}`, { tasa_cambio: m.tasa });
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo guardar la tasa');
    } finally {
      setGuardando((prev) => ({ ...prev, [m.id]: false }));
    }
  };

  const crearMoneda = async () => {
    setError('');
    if (!nueva.nombre || !nueva.codigo || !nueva.simbolo) { setError('Nombre, código y símbolo son obligatorios'); return; }
    setCreando(true);
    try {
      await API.post('/compras/monedas', nueva);
      setShowNueva(false);
      setNueva({ nombre: '', codigo: '', simbolo: '', tasa_cambio: 1 });
      cargar();
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo crear la moneda');
    } finally {
      setCreando(false);
    }
  };

  const desactivar = async (m) => {
    if (!window.confirm(`¿Desactivar ${m.nombre} (${m.codigo})?`)) return;
    try {
      await API.delete(`/compras/monedas/${m.id}`);
      cargar();
    } catch (e) {
      alert(e?.response?.data?.detail || 'No se pudo desactivar la moneda');
    }
  };

  if (cargando) {
    return <div style={{ textAlign: 'center', padding: 40, color: COL.muted }}>Cargando monedas…</div>;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {base && (
        <div style={{ padding: 16, borderRadius: 12, border: `1px solid ${COL.border}`, background: COL.surface, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: COL.greenBg, color: COL.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiCheck size={18} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: COL.muted }}>Moneda base</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: COL.text }}>{base.simbolo} {base.codigo}</div>
            <div style={{ fontSize: 12, color: COL.muted }}>Todas las demás monedas se calculan respecto a esta (tasa = 1).</div>
          </div>
        </div>
      )}

      <div style={{ padding: 16, borderRadius: 12, border: `1px solid ${COL.border}`, background: COL.surface }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: COL.text }}>Monedas de compras</div>
            <div style={{ fontSize: 12, color: COL.muted }}>Exclusivas para órdenes de compra y proveedores</div>
          </div>
          {puedeGestionar && (
            <Boton COL={COL} onClick={() => setShowNueva(true)}><FiPlus size={14} /> Nueva moneda</Boton>
          )}
        </div>

        {compras.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 20, color: COL.muted, fontSize: 13 }}>
            Sin monedas de compras. Crea una o asigna una moneda global a tus proveedores.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {compras.map((m) => (
              <div key={m.id} style={{ border: `1px solid ${COL.border}`, borderRadius: 10, padding: 12, background: COL.bg2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: COL.text }}>{m.simbolo} {m.nombre}</div>
                    <div style={{ fontSize: 11, color: COL.muted }}>{m.codigo}</div>
                  </div>
                  {puedeGestionar && (
                    <IconBtn COL={COL} danger onClick={() => desactivar(m)}><FiTrash2 size={14} /></IconBtn>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 12, color: COL.muted, whiteSpace: 'nowrap' }}>1 {base?.codigo || 'USD'} =</span>
                  <input
                    type="number" inputMode="decimal" min="0" step="0.01"
                    value={m.tasa}
                    onChange={(e) => actualizarTasa(m.id, e.target.value)}
                    style={{ ...inputStyle(COL), flex: 1, textAlign: 'center', fontWeight: 600 }}
                  />
                  <span style={{ fontSize: 12, color: COL.muted }}>{m.codigo}</span>
                </div>
                {puedeGestionar && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                    <Boton COL={COL} ghost onClick={() => guardarTasa(m)} disabled={guardando[m.id]}>
                      {guardando[m.id] ? '…' : 'Guardar'}
                    </Boton>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {secundariasGlobales.length > 0 && (
        <div style={{ padding: 16, borderRadius: 12, border: `1px solid ${COL.border}`, background: COL.surface }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COL.text, marginBottom: 4 }}>Monedas globales</div>
          <div style={{ fontSize: 12, color: COL.muted, marginBottom: 12 }}>
            Configuradas en el sistema (Configuración → Monedas). Disponibles para asignar a proveedores.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {secundariasGlobales.map((m) => (
              <span key={m.codigo} style={{ ...S.badge, background: COL.blueBg, color: COL.pri2, fontSize: 12, padding: '6px 10px' }}>
                {m.simbolo} {m.codigo} · 1 {base?.codigo || 'USD'} = {m.tasa}
              </span>
            ))}
          </div>
        </div>
      )}

      {showNueva && (
        <Overlay COL={COL} isMobile={isMobile} onClose={() => setShowNueva(false)}>
          <div style={{ ...S.modal, background: COL.surface, maxWidth: isMobile ? '100%' : 420 }}>
            <ModalHeader icon={FiPlus} title="Nueva moneda de compras" onClose={() => setShowNueva(false)} COL={COL} />
            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Field label="Nombre" icon={FiBox} required COL={COL}>
                <input value={nueva.nombre} onChange={(e) => setNueva({ ...nueva, nombre: e.target.value })} style={inputStyle(COL)} placeholder="Ej: Yuan" />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Código" icon={FiHash} required COL={COL}>
                  <input value={nueva.codigo} onChange={(e) => setNueva({ ...nueva, codigo: e.target.value.toUpperCase() })} style={inputStyle(COL)} placeholder="CNY" maxLength={10} />
                </Field>
                <Field label="Símbolo" icon={FiDollarSign} required COL={COL}>
                  <input value={nueva.simbolo} onChange={(e) => setNueva({ ...nueva, simbolo: e.target.value })} style={inputStyle(COL)} placeholder="¥" maxLength={10} />
                </Field>
              </div>
              <Field label={`Tasa (1 ${base?.codigo || 'USD'} = ?)`} icon={FiDollarSign} required COL={COL}>
                <input type="number" inputMode="decimal" min="0" step="0.01" value={nueva.tasa_cambio} onChange={(e) => setNueva({ ...nueva, tasa_cambio: parseFloat(e.target.value) || 0 })} style={inputStyle(COL)} />
              </Field>
              {error && <div style={{ fontSize: 12.5, color: COL.red }}>{error}</div>}
            </div>
            <ModalFooter onClose={() => setShowNueva(false)} onSave={crearMoneda} COL={COL} label="Crear moneda" guardando={creando} />
          </div>
        </Overlay>
      )}
    </div>
  );
}

/* ─────────────────────────── UI primitives ─────────────────────────── */

function SedeSelector({ sedes, sede, setSede, COL, isMobile }) {
  if (sedes.length === 0) return null;
  return (
    <select value={sede} onChange={(e) => setSede(e.target.value)} style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, fontSize: 13, cursor: 'pointer', maxWidth: isMobile ? 160 : 220 }}>
      {sedes.map((s) => <option key={s.codigo} value={s.codigo}>{s.nombre || s.codigo}</option>)}
    </select>
  );
}

function EmptyState({ icon: Icon, title, desc, COL, actionLabel, onAction }) {
  return (
    <div style={{ textAlign: 'center', padding: '40px 16px', background: COL.surface, borderRadius: 14, border: `1px dashed ${COL.border}` }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
        <Icon size={26} />
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: COL.text }}>{title}</div>
      <div style={{ fontSize: 13, color: COL.muted, marginTop: 6, maxWidth: 420, margin: '6px auto 0', lineHeight: 1.5 }}>{desc}</div>
      {actionLabel && onAction && (
        <div style={{ marginTop: 16 }}>
          <Boton COL={COL} onClick={onAction}><FiPlus size={15} /> {actionLabel}</Boton>
        </div>
      )}
    </div>
  );
}

function Overlay({ COL, onClose, children, isMobile, maxWidth }) {
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: COL.overlay, zIndex: 1000, display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ maxHeight: isMobile ? '96vh' : '94vh', width: '100%', maxWidth: maxWidth || 1000, display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ icon: Icon, title, subtitle, onClose, COL }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 18px 12px', flexShrink: 0 }}>
      {Icon && (
        <div style={{ width: 38, height: 38, borderRadius: 10, background: COL.blueBg, color: COL.pri2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={19} />
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COL.text, wordBreak: 'break-word' }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12.5, color: COL.muted, marginTop: 2, wordBreak: 'break-word' }}>{subtitle}</div>}
      </div>
      <IconBtn COL={COL} onClick={onClose}><FiX size={16} /></IconBtn>
    </div>
  );
}

function ModalFooter({ onClose, onSave, COL, label, guardando }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: 16, flexShrink: 0, background: COL.surface, borderTop: `1px solid ${COL.border}` }}>
      <Boton COL={COL} ghost onClick={onClose} disabled={guardando}>Cancelar</Boton>
      <Boton COL={COL} onClick={onSave} disabled={guardando}>{guardando ? 'Guardando…' : (label || 'Guardar')}</Boton>
    </div>
  );
}

function Field({ label, icon: Icon, required, error, hint, COL, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
        {Icon && <Icon size={13} style={{ color: COL.muted, flexShrink: 0 }} />}
        <span style={{ fontSize: 12, fontWeight: 700, color: COL.muted, whiteSpace: 'nowrap' }}>{label}</span>
        {required && <span style={{ color: COL.red, fontSize: 12 }}>*</span>}
      </div>
      {children}
      {error ? (
        <span style={{ fontSize: 11.5, color: COL.red, display: 'flex', alignItems: 'center', gap: 4 }}>
          <FiAlertTriangle size={12} /> {error}
        </span>
      ) : hint ? (
        <span style={{ fontSize: 11, color: COL.faint, lineHeight: 1.4 }}>{hint}</span>
      ) : null}
    </div>
  );
}

function MiniStat({ label, value, COL }) {
  return (
    <div style={{ padding: '10px 12px', borderRadius: 10, background: COL.bg2 }}>
      <div style={{ fontSize: 11, color: COL.muted }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: COL.text, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function Boton({ COL, ghost, onClick, children, disabled }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, border: ghost ? `1px solid ${COL.border}` : 'none', cursor: disabled ? 'not-allowed' : 'pointer', background: ghost ? 'transparent' : COL.pri2, color: ghost ? COL.text : '#fff', fontSize: 13, fontWeight: 600, minHeight: 38, opacity: disabled ? 0.5 : 1 }}>
      {children}
    </button>
  );
}

function IconBtn({ COL, danger, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 6, border: 'none', cursor: 'pointer', background: danger ? COL.redBg : COL.bg2, color: danger ? COL.red : COL.text, marginLeft: 4 }}>
      {children}
    </button>
  );
}

function Tabla({ children }) {
  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 720 }}>{children}</table>
    </div>
  );
}

const TH = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: 'var(--agx-muted)', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid var(--agx-border)' };
const TD = { padding: '10px 12px', fontSize: 13, color: 'var(--agx-text)', verticalAlign: 'middle' };

function inputStyle(COL) {
  return { boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.inputBg, color: COL.text, fontSize: 13, width: '100%', minWidth: 0 };
}
function selectStyle(COL) {
  return { boxSizing: 'border-box', padding: '9px 10px', borderRadius: 8, border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, fontSize: 13, width: '100%', minWidth: 0 };
}

const S = {
  modal: { borderRadius: 14, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden', margin: '0 auto', background: 'var(--agx-surface)', display: 'flex', flexDirection: 'column', maxHeight: '94vh', width: '100%' },
  badge: { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, whiteSpace: 'nowrap' },
  banner: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12 },
  warnBanner: { background: 'var(--agx-warning-bg)', color: 'var(--agx-warning-text)' },
};
